<template>
  <div class="blog-wrapper">

    <!-- content -->
    <div class="blog-detail-wrapper" v-if="artistDetail">
      <b-row>
        <!-- blogs -->
        <b-col cols="12">
          <b-card
              _img-top
              _img-alt="Постер"
              _:title="`${artistDetail.title}`"
          >
            <h1 class="d-flex align-items-center">
              <template v-if="!isEditingTitle">
                {{ artistDetail.title }}
                <b-button
                    v-if="$can('manage', 'artist')"
                    variant="flat-primary"
                    class="btn-icon ml-50"
                    size="sm"
                    @click="startEditingTitle"
                >
                  <feather-icon icon="Edit2Icon"/>
                </b-button>
              </template>
              <template v-else>
                <b-form-input
                    v-model="editTitle"
                    class="mr-1"
                    style="font-size: inherit; max-width: 400px;"
                    @keyup.enter="saveTitle"
                />
                <b-button
                    variant="success"
                    class="btn-icon mr-50"
                    size="sm"
                    @click="saveTitle"
                >
                  <feather-icon icon="CheckIcon"/>
                </b-button>
                <b-button
                    variant="flat-secondary"
                    class="btn-icon"
                    size="sm"
                    @click="cancelEditingTitle"
                >
                  <feather-icon icon="XIcon"/>
                </b-button>
              </template>
              <div class="d-flex ml-auto">
                <span class="font-weight-bold font-large-2 ml-1">
                  <div class="small">{{ artistDetail.popularity }}</div>
                </span>
              </div>
            </h1>

            <div v-if="aliasesText" class="text-muted font-small-4 mb-1">
              <span class="font-weight-bold mr-50">Также известен как:</span>
              <span>{{ aliasesText }}</span>
            </div>

            <b-row>
              <b-col
                  cols="12"
                  md="12"
                  class="d-flex align-items-center justify-content-center mb-2 mb-md-0"
              >
                <b-card
                    title="Исполнитель"
                    class="col-md-12 col-12"
                >
                  <b-overlay
                      :show="isLoadingArtist"
                      rounded="sm"
                  >
                    <b-row>
                      <b-col md="12">

                        <!-- Информация об артисте -->
                        <div class="artist-info-block mb-1 p-1 rounded" style="background-color: rgba(115, 103, 240, 0.05); border: 1px solid rgba(115, 103, 240, 0.15);">
                          <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem 1.5rem;">
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">ID:</span>
                              <strong class="font-small-3">{{ artistDetail.id }}</strong>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Язык:</span>
                              <b-badge :variant="artistDetail.russian ? 'light-success' : 'light-secondary'" class="font-small-2">
                                {{ artistDetail.russian ? 'Русский' : 'Зарубежный' }}
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist')"
                                  v-b-tooltip.hover
                                  title="Редактировать атрибуты"
                                  variant="flat-primary"
                                  class="btn-icon p-25 ml-25"
                                  size="sm"
                                  @click="openAttributesModal"
                              >
                                <feather-icon icon="Edit2Icon" size="12"/>
                              </b-button>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Треков:</span>
                              <strong class="font-small-3">{{ artistDetail.downloaded_tracks_count || 0 }}</strong>
                              <span class="text-muted font-small-3 mx-25">/</span>
                              <span class="font-small-3">{{ artistDetail.tracks_count || 0 }}</span>
                            </div>
                            <div v-if="artistDetail.onehitwonder" class="d-flex align-items-center">
                              <b-badge variant="light-warning" class="font-small-2">One Hit Wonder</b-badge>
                            </div>
                            <div v-if="artistDetail.notion" class="d-flex align-items-center">
                              <b-badge variant="light-info" class="font-small-2">Notion</b-badge>
                            </div>
                            <b-button
                                v-if="!artistDetail.notion_id && $can('manage', 'db-music')"
                                variant="outline-info"
                                size="sm"
                                v-b-tooltip.hover
                                title="Страницы в Notion ещё нет — создать и привязать"
                                @click="syncNotion"
                            >
                              <feather-icon icon="UploadCloudIcon" size="12" class="mr-25"/>
                              Синк в Notion
                            </b-button>
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Кластер:</span>
                              <v-select
                                  v-model="selectedClusterIds"
                                  :options="clusterOptions"
                                  :reduce="o => o.id"
                                  label="title"
                                  multiple
                                  :disabled="!$can('manage', 'artist') || isSavingClusters"
                                  placeholder="—"
                                  style="min-width: 220px"
                                  @input="saveClusters"
                              />
                              <b-spinner v-if="isSavingClusters" small class="ml-50"/>
                            </div>
                            <div
                                v-if="selectedClusters.length > 1"
                                class="d-flex align-items-center flex-wrap"
                                style="gap: .25rem;"
                            >
                              <span class="text-muted font-small-3 mr-25">Главный:</span>
                              <b-badge
                                  v-for="cluster in selectedClusters"
                                  :key="'main-cluster-' + cluster.id"
                                  :variant="mainClusterId === cluster.id ? 'warning' : 'light-secondary'"
                                  :class="{'cursor-pointer': $can('manage', 'artist')}"
                                  title="Сделать главным жанром"
                                  @click="$can('manage', 'artist') && !isSavingClusters && setMainCluster(cluster.id)"
                              >
                                <feather-icon icon="StarIcon" size="10" class="mr-25"/>{{ cluster.title }}
                              </b-badge>
                            </div>
                            <div class="d-flex align-items-center ml-md-auto" style="gap: 1rem;">
                              <b-form-checkbox
                                  v-if="$can('manage', 'artist')"
                                  :checked="!!artistDetail.skip_originals"
                                  switch
                                  @change="toggleSkipOriginals"
                              >
                                Пропускать оригиналы
                              </b-form-checkbox>
                              <b-form-checkbox
                                  v-if="$can('manage', 'artist')"
                                  :checked="!!artistDetail.is_cover_artist"
                                  switch
                                  @change="toggleCoverArtist"
                              >
                                Кавер-артист
                              </b-form-checkbox>
                            </div>
                          </div>
                          <!-- Сложность -->
                          <div
                              class="d-flex flex-wrap align-items-center mt-75 pt-75"
                              style="gap: 0.5rem 1.5rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
                          >
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Сложность:</span>
                              <b-form-rating
                                  :value="artistDetail.difficulty_rating"
                                  :stars="10"
                                  inline
                                  no-border
                                  size="sm"
                                  variant="warning"
                                  class="p-0"
                                  :readonly="!$can('manage', 'artist')"
                                  @change="updateDifficultyRating"
                              />
                              <span
                                  v-if="artistDetail.calculated_difficulty != null"
                                  class="text-muted ml-50"
                                  style="font-size: 1.285rem;"
                                  v-b-tooltip.hover
                                  title="Расчётная сложность"
                              >({{ artistDetail.calculated_difficulty }})</span>
                              <feather-icon
                                  icon="HelpCircleIcon"
                                  size="14"
                                  class="text-muted ml-25"
                                  v-b-tooltip.hover.html="difficultyTooltip"
                              />
                              <span v-if="artistDetail.difficulty_rating == null" class="text-muted font-small-3 ml-50">не задана</span>
                            </div>
                            <div class="d-flex align-items-center ml-md-auto" style="gap: 0.75rem;">
                              <rating-stats-block
                                  :rating="artistDetail.difficulty_rating"
                                  :calculated="artistDetail.calculated_difficulty"
                                  :lastfm="artistDetail.popularity"
                                  :spotify="artistDetail.spotify_playcount"
                                  :yandex-monthly-listeners="artistDetail.yandex_monthly_listeners"
                              />
                              <b-button
                                  v-if="$can('manage', 'db-music')"
                                  variant="flat-secondary"
                                  class="btn-icon"
                                  size="sm"
                                  :disabled="recalcDifficultyLoading"
                                  v-b-tooltip.hover
                                  title="Пересчитать расчётный рейтинг сложности"
                                  @click="recalculateDifficulty"
                              >
                                <b-spinner v-if="recalcDifficultyLoading" small />
                                <feather-icon v-else icon="SlidersIcon" size="14" />
                              </b-button>
                              <b-button
                                  v-if="$can('manage', 'db-music')"
                                  variant="flat-secondary"
                                  class="btn-icon"
                                  size="sm"
                                  v-b-tooltip.hover
                                  :title="platformStatsSyncedTitle"
                                  @click="syncPlatformStats"
                              >
                                <b-spinner v-if="platformStatsSyncing" small />
                                <feather-icon v-else icon="RefreshCwIcon" size="14" />
                              </b-button>
                            </div>
                          </div>
                          <!-- Десятилетия -->
                          <div
                              class="d-flex flex-wrap align-items-center mt-75 pt-75"
                              style="gap: .25rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
                          >
                            <span class="text-muted font-small-3 mr-50">Десятилетия:</span>
                            <b-badge
                                v-for="decade in decadeOptions"
                                :key="'decade-' + decade"
                                :variant="selectedDecades.includes(decade) ? (mainDecade === decade ? 'warning' : 'primary') : 'light-secondary'"
                                :class="{'cursor-pointer': $can('manage', 'artist')}"
                                :title="selectedDecades.includes(decade) ? 'Клик — убрать, звезда — сделать главным' : 'Добавить'"
                                @click="$can('manage', 'artist') && !isSavingDecades && toggleDecade(decade)"
                            >
                              <feather-icon
                                  v-if="selectedDecades.includes(decade)"
                                  icon="StarIcon"
                                  size="10"
                                  class="mr-25"
                                  @click.stop="$can('manage', 'artist') && !isSavingDecades && setMainDecade(decade)"
                              />{{ String(decade).slice(2) }}-е
                            </b-badge>
                            <b-button
                                v-if="$can('manage', 'artist')"
                                v-b-tooltip.hover
                                title="Рассчитать по годам треков (без годов — через LLM)"
                                variant="flat-primary"
                                class="btn-icon p-25 ml-25"
                                size="sm"
                                :disabled="isSavingDecades"
                                @click="calculateDecades"
                            >
                              <feather-icon icon="RefreshCwIcon" size="12"/>
                            </b-button>
                            <b-spinner v-if="isSavingDecades" small class="ml-50"/>
                          </div>
                          <!-- Данные из Notion: блок виден всегда, пустые поля показываются прочерком -->
                          <div
                              class="d-flex flex-wrap align-items-center mt-75 pt-75"
                              style="gap: 0.5rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
                          >
                            <span
                                v-for="(fact, index) in artistFacts"
                                :key="fact.label"
                                class="font-small-3"
                            >
                              <span class="text-muted mr-25">{{ fact.label }}:</span>{{ fact.value }}
                              <span v-if="index < artistFacts.length - 1" class="text-muted ml-50">·</span>
                            </span>
                            <b-button
                                v-if="$can('manage', 'artist')"
                                v-b-tooltip.hover
                                title="Редактировать атрибуты"
                                variant="flat-primary"
                                class="btn-icon p-25"
                                size="sm"
                                @click="openAttributesModal"
                            >
                              <feather-icon icon="Edit2Icon" size="12"/>
                            </b-button>
                          </div>
                          <!-- Внешние ссылки -->
                          <div
                              class="d-flex flex-wrap align-items-center mt-75 pt-75"
                              style="gap: 0.5rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
                          >
                            <span class="text-muted font-small-3 mr-25">Ссылки:</span>

                            <!-- SHS -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.second_hand_songs" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  class="font-small-2 badge-shs"
                                  :href="'https://secondhandsongs.com/artist/' + artistDetail.external_ids.second_hand_songs"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                {{ externalLabel('second_hand_songs') }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist-externals')"
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('second_hand_songs')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                :disabled="!$can('manage', 'artist-externals')"
                                v-else
                                variant="outline-info"
                                size="sm"
                                class="py-25 px-50 font-small-2 badge-outline-shs"
                                @click="syncShs"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              SHS
                            </b-button>

                            <!-- Spotify -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.spotify" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  variant="light-success"
                                  class="font-small-2"
                                  :href="'https://open.spotify.com/artist/' + artistDetail.external_ids.spotify"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                {{ externalLabel('spotify') }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist-externals')"
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('spotify')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                :disabled="!$can('manage', 'artist-externals')"
                                v-else
                                variant="outline-success"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                @click="syncSpotify"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              Spotify
                            </b-button>

                            <!-- Yandex Music -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.yandex" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  class="font-small-2 badge-yandex"
                                  :href="'https://music.yandex.ru/artist/' + artistDetail.external_ids.yandex"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                {{ externalLabel('yandex') }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist-externals')"
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('yandex')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                :disabled="!$can('manage', 'artist-externals')"
                                v-else
                                variant="outline-yandex"
                                class="py-25 px-50 font-small-2 btn-outline-yandex"
                                size="sm"
                                @click="syncYandex"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              Yandex
                            </b-button>

                            <!-- YouTube -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.youtube" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  class="font-small-2"
                                  :href="'https://www.youtube.com/channel/' + artistDetail.external_ids.youtube"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; background-color: #ff0000 !important; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                YouTube: {{ artistDetail.external_ids.youtube }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist-externals')"
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('youtube')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                v-else
                                variant="outline-danger"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                disabled
                                style="border-color: #ff0000 !important; color: #ff0000 !important;"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              YouTube
                            </b-button>

                            <!-- Wikipedia -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.wikipedia" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  variant="light-secondary"
                                  class="font-small-2"
                                  :href="wikipediaUrl(artistDetail.external_ids.wikipedia)"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                {{ wikipediaLabel(artistDetail.external_ids.wikipedia) }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist-externals')"
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('wikipedia')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                :disabled="!$can('manage', 'artist-externals')"
                                v-else
                                variant="outline-secondary"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                @click="syncWikipedia"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              Wiki
                            </b-button>

                            <!-- MusicBrainz -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.musicbrainz" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  class="font-small-2 badge-musicbrainz"
                                  :href="'https://musicbrainz.org/artist/' + artistDetail.external_ids.musicbrainz"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                {{ externalLabel('musicbrainz') }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  v-if="$can('manage', 'artist-externals')"
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('musicbrainz')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                :disabled="!$can('manage', 'artist-externals')"
                                v-else
                                variant="outline-musicbrainz"
                                size="sm"
                                class="py-25 px-50 font-small-2 btn-outline-musicbrainz"
                                @click="syncMusicBrainz"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              MusicBrainz
                            </b-button>

                            <!-- Добавить внешний ID -->
                            <b-button
                                v-if="$can('manage', 'artist-externals')"
                                v-b-tooltip.hover
                                title="Добавить внешний ID"
                                variant="flat-primary"
                                class="btn-icon p-25"
                                size="sm"
                                @click="showAddExternalIdModal = true"
                            >
                              <feather-icon icon="PlusCircleIcon" size="14"/>
                            </b-button>
                          </div>

                          <!-- Modal: Добавить внешний ID артисту -->
                          <b-modal
                              v-model="showAddExternalIdModal"
                              title="Добавить внешний ID"
                              ok-title="Добавить"
                              cancel-title="Отмена"
                              centered
                              :ok-disabled="!externalIdInput.trim() || isAddingExternalId"
                              :busy="isAddingExternalId"
                              @ok.prevent="addExternalId"
                              @hidden="externalIdInput = ''"
                          >
                            <b-form-group
                                label="Ссылка или идентификатор"
                                description="Вставьте ссылку на артиста (Spotify, YouTube, Yandex Music, SHS, MusicBrainz, Wikipedia) или идентификатор"
                            >
                              <b-form-input
                                  v-model="externalIdInput"
                                  placeholder="https://open.spotify.com/artist/... или ID"
                                  autofocus
                                  @keyup.enter="addExternalId"
                              />
                            </b-form-group>
                          </b-modal>

                          <merge-artists-modal
                              v-if="artistDetail"
                              v-model="showMergeArtistsModal"
                              :artist="artistDetail"
                              @merged="onArtistsMerged"
                          />
                        </div>

                        <div class="mb-1 d-flex align-items-center flex-wrap">
                          <b-button variant="primary" class="mr-1"
                              v-if="$can('manage', 'db-music')"
                                    @click="loadLastfmInfo"
                          >
                            <feather-icon
                                icon="TvIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">LastFM</span>
                          </b-button>
                          <b-button
                              v-if="$can('manage', 'artist-externals')"
                              class="mr-1 badge-musicbrainz"
                              :disabled="!(artistDetail.external_ids && artistDetail.external_ids.musicbrainz)"
                              @click="openMusicBrainzAttributes"
                          >
                            <feather-icon
                                icon="DatabaseIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">MusicBrainz</span>
                          </b-button>
                          <b-button
                              v-if="$can('manage', 'db-music')"
                              variant="outline-danger"
                              class="mr-1"
                              @click="flushCache"
                          >
                            <feather-icon
                                icon="TrashIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">Очистить кеш</span>
                          </b-button>
                          <b-button
                              v-if="$can('manage', 'db-music')"
                              variant="outline-primary"
                              class="mr-1"
                              @click="syncNotion"
                          >
                            <feather-icon
                                icon="RefreshCwIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">Синк Notion</span>
                          </b-button>
                          <b-button
                              v-if="$can('manage', 'artist')"
                              variant="outline-primary"
                              class="mr-1"
                              @click="showMergeArtistsModal = true"
                          >
                            <feather-icon icon="GitMergeIcon" class="mr-50"/>
                            <span class="align-middle">Объединить с …</span>
                          </b-button>
                          <b-button
                              v-if="$can('manage', 'db-music')"
                              variant="outline-danger"
                              class="mr-1"
                              @click="deleteArtist"
                          >
                            <feather-icon icon="Trash2Icon" class="mr-50"/>
                            <span class="align-middle">Удалить артиста</span>
                          </b-button>
                        </div>

                        <b-tabs
                            pills
                        >
                          <!-- Tab: Песни (таблица) -->
                          <b-tab v-if="$can('view', 'artist-tracks')">
                            <template #title>
                              <feather-icon
                                  icon="Share2Icon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Песни</span>
                              <b-badge variant="success" class="ml-50">
                                {{ artistDetail.tracks.length }}
                              </b-badge>
                              <small v-if="pendingBy('track', 'create').length" class="ml-25 pending-submission-count">
                                +{{ pendingBy('track', 'create').length }}
                              </small>
                            </template>

                            <div class="my-1 py-25">
                              <div v-if="$can('manage', 'db-music')" class="mb-1 d-flex align-items-center">
                                <!-- Массовый сдвиг рейтинга идёт мимо модерации — только админу -->
                                <template v-if="$can('manage', 'all')">
                                  <b-button
                                      size="sm"
                                      variant="outline-secondary"
                                      class="btn-icon mr-25"
                                      :disabled="!selectedTrackIds.length"
                                      v-b-tooltip.hover
                                      title="Понизить рейтинг отмеченных треков"
                                      @click="shiftSelectedTracksRating(-1)"
                                  >
                                    <feather-icon icon="MinusIcon"/>
                                  </b-button>
                                  <b-button
                                      size="sm"
                                      variant="outline-secondary"
                                      class="btn-icon mr-25"
                                      :disabled="!selectedTrackIds.length"
                                      v-b-tooltip.hover
                                      title="Повысить рейтинг отмеченных треков"
                                      @click="shiftSelectedTracksRating(1)"
                                  >
                                    <feather-icon icon="PlusIcon"/>
                                  </b-button>
                                  <span v-if="selectedTrackIds.length" class="text-muted font-small-3 mr-1">
                                    отмечено: {{ selectedTrackIds.length }}
                                  </span>
                                </template>
                                <b-button
                                    size="sm"
                                    variant="outline-primary"
                                    class="mr-1"
                                    @click="openLlmCoversModal('performed')"
                                >
                                  <feather-icon icon="ZapIcon" class="mr-50"/>
                                  <span class="align-middle">Каверы артиста</span>
                                </b-button>
                                <b-button
                                    size="sm"
                                    variant="outline-primary"
                                    @click="openLlmCoversModal('received')"
                                >
                                  <feather-icon icon="ZapIcon" class="mr-50"/>
                                  <span class="align-middle">Каверы на артиста</span>
                                </b-button>
                              </div>
                              <!-- Треки авторов на одобрении: отдельным блоком, у них ещё нет id трека -->
                              <div v-if="pendingBy('track', 'create').length" class="mb-1">
                                <h6 class="mb-50">На одобрении</h6>
                                <div
                                    v-for="submission in pendingBy('track', 'create')"
                                    :key="'pending-track-' + submission.id"
                                    class="d-flex align-items-center p-50 mb-50 pending-submission"
                                    style="gap: 0.75rem;"
                                >
                                  <b-badge variant="warning">На одобрении</b-badge>
                                  <span class="font-weight-bold">{{ submission.present.title }}</span>
                                  <small v-if="$can('manage', 'all') && submission.author" class="text-muted">
                                    {{ submission.author.name }}
                                  </small>
                                </div>
                              </div>
                              <b-table
                                  ref="refTracksListTable"
                                  :items="artistDetail.tracks"
                                  responsive
                                  :fields="tableColumns"
                                  :sort-compare="tracksSortCompare"
                                  :tbody-tr-class="rowClass"
                                  primary-key="id"
                                  show-empty
                                  empty-text="No tracks"
                                  class="position-relative"
                              >

                                <!-- Header: чекбокс «выбрать все треки» -->
                                <template #head(select)>
                                  <b-form-checkbox
                                      :checked="allTracksSelected"
                                      @change="toggleAllTracksSelection"
                                  />
                                </template>

                                <!-- Column: чекбокс выбора трека для массового изменения рейтинга -->
                                <template #cell(select)="data">
                                  <b-form-checkbox v-model="selectedTrackIds" :value="data.item.id"/>
                                </template>

                                <!-- Column: Артист (+ id трека) -->
                                <template #cell(artist)="data">
                                  <div class="text-nowrap">
                                    {{ data.item.artist }}
                                    <div class="font-small-2 text-muted" style="opacity: 0.6;">#{{ data.item.id }}</div>
                                  </div>
                                </template>

                                <!-- Column: Трек (+ год, кнопка общего плеера в шапке) -->
                                <template #cell(track)="data">
                                  <span>
                                    {{ data.item.track }}
                                    <span v-if="data.item.release_year" class="text-muted font-small-3 ml-25">{{ data.item.release_year }}</span>
                                    <b-badge v-if="pendingForTarget('track', data.item.id)" variant="warning" class="ml-25">
                                      {{ pendingTargetLabel(pendingForTarget('track', data.item.id)) }}
                                    </b-badge>
                                  </span>
                                  <template v-if="data.item.file">
                                    <b-button
                                        variant="flat-secondary"
                                        size="sm"
                                        class="btn-icon p-25 ml-25"
                                        v-b-tooltip.hover
                                        :title="isTrackPlaying('track-' + data.item.id) ? 'Остановить' : 'Прослушать'"
                                        @click="playArtistTrack(data.item)"
                                    >
                                      <feather-icon :icon="isTrackPlaying('track-' + data.item.id) ? 'PauseIcon' : 'PlayIcon'" size="14" />
                                    </b-button>
                                  </template>
                                </template>

                                <!-- Header: Сложность + массовый пересчёт рейтинга и перезагрузка прослушиваний -->
                                <template #head(difficulty_rating)="data">
                                  <span class="align-middle">{{ data.label }}</span>
                                  <b-button
                                      variant="flat-secondary"
                                      size="sm"
                                      class="btn-icon p-25 ml-25"
                                      :disabled="recalcTracksDifficultyLoading"
                                      v-b-tooltip.hover
                                      title="Пересчитать расчётный рейтинг у всех треков"
                                      @click="recalculateTracksDifficulty"
                                  >
                                    <b-spinner v-if="recalcTracksDifficultyLoading" small />
                                    <feather-icon v-else icon="SlidersIcon" size="14" />
                                  </b-button>
                                  <b-button
                                      variant="flat-secondary"
                                      size="sm"
                                      class="btn-icon p-25 ml-25"
                                      :disabled="syncTracksStatsLoading"
                                      v-b-tooltip.hover
                                      title="Перезагрузить прослушивания у всех треков"
                                      @click="syncTracksPlatformStats"
                                  >
                                    <b-spinner v-if="syncTracksStatsLoading" small />
                                    <feather-icon v-else icon="RefreshCwIcon" size="14" />
                                  </b-button>
                                </template>

                                <!-- Column: Сложность (звёзды открываются кликом по установленному рейтингу) -->
                                <template #cell(difficulty_rating)="data">
                                  <div v-if="editingTrackRatingId === data.item.id" class="d-inline-flex align-items-center">
                                    <b-form-rating
                                        :value="data.item.difficulty_rating"
                                        :stars="10"
                                        inline
                                        no-border
                                        size="sm"
                                        variant="warning"
                                        class="p-0"
                                        @change="updateTrackDifficultyRating(data.item, $event)"
                                    />
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-0 ml-25"
                                        size="sm"
                                        @click="editingTrackRatingId = null"
                                    >
                                      <feather-icon icon="XIcon" size="14" />
                                    </b-button>
                                  </div>
                                  <rating-stats-block
                                      v-else
                                      compact
                                      rating-editable
                                      refreshable
                                      :refreshing="syncingTrackStatsId === data.item.id"
                                      :rating="data.item.difficulty_rating"
                                      :calculated="data.item.calculated_difficulty"
                                      :lastfm="data.item.play_count"
                                      :spotify="data.item.spotify_playcount"
                                      @rating-click="editingTrackRatingId = data.item.id"
                                      @refresh="syncTrackPlatformStats(data.item)"
                                  />
                                </template>

                                <!-- Header: Инфо + массовые действия для сервисов -->
                                <template #head(info)="data">
                                  <div>{{ data.label }}</div>
                                  <!-- Массовая привязка ID и синк SHS — только админ (у остальных 403) -->
                                  <div v-if="$can('manage', 'all')" class="mt-25" style="white-space: nowrap;">
                                    <b-button
                                        variant="success"
                                        class="btn-icon p-0"
                                        style="width: 1.6rem; height: 1.6rem; font-size: 0.6rem; font-weight: 700;"
                                        :disabled="bindingTracksSource !== null"
                                        v-b-tooltip.hover
                                        title="Найти и привязать Spotify ID (и год) у треков без него"
                                        @click="bindTracksExternalIds('spotify')"
                                    >
                                      <b-spinner v-if="bindingTracksSource === 'spotify'" small />
                                      <span v-else>SP</span>
                                    </b-button>
                                    <b-button
                                        class="btn-icon p-0 ml-25 btn-yandex"
                                        style="width: 1.6rem; height: 1.6rem; font-size: 0.6rem; font-weight: 700;"
                                        :disabled="bindingTracksSource !== null"
                                        v-b-tooltip.hover
                                        title="Найти и привязать Yandex ID (и год) у треков без него"
                                        @click="bindTracksExternalIds('yandex')"
                                    >
                                      <b-spinner v-if="bindingTracksSource === 'yandex'" small />
                                      <span v-else>YA</span>
                                    </b-button>
                                    <b-button
                                        v-if="showShsTracksButton"
                                        class="btn-icon p-0 ml-25 shs-button"
                                        style="width: 1.6rem; height: 1.6rem; font-size: 0.6rem; font-weight: 700;"
                                        v-b-tooltip.hover
                                        title="Загрузить треки из SecondHandSongs"
                                        @click="syncShsTracks"
                                    >SH</b-button>
                                  </div>
                                </template>

                                <!-- Column: Info (External IDs) -->
                                <template #cell(info)="data">
                                  <span class="text-nowrap" v-if="data.item.external_ids">
                                    <b-badge
                                        v-if="data.item.external_ids.spotify"
                                        v-b-tooltip.hover
                                        :title="'Spotify: ' + trackExternalLabel(data.item, 'spotify')"
                                        tag="a"
                                        :href="'https://open.spotify.com/track/' + data.item.external_ids.spotify"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="success"
                                        class="mr-25 cursor-pointer"
                                        style="font-size: 0.7rem;"
                                    >SP</b-badge>
                                    <b-badge
                                        v-if="data.item.external_ids.yandex"
                                        v-b-tooltip.hover
                                        :title="'Yandex Music: ' + trackExternalLabel(data.item, 'yandex')"
                                        tag="a"
                                        :href="'https://music.yandex.ru/track/' + data.item.external_ids.yandex"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="mr-25 cursor-pointer badge-yandex"
                                        style="font-size: 0.7rem;"
                                    >YA</b-badge>
                                    <b-badge
                                        v-if="data.item.external_ids.youtube"
                                        v-b-tooltip.hover
                                        :title="'YouTube: ' + trackExternalLabel(data.item, 'youtube')"
                                        tag="a"
                                        :href="'https://www.youtube.com/watch?v=' + data.item.external_ids.youtube"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="danger"
                                        class="mr-25 cursor-pointer"
                                        style="font-size: 0.7rem; background-color: #ff0000 !important;"
                                    >YT</b-badge>
                                    <b-badge
                                        v-if="data.item.external_ids.second_hand_songs"
                                        v-b-tooltip.hover
                                        :title="'SHS: ' + trackExternalLabel(data.item, 'second_hand_songs')"
                                        tag="a"
                                        :href="'https://secondhandsongs.com/performance/' + data.item.external_ids.second_hand_songs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="mr-25 cursor-pointer badge-shs"
                                        style="font-size: 0.7rem;"
                                    >SH</b-badge>
                                  </span>
                                </template>

                                <!-- Column: Client -->
                                <template #cell(covers)="data">
                                  <b-button
                                      v-if="data.item.has_covers"
                                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                      variant="success"
                                      class="btn-icon"
                                      :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                                  >
                                    <feather-icon icon="DiscIcon"/>
                                  </b-button>
                                  <b-button
                                      v-else-if="data.item.is_cover"
                                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                      variant="warning"
                                      class="btn-icon"
                                      :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                                  >
                                    <feather-icon icon="DiscIcon"/>
                                  </b-button>
                                  <b-button
                                      v-else
                                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                      variant="secondary"
                                      class="btn-icon"
                                      :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                                  >
                                    <feather-icon icon="DiscIcon"/>
                                  </b-button>
                                </template>

                                <!-- Header: действия + кнопка «игнорировать пропущенное» -->
                                <template #head(actions)="data">
                                  <span class="align-middle">{{ data.label }}</span>
                                  <b-button
                                      v-if="$can('manage', 'all')"
                                      variant="flat-secondary"
                                      size="sm"
                                      class="btn-icon p-25 ml-25"
                                      v-b-tooltip.hover
                                      title="Игнорировать пропущенные треки"
                                      @click="ignoreTracks"
                                  >
                                    <feather-icon icon="UsersIcon" size="14" />
                                  </b-button>
                                </template>

                                <template #cell(actions)="data">
                                  <span class="text-nowrap">
                                    <b-button
                                        :disabled="!$can('view', 'track-view')"
                                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                        variant="warning"
                                        class="btn-icon ml-1"
                                        :to="$can('view', 'track-view') ? {name: 'track-detail', params: { id: data.item.id }} : null"
                                    >
                                    <feather-icon icon="DownloadIcon"/>
                                    </b-button>
                                    <b-button
                                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                        :variant="data.item.file ? `success` : ``"
                                        :disabled="!data.item.file"
                                        class="btn-icon ml-1"
                                        :href="data.item.url"
                                    >
                                      <feather-icon :icon="data.item.file ? `SaveIcon` : `FileIcon`"/>
                                    </b-button>
                                    <b-button
                                        :variant="data.item.ignore ? 'danger' : 'primary'"
                                        class="btn-icon ml-1"
                                        @click="toggleIgnore(data.item)"
                                    >
                                      <feather-icon :icon="(data.item.ignore) ? 'EyeOffIcon' : 'EyeIcon'"/>
                                    </b-button>
                                    <b-button
                                        v-if="$can('manage', 'track-delete')"
                                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                        variant="danger"
                                        class="btn-icon ml-1"
                                        :disabled="ownPendingForTarget('track', data.item.id)"
                                        @click="deleteTrack(data.item.id)"
                                    >
                                      <feather-icon icon="Trash2Icon"/>
                                    </b-button>
                                  </span>

                                </template>
                              </b-table>

                              <b-button variant="primary" class="mr-1"
                                        @click="loadAdditionalTracks"
                              >
                                <feather-icon
                                    icon="PlusSquareIcon"
                                    class="mr-50"
                                />
                                <span class="align-middle">Дополнительные треки</span>
                              </b-button>

                              <b-input-group class="d-inline-flex ml-1" style="width: 350px; vertical-align: middle;">
                                <b-form-input
                                    v-model="trackSearchQuery"
                                    placeholder="Поиск трека по названию..."
                                    size="sm"
                                    @keyup.enter="searchTracksByName"
                                />
                                <b-input-group-append>
                                  <b-button variant="info" size="sm" @click="searchTracksByName">
                                    <feather-icon icon="SearchIcon" class="mr-50" />
                                    <span class="align-middle">Поиск</span>
                                  </b-button>
                                </b-input-group-append>
                              </b-input-group>

                              <div v-if="additional && additional.length">
                                <div v-for="group in additional" :key="group.source" class="mt-2">
                                  <h6 class="mb-0 d-flex align-items-center">
                                    <source-icon :source="group.source" class="mr-50"/>
                                    {{ additionalGroupTitle(group.source) }}
                                  </h6>

                                  <div v-for="track in group.tracks" class="d-flex flex-md-row flex-column mt-1">
                                    <b-button
                                        variant="warning"
                                        class="btn-icon mr-1"
                                        size="sm"
                                        @click="addTrack(track.title)"
                                    >
                                      <feather-icon icon="PlusIcon"/>
                                    </b-button>

                                    <span class="font-weight-bold d-block d-flex align-items-center">
                                      <span :class="track.other_artist ? 'text-muted' : 'text-success'">
                                        {{ track.artist }} - {{ track.title }}<template v-if="track.popularity != null"> [{{ track.popularity }}]</template>
                                      </span>
                                      <b-form-rating
                                          v-if="track.popularity != null"
                                          :value="toStarRating(track.popularity)"
                                          readonly
                                          inline
                                          no-border
                                          half
                                          size="sm"
                                          variant="warning"
                                          class="p-0 ml-50"
                                      />
                                      <b-badge v-if="track.playcount" variant="light-secondary" class="ml-50">
                                        ▶ {{ formatNumber(track.playcount) }}
                                      </b-badge>
                                    </span>

                                  </div>
                                </div>
                              </div>

                            </div>
                          </b-tab>


                          <!-- Tab: Списки -->
                          <b-tab v-if="$can('view', 'db-music')">
                            <template #title>
                              <feather-icon
                                  icon="Share2Icon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Списки</span>
                              <b-badge variant="success" class="ml-50">
                                {{ artistDetail.lists_count || 0 }}
                              </b-badge>
                            </template>

                            <div class="my-1 py-25">
                              <div class="d-flex align-items-center flex-wrap mb-1">
                                <b-button
                                    variant="primary"
                                    size="sm"
                                    class="mr-1"
                                    :disabled="isSuggestingNotionTags"
                                    @click="suggestNotionTags(false)"
                                >
                                  <b-spinner v-if="isSuggestingNotionTags" small class="mr-50"/>
                                  <feather-icon v-else icon="ZapIcon" class="mr-50"/>
                                  Подобрать
                                </b-button>
                                <b-button
                                    v-if="notionTagsLoaded"
                                    variant="outline-secondary"
                                    size="sm"
                                    class="mr-1"
                                    :disabled="isSuggestingNotionTags"
                                    @click="suggestNotionTags(true)"
                                >
                                  <feather-icon icon="RefreshCwIcon" class="mr-50"/>
                                  Пересобрать
                                </b-button>
                                <b-badge
                                    v-if="notionTagsLoaded && notionTagsCached"
                                    variant="light-secondary"
                                    v-b-tooltip.hover
                                    title="Результат прошлого подбора; «Пересобрать» спросит ЛЛМ заново"
                                >
                                  из кеша
                                </b-badge>
                              </div>

                              <div v-if="notionTagsLoaded" class="mb-2">
                                <div
                                    v-for="grade in notionTagGrades"
                                    :key="grade.key"
                                    class="mb-1"
                                >
                                  <div class="mb-50">
                                    <b-badge :variant="grade.variant">{{ grade.title }}</b-badge>
                                    <span class="text-muted ml-50">{{ notionTagGroups[grade.key].length }}</span>
                                  </div>
                                  <div v-if="!notionTagGroups[grade.key].length" class="text-muted">
                                    Нет тегов
                                  </div>
                                  <div v-else class="d-flex flex-wrap align-items-center">
                                    <div
                                        v-for="item in visibleNotionTags(grade.key)"
                                        :key="item.tag"
                                        class="d-flex align-items-center mr-1 mb-50"
                                    >
                                      <b-badge
                                          :variant="'light-' + grade.variant"
                                          v-b-tooltip.hover
                                          :title="item.reason || 'Обоснование не указано'"
                                      >
                                        {{ item.tag }}
                                      </b-badge>
                                      <b-button
                                          variant="flat-success"
                                          class="btn-icon ml-25"
                                          size="sm"
                                          :disabled="addingNotionTag !== null"
                                          v-b-tooltip.hover
                                          title="Добавить тег в Notion"
                                          @click="addNotionTag(item.tag)"
                                      >
                                        <b-spinner v-if="addingNotionTag === item.tag" small/>
                                        <feather-icon v-else icon="PlusIcon"/>
                                      </b-button>
                                    </div>
                                    <b-button
                                        v-if="notionTagGroups[grade.key].length > notionTagsVisibleLimit"
                                        variant="flat-primary"
                                        size="sm"
                                        class="mb-50"
                                        @click="toggleNotionTagsExpanded(grade.key)"
                                    >
                                      {{ notionTagsToggleLabel(grade.key) }}
                                    </b-button>
                                  </div>
                                </div>

                                <div class="d-flex align-items-center mt-1 notion-tag-manual">
                                  <v-select
                                      v-model="manualNotionTag"
                                      :options="notionTagDictionary"
                                      placeholder="Добавить тег вручную"
                                      class="flex-grow-1"
                                  />
                                  <b-button
                                      variant="success"
                                      class="btn-icon ml-50"
                                      size="sm"
                                      :disabled="!manualNotionTag || addingNotionTag !== null"
                                      v-b-tooltip.hover
                                      title="Добавить выбранный тег в Notion"
                                      @click="addNotionTag(manualNotionTag)"
                                  >
                                    <b-spinner v-if="addingNotionTag === manualNotionTag" small/>
                                    <feather-icon v-else icon="PlusIcon"/>
                                  </b-button>
                                </div>
                              </div>

                              <b-table
                                  :items="artistDetail.lists || []"
                                  :fields="artistListsColumns"
                                  responsive
                                  show-empty
                                  empty-text="Списков нет"
                                  class="position-relative"
                              >
                                <template #cell(title)="data">
                                  <router-link
                                      :to="{ name: 'list-view', params: { id: data.item.id } }"
                                  >
                                    {{ data.item.title }}
                                  </router-link>
                                </template>

                                <template #cell(items_count)="data">
                                  <b-badge variant="light-primary">
                                    {{ data.item.items_count }}
                                  </b-badge>
                                </template>

                                <template #cell(auto)="data">
                                  <b-badge :variant="data.item.auto ? 'light-success' : 'light-secondary'">
                                    {{ data.item.auto ? 'да' : '—' }}
                                  </b-badge>
                                </template>

                                <template #cell(autotopics)="data">
                                  <div
                                      v-if="(data.item.autotopics || []).length"
                                      class="d-flex flex-wrap"
                                  >
                                    <b-badge
                                        v-for="autotopic in data.item.autotopics"
                                        :key="autotopic.id"
                                        variant="light-info"
                                        class="mr-50 mb-25"
                                    >
                                      {{ autotopic.title }}
                                    </b-badge>
                                  </div>
                                  <b-badge
                                      v-else
                                      variant="light-secondary"
                                  >
                                    свободный
                                  </b-badge>
                                </template>
                              </b-table>
                            </div>
                          </b-tab>

                          <!-- Tab: SHS Треки -->
                          <b-tab
                              v-if="$can('view', 'artist-tracks') && artistDetail.external_ids && artistDetail.external_ids.second_hand_songs != null"
                              @click="fetchShsTracks"
                          >
                            <template #title>
                              <feather-icon
                                  icon="MusicIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">SHS Треки</span>
                            </template>

                            <div class="my-1 py-25 shs-actions">
                              <div v-if="shsTracksError" class="text-danger mb-1">{{ shsTracksError }}</div>
                              <b-table
                                  v-if="mergedShsTracks.length"
                                  :items="mergedShsTracks"
                                  responsive
                                  :fields="shsTableColumns"
                                  show-empty
                                  empty-text="Нет треков"
                                  class="position-relative"
                              >
                                <template #cell(db_title)="data">
                                  <router-link
                                      v-if="data.item.db_title && data.item.track_id"
                                      :to="{name: 'track-detail', params: { id: data.item.track_id }, query: { tab: 'covers' }}"
                                  >
                                    {{ data.item.db_title }}
                                  </router-link>
                                </template>

                                <template #cell(shs_title)="data">
                                  <span>{{ data.item.shs_title }}</span>
                                </template>

                                <template #cell(is_original)="data">
                                  <div v-if="data.item.is_original !== null" class="d-flex align-items-center">
                                    <b-badge :variant="data.item.is_original ? 'success' : 'warning'">
                                      {{ data.item.is_original ? 'Оригинал' : 'Кавер' }}
                                    </b-badge>
                                    <b-badge
                                        v-if="data.item.is_original && data.item.db_covers_count !== null"
                                        variant="light-info"
                                        class="ml-25"
                                        v-b-tooltip.hover
                                        title="Каверов в БД"
                                    >
                                      {{ data.item.db_covers_count }}
                                    </b-badge>
                                    <b-button
                                        v-if="!data.item.is_original && data.item.shs_id"
                                        variant="flat-info"
                                        class="btn-icon ml-25"
                                        size="sm"
                                        v-b-tooltip.hover
                                        title="Информация из SHS: оригинал и каверы"
                                        @click="openShsPerformanceModal(data.item)"
                                    >
                                      <feather-icon icon="InfoIcon"/>
                                    </b-button>
                                  </div>
                                </template>

                                <template #cell(actions)="data">
                                  <div v-if="!data.item.linked && data.item.shs_id" class="d-flex">
                                    <b-button
                                        v-if="!data.item.track_id"
                                        variant="success"
                                        class="btn-icon mr-25"
                                        size="sm"
                                        @click="addShsTrack(data.item)"
                                        v-b-tooltip.hover
                                        title="Создать новый трек"
                                    >
                                      <feather-icon icon="PlusIcon"/>
                                    </b-button>
                                    <b-button
                                        v-if="data.item.track_id && !data.item.is_self_cover && $can('manage', 'all')"
                                        variant="primary"
                                        class="btn-icon mr-25"
                                        size="sm"
                                        @click="quickLinkShsTrack(data.item)"
                                        v-b-tooltip.hover
                                        title="Привязать к найденному треку"
                                    >
                                      <feather-icon icon="LinkIcon"/>
                                    </b-button>
                                    <b-button
                                        v-if="$can('manage', 'all')"
                                        variant="warning"
                                        class="btn-icon"
                                        size="sm"
                                        @click="openLinkShsTrackModal(data.item)"
                                        v-b-tooltip.hover
                                        title="Привязать к существующему"
                                    >
                                      <feather-icon icon="SearchIcon"/>
                                    </b-button>
                                  </div>
                                </template>

                                <template #cell(uri)="data">
                                  <b-button
                                      v-if="data.item.uri"
                                      variant="flat-primary"
                                      class="btn-icon"
                                      size="sm"
                                      :href="data.item.uri"
                                      target="_blank"
                                  >
                                    <feather-icon icon="ExternalLinkIcon"/>
                                  </b-button>
                                </template>
                              </b-table>
                              <div v-else-if="!shsTracksError" class="text-muted">Нажмите на вкладку для загрузки</div>
                            </div>
                          </b-tab>

                          <!-- Tab: Алиасы -->
                          <b-tab v-if="$can('view', 'db-music')" @click="fetchAliases">
                            <template #title>
                              <feather-icon
                                  icon="UsersIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Алиасы</span>
                            </template>

                            <div class="my-1 py-25">
                              <div v-if="artistAliases.length">
                                <div
                                    v-for="alias in artistAliases"
                                    :key="alias.id"
                                    class="d-flex align-items-center mt-1"
                                >
                                  <feather-icon
                                      icon="ChevronRightIcon"
                                      size="16"
                                      class="mr-50"
                                  />
                                  <span>{{ alias.alias }}</span>
                                </div>
                              </div>
                              <div v-else class="text-muted">Нет алиасов</div>

                              <div v-if="externalAliasCandidates.length" class="mt-1">
                                <h6>Из внешних ссылок:</h6>
                                <div
                                    v-for="candidate in externalAliasCandidates"
                                    :key="candidate.name"
                                    class="d-flex align-items-center mt-1"
                                >
                                  <b-button
                                      variant="success"
                                      class="btn-icon mr-1"
                                      size="sm"
                                      @click="addAliasFromExternal(candidate.name)"
                                  >
                                    <feather-icon icon="UserPlusIcon"/>
                                  </b-button>
                                  <span>{{ candidate.name }}</span>
                                  <span class="text-muted font-small-2 ml-50">({{ candidate.sources.join(', ') }})</span>
                                </div>
                              </div>

                              <div class="d-flex align-items-center mt-1">
                                <b-button
                                    variant="success"
                                    class="btn-icon mr-1"
                                    @click="addManualAlias"
                                    :disabled="!manualAliasName.trim()"
                                >
                                  <feather-icon icon="UserPlusIcon"/>
                                </b-button>
                                <b-form-input
                                    v-model="manualAliasName"
                                    placeholder="Имя алиаса"
                                />
                              </div>

                              <b-button
                                  variant="primary"
                                  class="mt-1"
                                  @click="searchForAliases"
                              >
                                <feather-icon
                                    icon="SearchIcon"
                                    class="mr-50"
                                />
                                <span class="align-middle">Поиск</span>
                              </b-button>

                              <div v-if="aliasSearchResults.length" class="mt-1">
                                <h6>Результаты поиска:</h6>
                                <div
                                    v-for="result in aliasSearchResults"
                                    :key="result.name"
                                    class="d-flex align-items-center mt-1"
                                >
                                  <b-button
                                      variant="success"
                                      class="btn-icon mr-1"
                                      size="sm"
                                      @click="addAliasFromSearch(result.name)"
                                  >
                                    <feather-icon icon="UserPlusIcon"/>
                                  </b-button>
                                  <span>{{ result.name }}</span>
                                </div>
                              </div>
                            </div>
                          </b-tab>

                          <!-- Tab: Состав -->
                          <b-tab v-if="$can('view', 'artist-members')" @click="initMembers">
                            <template #title>
                              <feather-icon
                                  :icon="hasMembers ? 'UsersIcon' : 'UserXIcon'"
                                  :class="hasMembers ? 'text-success' : 'text-muted'"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Состав</span>
                            </template>

                            <div class="my-1 py-25">
                              <!-- Правка состава на одобрении: было → стало -->
                              <b-alert
                                  v-if="pendingBy('members')[0]"
                                  variant="warning"
                                  show
                                  class="p-1"
                              >
                                <div class="font-weight-bold mb-50">
                                  Состав на одобрении
                                  <small v-if="$can('manage', 'all') && pendingBy('members')[0].author" class="font-weight-normal">
                                    · {{ pendingBy('members')[0].author.name }}
                                  </small>
                                </div>
                                <submission-preview :submission="pendingBy('members')[0]" />
                              </b-alert>
                              <!-- Кнопки действий -->
                              <div class="d-flex mb-1" style="gap: 0.5rem;">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    :disabled="isLoadingMembers"
                                    @click="loadMembers"
                                >
                                  <b-spinner v-if="isLoadingMembers" small class="mr-50" />
                                  <feather-icon v-else icon="DownloadCloudIcon" class="mr-50" />
                                  Загрузить через AI
                                </b-button>
                                <b-button
                                    variant="success"
                                    size="sm"
                                    :disabled="isSavingMembers"
                                    @click="saveMembers"
                                >
                                  <b-spinner v-if="isSavingMembers" small class="mr-50" />
                                  <feather-icon v-else icon="SaveIcon" class="mr-50" />
                                  Сохранить
                                </b-button>
                              </div>

                              <b-row>
                                <!-- Текущие участники -->
                                <b-col md="6">
                                  <h6 class="mt-1">Текущие участники</h6>
                                  <div
                                      v-for="(member, idx) in currentMembers"
                                      :key="'cur-' + idx"
                                      class="d-flex align-items-center mb-50"
                                      style="gap: 0.25rem;"
                                  >
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === 0"
                                        @click="moveMemberUp(currentMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronUpIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === currentMembers.length - 1"
                                        @click="moveMemberDown(currentMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronDownIcon" size="14" />
                                    </b-button>
                                    <b-form-input
                                        v-model="currentMembers[idx]"
                                        size="sm"
                                        class="flex-grow-1"
                                    />
                                    <b-button
                                        variant="flat-warning"
                                        class="btn-icon p-25"
                                        size="sm"
                                        v-b-tooltip.hover
                                        title="В бывшие"
                                        @click="moveToFormer(idx)"
                                    >
                                      <feather-icon icon="ArrowRightIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-danger"
                                        class="btn-icon p-25"
                                        size="sm"
                                        @click="removeMember(currentMembers, idx)"
                                    >
                                      <feather-icon icon="XIcon" size="14" />
                                    </b-button>
                                  </div>
                                  <div v-if="!currentMembers.length" class="text-muted mb-50">Нет текущих участников</div>
                                  <div class="d-flex align-items-center mt-50" style="gap: 0.25rem;">
                                    <b-form-input
                                        v-model="newCurrentMember"
                                        size="sm"
                                        placeholder="Новый участник"
                                        class="flex-grow-1"
                                        @keyup.enter="addCurrentMember"
                                    />
                                    <b-button
                                        variant="success"
                                        class="btn-icon"
                                        size="sm"
                                        :disabled="!newCurrentMember.trim()"
                                        @click="addCurrentMember"
                                    >
                                      <feather-icon icon="PlusIcon" />
                                    </b-button>
                                  </div>
                                </b-col>

                                <!-- Бывшие участники -->
                                <b-col md="6">
                                  <h6 class="mt-1">Бывшие участники</h6>
                                  <div
                                      v-for="(member, idx) in formerMembers"
                                      :key="'fmr-' + idx"
                                      class="d-flex align-items-center mb-50"
                                      style="gap: 0.25rem;"
                                  >
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === 0"
                                        @click="moveMemberUp(formerMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronUpIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === formerMembers.length - 1"
                                        @click="moveMemberDown(formerMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronDownIcon" size="14" />
                                    </b-button>
                                    <b-form-input
                                        v-model="formerMembers[idx]"
                                        size="sm"
                                        class="flex-grow-1"
                                    />
                                    <b-button
                                        variant="flat-info"
                                        class="btn-icon p-25"
                                        size="sm"
                                        v-b-tooltip.hover
                                        title="В текущие"
                                        @click="moveToCurrent(idx)"
                                    >
                                      <feather-icon icon="ArrowLeftIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-danger"
                                        class="btn-icon p-25"
                                        size="sm"
                                        @click="removeMember(formerMembers, idx)"
                                    >
                                      <feather-icon icon="XIcon" size="14" />
                                    </b-button>
                                  </div>
                                  <div v-if="!formerMembers.length" class="text-muted mb-50">Нет бывших участников</div>
                                  <div class="d-flex align-items-center mt-50" style="gap: 0.25rem;">
                                    <b-form-input
                                        v-model="newFormerMember"
                                        size="sm"
                                        placeholder="Новый бывший участник"
                                        class="flex-grow-1"
                                        @keyup.enter="addFormerMember"
                                    />
                                    <b-button
                                        variant="success"
                                        class="btn-icon"
                                        size="sm"
                                        :disabled="!newFormerMember.trim()"
                                        @click="addFormerMember"
                                    >
                                      <feather-icon icon="PlusIcon" />
                                    </b-button>
                                  </div>
                                </b-col>
                              </b-row>
                            </div>
                          </b-tab>

                          <!-- Tab: Обложки -->
                          <b-tab v-if="$can('view', 'artist-covers')" @click="loadSavedCovers">
                            <template #title>
                              <feather-icon
                                  icon="ImageIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Обложки</span>
                              <b-badge variant="success" class="ml-50">
                                {{ savedCoversLoaded ? savedCovers.length : (artistDetail.covers_count || 0) }}
                              </b-badge>
                              <small v-if="pendingBy('album_cover', 'create').length" class="ml-25 pending-submission-count">
                                +{{ pendingBy('album_cover', 'create').length }}
                              </small>
                            </template>

                            <div class="my-1 py-25">
                              <!-- Сохранённые обложки -->
                              <div v-if="isLoadingSavedCovers" class="mb-1">
                                <b-spinner small class="mr-50" />
                                <span class="text-muted">Загрузка обложек...</span>
                              </div>
                              <div v-if="savedCovers.length || pendingBy('album_cover', 'create').length" class="mb-2">
                                <h5>Сохранённые обложки</h5>
                                <div class="d-flex flex-wrap" style="gap: 1rem;">
                                  <div
                                      v-for="(cover, index) in savedCovers"
                                      :key="cover.id"
                                      style="width: 200px;"
                                      :class="{
                                        'pending-submission': pendingForTarget('album_cover', cover.id),
                                        'pending-submission-delete': (pendingForTarget('album_cover', cover.id) || {}).action === 'delete',
                                      }"
                                  >
                                    <div class="position-relative">
                                      <b-img
                                          :src="cover.url"
                                          fluid
                                          rounded
                                          style="width: 200px; height: 200px; object-fit: cover; cursor: pointer;"
                                          @click="openFullCover(index)"
                                      />
                                      <template v-if="pendingForTarget('album_cover', cover.id)">
                                        <b-badge
                                            :id="'pending-cover-' + cover.id"
                                            variant="warning"
                                            class="pending-submission-badge"
                                        >
                                          {{ pendingTargetLabel(pendingForTarget('album_cover', cover.id)) }}
                                        </b-badge>
                                        <b-popover
                                            :target="'pending-cover-' + cover.id"
                                            triggers="hover"
                                            placement="bottom"
                                            custom-class="pending-submission-popover"
                                        >
                                          <submission-preview :submission="pendingForTarget('album_cover', cover.id)" />
                                        </b-popover>
                                      </template>
                                      <b-button
                                          variant="danger"
                                          class="btn-icon position-absolute"
                                          size="sm"
                                          style="top: 8px; right: 8px; opacity: 0.9;"
                                          :disabled="ownPendingForTarget('album_cover', cover.id)"
                                          @click.stop="deleteCover(cover)"
                                          v-b-tooltip.hover
                                          title="Удалить обложку"
                                      >
                                        <feather-icon icon="TrashIcon" />
                                      </b-button>
                                    </div>
                                    <div class="mt-50">
                                      <strong v-if="cover.name" class="d-block font-small-3 text-truncate">{{ cover.name }}</strong>
                                      <b-form-rating
                                          :value="cover.difficulty || 0"
                                          :stars="5"
                                          inline
                                          no-border
                                          size="sm"
                                          variant="warning"
                                          class="p-0"
                                          :disabled="ownPendingForTarget('album_cover', cover.id)"
                                          @change="updateCoverDifficulty(cover, $event)"
                                      />
                                    </div>
                                  </div>
                                  <!-- Обложки авторов на одобрении (ещё не в БД) -->
                                  <div
                                      v-for="submission in pendingBy('album_cover', 'create')"
                                      :key="'pending-cover-create-' + submission.id"
                                      style="width: 200px;"
                                      class="pending-submission"
                                  >
                                    <div class="position-relative">
                                      <b-img
                                          :src="submission.present.image_url"
                                          fluid
                                          rounded
                                          style="width: 200px; height: 200px; object-fit: cover;"
                                      />
                                      <b-badge variant="warning" class="pending-submission-badge">На одобрении</b-badge>
                                    </div>
                                    <div class="mt-50 px-25">
                                      <strong v-if="submission.present.title" class="d-block font-small-3 text-truncate">{{ submission.present.title }}</strong>
                                      <small v-if="$can('manage', 'all') && submission.author" class="d-block text-muted">
                                        {{ submission.author.name }}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <b-button
                                  variant="success"
                                  class="mb-1"
                                  :disabled="isLoadingCovers || !hasSpotifyId"
                                  @click="searchSpotifyCovers"
                              >
                                <b-spinner v-if="isLoadingCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Спотифай</span>
                              </b-button>

                              <div v-if="coversError" class="text-danger mb-1">{{ coversError }}</div>

                              <div v-if="spotifyCovers.length">
                                <template v-for="(group, gIndex) in groupedSpotifyCovers">
                                  <hr v-if="gIndex > 0" :key="'sep-' + gIndex" class="w-100 my-1" />
                                  <div :key="'grp-' + gIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                    <div
                                        v-for="(album, index) in group"
                                        :key="index"
                                        class="cover-card"
                                        style="width: 200px;"
                                        :class="{ 'cover-saved': isCoverSaved(album) }"
                                    >
                                  <div
                                      class="position-relative cursor-pointer"
                                      @click="openCoverPreview(album)"
                                  >
                                    <b-img
                                        :src="album.preview || album.cover"
                                        fluid
                                        rounded
                                        style="width: 200px; height: 200px; object-fit: cover;"
                                        :style="isCoverSaved(album) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                    />
                                    <b-badge
                                        v-if="isCoverSaved(album)"
                                        variant="success"
                                        class="position-absolute"
                                        style="top: 8px; right: 8px; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Обложка сохранена"
                                    >
                                      <feather-icon icon="CheckIcon" size="14" />
                                    </b-badge>
                                    <b-badge
                                        v-if="album.total_tracks"
                                        variant="dark"
                                        class="position-absolute"
                                        style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Кол-во треков"
                                    >
                                      <feather-icon icon="MusicIcon" size="12" class="mr-25" />
                                      {{ album.total_tracks }}
                                    </b-badge>
                                    <b-button
                                        variant="success"
                                        class="btn-icon position-absolute"
                                        size="sm"
                                        style="bottom: 8px; right: 8px; opacity: 0.9;"
                                        @click.stop="saveCover(album)"
                                        v-b-tooltip.hover
                                        title="Сохранить обложку"
                                    >
                                      <feather-icon icon="DownloadIcon" />
                                    </b-button>
                                  </div>
                                  <div class="mt-50">
                                    <strong class="d-block font-small-3 text-truncate">{{ album.name }}</strong>
                                    <strong
                                        v-if="album.artists"
                                        class="d-block font-small-2 text-truncate text-primary"
                                    >{{ album.artists }}</strong>
                                    <div class="d-flex align-items-center" style="gap: 0.4rem;">
                                      <span v-if="album.release_date" class="text-muted font-small-2">{{ formatReleaseDate(album.release_date, album.release_date_precision) }}</span>
                                      <b-badge v-if="album.album_type" :variant="albumTypeVariant(album.album_type)" pill class="font-small-1">
                                        <feather-icon :icon="albumTypeIcon(album.album_type)" size="10" class="mr-25" />
                                        {{ albumTypeLabel(album.album_type) }}
                                      </b-badge>
                                    </div>
                                  </div>
                                    </div>
                                  </div>
                                </template>
                              </div>

                              <div v-else-if="coversSearched && !coversError" class="text-muted">
                                Обложки не найдены
                              </div>

                              <hr class="w-100 my-1" />

                              <b-button
                                  variant="yandex"
                                  class="mb-1 btn-yandex"
                                  :disabled="isLoadingYandexCovers || !hasYandexId"
                                  @click="searchYandexCovers"
                              >
                                <b-spinner v-if="isLoadingYandexCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Яндекса</span>
                              </b-button>

                              <div v-if="yandexCoversError" class="text-danger mb-1">{{ yandexCoversError }}</div>

                              <div v-if="yandexCovers.length">
                                <template v-for="(group, gIndex) in groupedYandexCovers">
                                  <hr v-if="gIndex > 0" :key="'ya-sep-' + gIndex" class="w-100 my-1" />
                                  <div :key="'ya-grp-' + gIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                    <div
                                        v-for="(album, index) in group"
                                        :key="index"
                                        class="cover-card"
                                        style="width: 200px;"
                                        :class="{ 'cover-saved': isCoverSaved(album) }"
                                    >
                                  <div
                                      class="position-relative cursor-pointer"
                                      @click="openCoverPreview(album)"
                                  >
                                    <b-img
                                        :src="album.preview || album.cover"
                                        fluid
                                        rounded
                                        style="width: 200px; height: 200px; object-fit: cover;"
                                        :style="isCoverSaved(album) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                    />
                                    <b-badge
                                        v-if="isCoverSaved(album)"
                                        variant="success"
                                        class="position-absolute"
                                        style="top: 8px; right: 8px; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Обложка сохранена"
                                    >
                                      <feather-icon icon="CheckIcon" size="14" />
                                    </b-badge>
                                    <b-badge
                                        v-if="album.total_tracks"
                                        variant="dark"
                                        class="position-absolute"
                                        style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Кол-во треков"
                                    >
                                      <feather-icon icon="MusicIcon" size="12" class="mr-25" />
                                      {{ album.total_tracks }}
                                    </b-badge>
                                    <b-button
                                        variant="success"
                                        class="btn-icon position-absolute"
                                        size="sm"
                                        style="bottom: 8px; right: 8px; opacity: 0.9;"
                                        @click.stop="saveCover(album)"
                                        v-b-tooltip.hover
                                        title="Сохранить обложку"
                                    >
                                      <feather-icon icon="DownloadIcon" />
                                    </b-button>
                                  </div>
                                  <div class="mt-50">
                                    <strong class="d-block font-small-3 text-truncate">{{ album.name }}</strong>
                                    <strong
                                        v-if="album.artists"
                                        class="d-block font-small-2 text-truncate text-primary"
                                    >{{ album.artists }}</strong>
                                    <div class="d-flex align-items-center" style="gap: 0.4rem;">
                                      <span v-if="album.release_date" class="text-muted font-small-2">{{ formatReleaseDate(album.release_date, album.release_date_precision) }}</span>
                                      <b-badge v-if="album.album_type" :variant="albumTypeVariant(album.album_type)" pill class="font-small-1">
                                        <feather-icon :icon="albumTypeIcon(album.album_type)" size="10" class="mr-25" />
                                        {{ albumTypeLabel(album.album_type) }}
                                      </b-badge>
                                    </div>
                                  </div>
                                    </div>
                                  </div>
                                </template>
                              </div>
                              <div v-else-if="yandexCoversSearched && !yandexCoversError" class="text-muted">
                                Обложки не найдены
                              </div>
                            </div>
                          </b-tab>

                          <!-- Tab: Фото -->
                          <b-tab v-if="$can('view', 'artist-photos')" @click="loadSavedPhotos">
                            <template #title>
                              <feather-icon icon="CameraIcon" size="16" class="mr-0 mr-sm-50" />
                              <span class="d-none d-sm-inline">Фото</span>
                              <b-badge variant="success" class="ml-50">
                                {{ savedPhotosLoaded ? savedPhotos.length : (artistDetail.photos_count || 0) }}
                              </b-badge>
                              <small v-if="pendingBy('photo', 'create').length" class="ml-25 pending-submission-count">
                                +{{ pendingBy('photo', 'create').length }}
                              </small>
                            </template>

                            <div class="my-1 py-25">
                              <div v-if="isLoadingSavedPhotos" class="mb-1">
                                <b-spinner small class="mr-50" />
                                <span class="text-muted">Загрузка фото...</span>
                              </div>

                              <div v-if="savedPhotos.length || pendingBy('photo', 'create').length" class="mb-2">
                                <h5>Сохранённые фото</h5>
                                <div class="d-flex flex-wrap" style="gap: 1rem;">
                                  <div
                                      v-for="photo in savedPhotos"
                                      :key="photo.id"
                                      style="width: 200px;"
                                      :class="{
                                        'pending-submission': pendingForTarget('photo', photo.id),
                                        'pending-submission-delete': (pendingForTarget('photo', photo.id) || {}).action === 'delete',
                                      }"
                                  >
                                    <div class="position-relative">
                                      <b-img
                                          :src="photo.url"
                                          fluid rounded
                                          style="width: 200px; height: 200px; object-fit: cover; cursor: pointer;"
                                          @click="openPhotoModal(photo, false)"
                                      />
                                      <template v-if="pendingForTarget('photo', photo.id)">
                                        <b-badge
                                            :id="'pending-photo-' + photo.id"
                                            variant="warning"
                                            class="pending-submission-badge"
                                        >
                                          {{ pendingTargetLabel(pendingForTarget('photo', photo.id)) }}
                                        </b-badge>
                                        <b-popover
                                            :target="'pending-photo-' + photo.id"
                                            triggers="hover"
                                            placement="bottom"
                                            custom-class="pending-submission-popover"
                                        >
                                          <submission-preview :submission="pendingForTarget('photo', photo.id)" />
                                        </b-popover>
                                      </template>
                                      <b-button
                                          variant="danger"
                                          class="btn-icon position-absolute"
                                          size="sm"
                                          style="top: 8px; right: 8px; opacity: 0.9;"
                                          :disabled="ownPendingForTarget('photo', photo.id)"
                                          @click.stop="deletePhoto(photo)"
                                          v-b-tooltip.hover
                                          title="Удалить фото"
                                      >
                                        <feather-icon icon="TrashIcon" />
                                      </b-button>
                                    </div>
                                    <div class="mt-50">
                                      <b-badge v-if="photo.type" variant="light-primary" class="mb-25">{{ photoTypeLabel(photo.type) }}</b-badge>
                                      <strong v-if="photo.name" class="d-block font-small-3 text-truncate">{{ photo.name }}</strong>
                                      <span v-if="photo.width && photo.height" :class="isHighResPhoto(photo) ? 'text-success font-weight-bold font-small-2' : 'text-muted font-small-2'">{{ photo.width }}×{{ photo.height }}</span>
                                    </div>
                                  </div>
                                  <!-- Фото авторов на одобрении (ещё не в БД) -->
                                  <div
                                      v-for="submission in pendingBy('photo', 'create')"
                                      :key="'pending-photo-create-' + submission.id"
                                      style="width: 200px;"
                                      class="pending-submission"
                                  >
                                    <div class="position-relative">
                                      <b-img
                                          :src="submission.present.image_url"
                                          fluid rounded
                                          style="width: 200px; height: 200px; object-fit: cover;"
                                      />
                                      <b-badge variant="warning" class="pending-submission-badge">На одобрении</b-badge>
                                    </div>
                                    <div class="mt-50 px-25">
                                      <strong v-if="submission.present.title" class="d-block font-small-3 text-truncate">{{ submission.present.title }}</strong>
                                      <small v-if="$can('manage', 'all') && submission.author" class="d-block text-muted">
                                        {{ submission.author.name }}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div class="d-flex flex-wrap align-items-center mb-1" style="gap: 0.5rem;">
                                <b-form-file
                                    v-model="uploadFile"
                                    accept="image/*"
                                    placeholder="Выберите файл..."
                                    browse-text="Обзор"
                                    :disabled="isUploadingPhoto"
                                    style="max-width: 350px;"
                                />
                                <b-form-select
                                    v-model="uploadType"
                                    :options="photoTypeOptions"
                                    :disabled="isUploadingPhoto"
                                    style="max-width: 180px;"
                                />
                                <b-button
                                    variant="success"
                                    :disabled="!uploadFile || isUploadingPhoto"
                                    @click="uploadOwnPhoto"
                                >
                                  <b-spinner v-if="isUploadingPhoto" small class="mr-50" />
                                  <feather-icon v-else icon="UploadIcon" class="mr-50" />
                                  <span class="align-middle">Загрузить</span>
                                </b-button>
                              </div>

                              <!-- Google: тип поиска подставляет запрос целиком в поле, его можно поправить перед поиском -->
                              <b-input-group class="mb-1" style="max-width: 700px;">
                                <b-input-group-prepend>
                                  <b-dropdown
                                      variant="outline-primary"
                                      :text="`Google: ${googleSearchTypeLabel}`"
                                      :disabled="isLoadingPhotos"
                                  >
                                    <b-dropdown-item
                                        v-for="searchType in googleSearchTypes"
                                        :key="searchType.label"
                                        :active="selectedGoogleSearchType === searchType.type"
                                        @click="selectGoogleSearchType(searchType)"
                                    >
                                      {{ searchType.label }}
                                    </b-dropdown-item>
                                  </b-dropdown>
                                </b-input-group-prepend>
                                <b-form-input
                                    v-model="customPhotoQuery"
                                    placeholder="Запрос в Google целиком"
                                    :disabled="isLoadingPhotos"
                                    @keyup.enter="runGoogleSearch"
                                />
                                <b-input-group-append>
                                  <b-button
                                      variant="primary"
                                      :disabled="isLoadingPhotos || !customPhotoQuery.trim()"
                                      @click="runGoogleSearch"
                                  >
                                    <b-spinner v-if="isLoadingPhotos" small class="mr-50" />
                                    <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                    <span class="align-middle">Поиск</span>
                                  </b-button>
                                </b-input-group-append>
                              </b-input-group>

                              <!-- Модерируемые источники: официальные фото стримингов, Википедия, fanart.tv -->
                              <div class="d-flex flex-wrap mb-1" style="gap: 0.5rem;">
                                <span
                                    v-for="source in photoSources"
                                    :key="source.key"
                                    v-b-tooltip.hover
                                    :title="isPhotoSourceAvailable(source) ? (source.note || '') : `Привяжите артиста к ${source.bindingLabel}`"
                                    class="d-inline-block"
                                >
                                  <b-button
                                      :variant="source.variant"
                                      :disabled="sourcePhotos[source.key].loading || !isPhotoSourceAvailable(source)"
                                      @click="loadSourcePhotos(source.key)"
                                  >
                                    <b-spinner v-if="sourcePhotos[source.key].loading" small class="mr-50" />
                                    <feather-icon v-else icon="ImageIcon" class="mr-50" />
                                    <span class="align-middle">{{ source.label }}</span>
                                  </b-button>
                                </span>
                              </div>

                              <div v-if="photosError" class="text-danger mb-1">{{ photosError }}</div>

                              <div v-if="photoResults.length" class="d-flex flex-wrap" style="gap: 1rem;">
                                <div v-for="(photo, index) in photoResults" :key="photo.image_url" style="width: 200px;">
                                  <div class="position-relative cursor-pointer" @click="openPhotoModal(photo, true)">
                                    <b-img
                                        :src="photo.thumbnail || photo.image_url"
                                        fluid rounded
                                        style="width: 200px; height: 200px; object-fit: cover;"
                                        :style="isPhotoSaved(photo) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                    />
                                    <b-badge
                                        v-if="isPhotoSaved(photo)"
                                        variant="success"
                                        class="position-absolute"
                                        style="top: 8px; right: 8px; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Уже сохранено"
                                    >
                                      <feather-icon icon="CheckIcon" size="14" />
                                    </b-badge>
                                    <b-badge
                                        v-if="photo.width && photo.height"
                                        :variant="isHighResPhoto(photo) ? 'success' : 'dark'"
                                        class="position-absolute"
                                        style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.75rem;"
                                    >
                                      {{ photo.width }}×{{ photo.height }}
                                    </b-badge>
                                  </div>
                                  <div class="mt-50">
                                    <strong class="d-block font-small-3 text-truncate">{{ photo.title }}</strong>
                                    <a
                                        v-if="photo.source_url"
                                        :href="photo.source_url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="font-small-2"
                                    >
                                      <feather-icon icon="ExternalLinkIcon" size="11" class="mr-25" />Источник
                                    </a>
                                  </div>
                                </div>
                              </div>

                              <div v-else-if="photosSearched && !photosError" class="text-muted">
                                Фото не найдены
                              </div>

                              <div v-if="photoNextStart" class="mt-1">
                                <b-button variant="outline-primary" :disabled="isLoadingPhotos" @click="loadMorePhotos">
                                  <b-spinner v-if="isLoadingPhotos" small class="mr-50" />
                                  <span class="align-middle">Показать ещё</span>
                                </b-button>
                              </div>

                              <template v-for="source in photoSources">
                                <template v-if="sourcePhotos[source.key].searched">
                                  <hr :key="`${source.key}-hr`" class="my-1" />
                                  <h6 :key="`${source.key}-title`" class="mb-1">{{ source.label }}</h6>

                                  <div v-if="sourcePhotos[source.key].items.length" :key="`${source.key}-items`" class="d-flex flex-wrap" style="gap: 1rem;">
                                    <div v-for="photo in sourcePhotos[source.key].items" :key="photo.image_url" style="width: 200px;">
                                      <div class="position-relative cursor-pointer" @click="openPhotoModal(photo, true)">
                                        <b-img
                                            :src="photo.thumbnail || photo.image_url"
                                            fluid rounded
                                            style="width: 200px; height: 200px; object-fit: cover;"
                                            :style="isPhotoSaved(photo) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                        />
                                        <b-badge
                                            v-if="isPhotoSaved(photo)"
                                            variant="success"
                                            class="position-absolute"
                                            style="top: 8px; right: 8px; font-size: 0.85rem;"
                                            v-b-tooltip.hover
                                            title="Уже сохранено"
                                        >
                                          <feather-icon icon="CheckIcon" size="14" />
                                        </b-badge>
                                        <b-badge
                                            v-if="photo.width && photo.height"
                                            :variant="isHighResPhoto(photo) ? 'success' : 'dark'"
                                            class="position-absolute"
                                            style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.75rem;"
                                        >
                                          {{ photo.width }}×{{ photo.height }}
                                        </b-badge>
                                      </div>
                                      <div class="mt-50">
                                        <strong class="d-block font-small-3 text-truncate">{{ photo.title }}</strong>
                                        <a
                                            v-if="photo.source_url"
                                            :href="photo.source_url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="font-small-2"
                                        >
                                          <feather-icon icon="ExternalLinkIcon" size="11" class="mr-25" />Источник
                                        </a>
                                      </div>
                                    </div>
                                  </div>

                                  <div v-else :key="`${source.key}-empty`" class="text-muted">
                                    Фото не найдены
                                  </div>
                                </template>
                              </template>
                            </div>
                          </b-tab>

                          <!-- Tab: Альбомы -->
                          <b-tab v-if="$can('view', 'artist-tracks')">
                            <template #title>
                              <feather-icon
                                  icon="DiscIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Альбомы</span>
                            </template>

                            <div ref="albumsContainer" class="my-1 py-25">
                              <!-- Spotify -->
                              <b-button
                                  variant="success"
                                  class="mb-1"
                                  :disabled="isLoadingCovers || !hasSpotifyId"
                                  @click="searchSpotifyCovers"
                              >
                                <b-spinner v-if="isLoadingCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Спотифай</span>
                              </b-button>

                              <div v-if="coversError" class="text-danger mb-1">{{ coversError }}</div>

                              <div v-if="spotifyCovers.length">
                                <template v-for="(group, gIndex) in groupedSpotifyCovers">
                                  <hr v-if="gIndex > 0" :key="'al-sp-sep-' + gIndex" class="w-100 my-1" />
                                  <template v-for="(row, rIndex) in albumRows(group)">
                                    <div :key="'al-sp-row-' + gIndex + '-' + rIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                      <album-card
                                          v-for="album in row"
                                          :key="'sp-' + album.id"
                                          :album="album"
                                          source="spotify"
                                          :active="!!activeAlbum && activeAlbum.source === 'spotify' && activeAlbum.id === album.id"
                                          @toggle="toggleAlbumTracks"
                                          @add-cover="openCoverPreview"
                                      />
                                    </div>
                                    <album-tracks-panel
                                        v-if="rowHasActiveAlbum(row, 'spotify')"
                                        :key="'al-sp-panel-' + gIndex + '-' + rIndex"
                                        :album-name="activeAlbum.name"
                                        :tracks="albumTracks[activeAlbum.id] || null"
                                        :loading="!!albumTracksLoading[activeAlbum.id]"
                                        @close="closeAlbumTracks"
                                        @add-track="addAlbumTrack"
                                    />
                                  </template>
                                </template>
                              </div>
                              <div v-else-if="coversSearched && !coversError" class="text-muted">
                                Альбомы не найдены
                              </div>

                              <hr class="w-100 my-1" />

                              <!-- Yandex -->
                              <b-button
                                  variant="yandex"
                                  class="mb-1 btn-yandex"
                                  :disabled="isLoadingYandexCovers || !hasYandexId"
                                  @click="searchYandexCovers"
                              >
                                <b-spinner v-if="isLoadingYandexCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Яндекса</span>
                              </b-button>

                              <div v-if="yandexCoversError" class="text-danger mb-1">{{ yandexCoversError }}</div>

                              <div v-if="yandexCovers.length">
                                <template v-for="(group, gIndex) in groupedYandexCovers">
                                  <hr v-if="gIndex > 0" :key="'al-ya-sep-' + gIndex" class="w-100 my-1" />
                                  <template v-for="(row, rIndex) in albumRows(group)">
                                    <div :key="'al-ya-row-' + gIndex + '-' + rIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                      <album-card
                                          v-for="album in row"
                                          :key="'ya-' + album.id"
                                          :album="album"
                                          source="yandex"
                                          :active="!!activeAlbum && activeAlbum.source === 'yandex' && activeAlbum.id === album.id"
                                          @toggle="toggleAlbumTracks"
                                          @add-cover="openCoverPreview"
                                      />
                                    </div>
                                    <album-tracks-panel
                                        v-if="rowHasActiveAlbum(row, 'yandex')"
                                        :key="'al-ya-panel-' + gIndex + '-' + rIndex"
                                        :album-name="activeAlbum.name"
                                        :tracks="albumTracks[activeAlbum.id] || null"
                                        :loading="!!albumTracksLoading[activeAlbum.id]"
                                        @close="closeAlbumTracks"
                                        @add-track="addAlbumTrack"
                                    />
                                  </template>
                                </template>
                              </div>
                              <div v-else-if="yandexCoversSearched && !yandexCoversError" class="text-muted">
                                Альбомы не найдены
                              </div>
                            </div>
                          </b-tab>

                          <!-- Tab: Похожие артисты -->
                          <b-tab v-if="$can('view', 'db-music')" @click="openSimilarTab">
                            <template #title>
                              <feather-icon
                                  icon="CompassIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Похожие</span>
                            </template>

                            <div class="my-1 py-25">
                              <!-- Ручной список похожих (drag-and-drop порядок) -->
                              <div class="mb-2">
                                <h5>Добавленные похожие</h5>
                                <!-- Заявки авторов на добавление похожих (ещё не в БД) -->
                                <div
                                    v-for="submission in pendingBy('similar', 'create')"
                                    :key="'pending-similar-' + submission.id"
                                    class="d-flex align-items-center border rounded p-75 mb-50 pending-submission"
                                    style="gap: 0.75rem;"
                                >
                                  <span class="font-weight-bold text-truncate">{{ submission.present.title }}</span>
                                  <b-badge variant="warning">На одобрении</b-badge>
                                  <small v-if="$can('manage', 'all') && submission.author" class="text-muted">
                                    {{ submission.author.name }}
                                  </small>
                                </div>
                                <draggable
                                    v-if="manualSimilar.length"
                                    :list="manualSimilar"
                                    :disabled="!$can('manage', 'all')"
                                    handle=".manual-similar-handle"
                                    tag="div"
                                    @end="saveManualOrder"
                                >
                                  <div
                                      v-for="row in manualSimilar"
                                      :key="row.id"
                                      class="d-flex align-items-center border rounded p-75 mb-50"
                                      :class="{
                                        'pending-submission': pendingForTarget('similar', row.id),
                                        'pending-submission-delete': (pendingForTarget('similar', row.id) || {}).action === 'delete',
                                      }"
                                      style="gap: 0.75rem;"
                                  >
                                    <feather-icon
                                        v-if="$can('manage', 'all')"
                                        icon="MenuIcon"
                                        class="manual-similar-handle cursor-move flex-shrink-0 text-muted"
                                    />
                                    <template v-if="row.artist">
                                      <b-link
                                          :to="{name: 'artist-view', params: {id: row.artist.id}}"
                                          class="font-weight-bold text-truncate"
                                      >
                                        {{ row.artist.title }}
                                      </b-link>
                                      <span class="text-muted text-nowrap">{{ row.artist.gender || '—' }}</span>
                                      <span class="text-muted text-nowrap">{{ row.artist.country || '—' }}</span>
                                    </template>
                                    <template v-else>
                                      <span class="font-weight-bold text-truncate">{{ row.name }}</span>
                                      <b-badge variant="light-secondary">нет в БД</b-badge>
                                    </template>
                                    <b-badge v-if="pendingForTarget('similar', row.id)" variant="warning">
                                      {{ pendingTargetLabel(pendingForTarget('similar', row.id)) }}
                                    </b-badge>
                                    <b-button
                                        variant="flat-danger"
                                        class="btn-icon ml-auto flex-shrink-0"
                                        size="sm"
                                        :disabled="ownPendingForTarget('similar', row.id)"
                                        @click="removeManualSimilar(row)"
                                        v-b-tooltip.hover
                                        title="Убрать из похожих"
                                    >
                                      <feather-icon icon="Trash2Icon"/>
                                    </b-button>
                                  </div>
                                </draggable>
                                <div v-else-if="!pendingBy('similar', 'create').length" class="text-muted">Список пуст</div>
                              </div>

                              <!-- Добавление: поиск по БД -->
                              <div class="mb-2">
                                <b-input-group>
                                  <b-form-input
                                      v-model="manualSearchQuery"
                                      placeholder="Поиск артиста в БД (название или алиас)..."
                                      @keyup.enter="searchManualCandidates"
                                  />
                                  <b-input-group-append>
                                    <b-button
                                        variant="primary"
                                        @click="searchManualCandidates"
                                        :disabled="manualSearchLoading"
                                    >
                                      <feather-icon icon="SearchIcon"/>
                                    </b-button>
                                  </b-input-group-append>
                                </b-input-group>
                                <div v-if="manualSearchResults.length" class="mt-50">
                                  <div
                                      v-for="found in manualSearchResults"
                                      :key="found.id"
                                      class="d-flex align-items-center border rounded p-50 mb-25"
                                      style="gap: 0.75rem;"
                                  >
                                    <b-link
                                        :to="{name: 'artist-view', params: {id: found.id}}"
                                        class="text-truncate"
                                    >
                                      {{ found.title }}
                                    </b-link>
                                    <span class="text-muted text-nowrap">{{ found.gender || '—' }}</span>
                                    <span class="text-muted text-nowrap">{{ found.country || '—' }}</span>
                                    <b-button
                                        v-if="!isManualAdded(found.id, found.title)"
                                        variant="flat-success"
                                        class="btn-icon ml-auto flex-shrink-0"
                                        size="sm"
                                        @click="addManualFromSearch(found)"
                                        :disabled="manualAddingKey !== null"
                                        v-b-tooltip.hover
                                        title="В похожие"
                                    >
                                      <b-spinner v-if="manualAddingKey === found.title" small/>
                                      <feather-icon v-else icon="PlusIcon"/>
                                    </b-button>
                                    <feather-icon v-else icon="CheckIcon" class="ml-auto text-success flex-shrink-0"/>
                                  </div>
                                </div>
                              </div>

                              <!-- Кандидаты из внешних источников -->
                              <div class="d-flex align-items-center mb-1" style="gap: 0.5rem;">
                                <h5 class="mb-0">Кандидаты</h5>
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    @click="loadTastedive"
                                    :disabled="tastediveLoading"
                                >
                                  {{ tastediveLoading ? 'Загрузка...' : 'Загрузить из TasteDive' }}
                                </b-button>
                              </div>
                              <b-row v-if="similar && similar.length">
                                <b-col
                                    v-for="(artist, index) in similar"
                                    :key="artist.mbid || index"
                                    cols="12"
                                    sm="6"
                                    md="4"
                                    lg="3"
                                    class="mb-1"
                                >
                                  <div
                                      class="d-flex align-items-center border rounded p-1"
                                      :class="similarSourceClass(artist)"
                                      style="gap: 0.75rem;"
                                  >
                                    <b-img
                                        v-if="similarArtistImage(artist)"
                                        :src="similarArtistImage(artist)"
                                        rounded
                                        width="48"
                                        height="48"
                                        style="object-fit: cover; flex-shrink: 0;"
                                    />
                                    <div class="d-flex align-items-center flex-grow-1" style="min-width: 0; gap: 0.25rem;">
                                      <div class="text-truncate font-weight-bold">
                                        <b-link
                                            v-if="artist.db"
                                            :to="{name: 'artist-view', params: { id: artist.db.id }}"
                                        >
                                          {{ artist.name }}
                                        </b-link>
                                        <template v-else>{{ artist.name }}</template>
                                      </div>
                                      <b-button
                                          v-if="!artist.db"
                                          :to="{name: 'artists-search', query: { q: artist.name }}"
                                          variant="flat-primary"
                                          class="btn-icon flex-shrink-0"
                                          size="sm"
                                          v-b-tooltip.hover
                                          title="Искать"
                                      >
                                        <feather-icon icon="SearchIcon"/>
                                      </b-button>
                                    </div>
                                    <div class="text-nowrap">
                                      <b-button
                                          v-if="!isManualAdded(artist.db && artist.db.id, artist.name)"
                                          variant="flat-info"
                                          class="btn-icon"
                                          size="sm"
                                          @click="addManualFromCandidate(artist)"
                                          :disabled="manualAddingKey !== null"
                                          v-b-tooltip.hover
                                          title="В похожие"
                                      >
                                        <b-spinner v-if="manualAddingKey === artist.name" small/>
                                        <feather-icon v-else icon="PlusCircleIcon"/>
                                      </b-button>
                                      <b-button
                                          v-else
                                          variant="flat-success"
                                          class="btn-icon"
                                          size="sm"
                                          disabled
                                      >
                                        <feather-icon icon="CheckCircleIcon"/>
                                      </b-button>
                                      <b-button
                                          v-if="artist.url && (artist.source === 'lastfm' || artist.url.includes('last.fm'))"
                                          :href="artist.url"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          variant="flat-danger"
                                          class="btn-icon ml-25"
                                          size="sm"
                                          v-b-tooltip.hover
                                          title="Открыть на Last.fm"
                                      >
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                                          <path d="M10.584 17.21l-.88-2.392s-1.43 1.594-3.573 1.594c-1.897 0-3.244-1.649-3.244-4.288 0-3.382 1.704-4.591 3.381-4.591 2.42 0 3.189 1.567 3.849 3.574l.88 2.749c.879 2.666 2.529 4.81 7.285 4.81 3.409 0 5.718-1.044 5.718-3.793 0-2.227-1.265-3.381-3.63-3.932l-1.76-.385c-1.209-.275-1.567-.77-1.567-1.594 0-.934.742-1.484 1.952-1.484 1.32 0 2.034.495 2.144 1.677l2.749-.33c-.22-2.474-1.924-3.492-4.729-3.492-2.474 0-4.893.935-4.893 3.932 0 1.87.907 3.052 3.189 3.602l1.87.44c1.402.33 1.869.907 1.869 1.704 0 1.017-.99 1.43-2.86 1.43-2.776 0-3.932-1.457-4.591-3.464l-.907-2.749c-1.155-3.575-3.001-4.893-6.656-4.893C2.31 5.653 0 8.019 0 12.089c0 3.909 2.006 6.025 5.608 6.025 2.914 0 4.976-1.594 4.976-1.594z"/>
                                        </svg>
                                      </b-button>
                                      <b-button
                                          v-else-if="artist.url && (artist.source === 'yandex' || artist.url.includes('music.yandex'))"
                                          :href="artist.url"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          variant="flat-warning"
                                          class="btn-icon ml-25"
                                          size="sm"
                                          style="font-weight: 700; font-size: 0.7rem;"
                                          v-b-tooltip.hover
                                          title="Открыть в Яндекс.Музыке"
                                      >
                                        Я
                                      </b-button>
                                      <b-button
                                          v-else-if="artist.url"
                                          :href="artist.url"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          variant="flat-secondary"
                                          class="btn-icon ml-25"
                                          size="sm"
                                          v-b-tooltip.hover
                                          title="Открыть ссылку"
                                      >
                                        <feather-icon icon="ExternalLinkIcon"/>
                                      </b-button>
                                    </div>
                                  </div>
                                </b-col>
                              </b-row>
                              <div v-else class="text-muted">Нет похожих артистов</div>
                            </div>
                          </b-tab>

                          <!-- Tab: Чарты UK/US -->
                          <b-tab v-if="$can('view', 'db-music')" @click="$refs.artistChartsTab && $refs.artistChartsTab.load()">
                            <template #title>
                              <feather-icon
                                  icon="AwardIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Чарты</span>
                            </template>
                            <artist-charts-tab
                                ref="artistChartsTab"
                                :artist-id="$route.params.id"
                            />
                          </b-tab>
                        </b-tabs>
                      </b-col>
                    </b-row>
                  </b-overlay>
                </b-card>
              </b-col>
            </b-row>

            <div
                class="blog-content"
                v-html="artistDetail.description"
            />

          </b-card>
        </b-col>
        <!--/ blogs -->
      </b-row>
      <!--/ blogs -->
    </div>
    <!--/ content -->

    <!-- Правка атрибутов артиста (язык, тип, страна, даты, годы) -->
    <b-modal
        v-model="showAttributesModal"
        title="Атрибуты артиста"
        ok-title="Сохранить"
        cancel-title="Отмена"
        centered
        :ok-disabled="isSavingAttributes"
        @ok.prevent="saveAttributes"
    >
      <b-overlay :show="isSavingAttributes" rounded="sm">
        <b-row>
          <b-col cols="6">
            <b-form-group label="Язык">
              <b-form-select v-model="attributesForm.russian" :options="languageOptions"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Тип">
              <b-form-select v-model="attributesForm.artist_type" :options="artistTypeOptions"/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Страна">
          <v-select
              v-model="attributesForm.country"
              :options="countryOptions"
              :loading="isLoadingCountries"
              placeholder="Не указана"
          />
        </b-form-group>
        <b-row v-if="attributesForm.artist_type !== 'Группа'">
          <b-col cols="6">
            <b-form-group label="Дата рождения">
              <b-form-input v-model="attributesForm.birth_date" type="date"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Дата смерти">
              <b-form-input v-model="attributesForm.death_date" type="date"/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row v-if="attributesForm.artist_type !== 'Человек'">
          <b-col cols="6">
            <b-form-group label="Год основания">
              <b-form-input v-model="attributesForm.foundation_year" type="number" min="1000" placeholder="—"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Год распада">
              <b-form-input v-model="attributesForm.dissolution_year" type="number" min="1000" placeholder="—"/>
            </b-form-group>
          </b-col>
        </b-row>
        <small v-if="artistDetail && artistDetail.notion_id" class="text-muted">
          Всё, кроме типа, сначала записывается в Notion — источник истины для этих полей.
        </small>
      </b-overlay>
    </b-modal>

    <!-- SHS Candidates Modal -->
    <b-modal
        v-model="showShsCandidates"
        title="Выберите артиста в SHS"
        hide-footer
        centered
    >
      <div v-if="shsCandidates.length">
        <div
            v-for="candidate in shsCandidates"
            :key="candidate.shs_id"
            class="d-flex align-items-center mb-1"
        >
          <b-button
              variant="primary"
              size="sm"
              class="mr-1"
              @click="selectShsCandidate(candidate.shs_id)"
          >
            <feather-icon icon="CheckIcon"/>
          </b-button>
          <span>{{ candidate.name }}</span>
          <a :href="candidate.uri" target="_blank" class="ml-auto">
            <feather-icon icon="ExternalLinkIcon"/>
          </a>
        </div>
      </div>
      <div v-else class="text-muted">Артист не найден в SecondHandSongs</div>
    </b-modal>

    <!-- MusicBrainz Candidates Modal -->
    <b-modal
        v-model="showMusicBrainzCandidates"
        title="Выберите артиста в MusicBrainz"
        hide-footer
        centered
    >
      <div v-if="musicBrainzCandidates.length">
        <div
            v-for="candidate in musicBrainzCandidates"
            :key="candidate.mbid"
            class="d-flex align-items-center mb-1"
        >
          <b-button
              variant="primary"
              size="sm"
              class="mr-1"
              @click="selectMusicBrainzCandidate(candidate.mbid)"
          >
            <feather-icon icon="CheckIcon"/>
          </b-button>
          <div>
            <div>{{ candidate.name }}</div>
            <small class="text-muted">
              {{ [candidate.disambiguation, candidate.country, candidate.type, musicBrainzYears(candidate)].filter(Boolean).join(' · ') }}
            </small>
            <small v-if="Object.keys(candidate.external_ids || {}).length" class="d-block text-muted">
              Есть: {{ Object.keys(candidate.external_ids).join(', ') }}
            </small>
          </div>
          <a :href="'https://musicbrainz.org/artist/' + candidate.mbid" target="_blank" class="ml-auto">
            <feather-icon icon="ExternalLinkIcon"/>
          </a>
        </div>
      </div>
      <div v-else class="text-muted">Артист не найден в MusicBrainz</div>
    </b-modal>

    <!-- MusicBrainz Attributes Modal -->
    <b-modal
        v-model="showMusicBrainzAttributes"
        title="Данные из MusicBrainz"
        centered
        size="lg"
        ok-title="Применить выбранное"
        cancel-title="Отмена"
        :ok-disabled="isApplyingMusicBrainzAttributes || !musicBrainzHasSelection"
        :busy="isApplyingMusicBrainzAttributes"
        :hide-footer="musicBrainzAttributes && !musicBrainzAttributes.bound"
        @ok.prevent="applyMusicBrainzAttributes"
    >
      <div v-if="musicBrainzAttributes && !musicBrainzAttributes.bound" class="text-center py-1">
        <p class="mb-1">{{ musicBrainzAttributes.message || 'Артист не привязан к MusicBrainz.' }}</p>
        <b-button
            v-if="!musicBrainzAttributes.message"
            variant="outline-musicbrainz"
            class="btn-outline-musicbrainz"
            @click="findMusicBrainzFromAttributes"
        >
          <feather-icon icon="SearchIcon" size="14" class="mr-25"/>
          Найти в MusicBrainz
        </b-button>
      </div>

      <div v-else-if="musicBrainzAttributes">
        <p class="font-small-3 text-muted mb-1">
          {{ musicBrainzAttributes.mb_name }}
          <span v-if="musicBrainzAttributes.mb_type">· {{ musicBrainzAttributes.mb_type }}</span>
        </p>

        <div class="mb-1">
          <div class="d-flex font-small-2 text-muted mb-25" style="gap: 0.5rem;">
            <span style="width: 28px;"></span>
            <span style="flex: 1 1 30%;">Поле</span>
            <span style="flex: 1 1 35%;">Сейчас</span>
            <span style="flex: 1 1 35%;">MusicBrainz</span>
          </div>
          <div
              v-for="field in musicBrainzAttributes.fields"
              :key="field.key"
              class="d-flex align-items-start py-25"
              style="gap: 0.5rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
          >
            <span style="width: 28px;">
              <b-form-checkbox
                  v-model="musicBrainzSelectedFields"
                  :value="field.key"
                  :disabled="!field.applicable"
              />
            </span>
            <span style="flex: 1 1 30%;">
              {{ field.label }}
              <span v-if="field.note" class="d-block font-small-2 text-muted">{{ field.note }}</span>
              <span v-if="!field.applicable && field.reason" class="d-block font-small-2 text-muted">{{ field.reason }}</span>
            </span>
            <span class="text-muted" style="flex: 1 1 35%;">{{ field.current || '—' }}</span>
            <span style="flex: 1 1 35%;">{{ field.suggested || '—' }}</span>
          </div>
        </div>

        <div v-if="musicBrainzAttributes.aliases.length">
          <h6 class="mb-50">Алиасы</h6>
          <b-form-checkbox
              v-for="alias in musicBrainzAttributes.aliases"
              :key="alias.alias"
              v-model="musicBrainzSelectedAliases"
              :value="alias.alias"
              class="mb-25"
          >
            {{ alias.alias }}
            <span class="text-muted font-small-2">({{ alias.locale }})</span>
          </b-form-checkbox>
        </div>
      </div>
    </b-modal>

    <!-- Link SHS Track Modal -->
    <b-modal
        v-model="showLinkShsTrackModal"
        title="Привязать трек из SHS"
        centered
        ok-title="Привязать"
        cancel-title="Отмена"
        :ok-disabled="!selectedTrackIdForLink"
        @ok="linkShsTrack"
    >
      <div v-if="linkShsTrackItem">
        <p class="mb-50"><strong>SHS трек:</strong> {{ linkShsTrackItem.shs_title || linkShsTrackItem.title }}</p>
        <label class="mb-50">Выберите трек из БД:</label>
        <v-select
            v-model="selectedTrackIdForLink"
            :options="artistTracksForLink"
            label="label"
            :reduce="val => val.value"
            placeholder="Поиск трека..."
            :filterable="true"
            :append-to-body="true"
        />
      </div>
    </b-modal>

    <!-- SHS Performance Info Modal -->
    <b-modal
        v-model="showShsPerformanceModal"
        :title="shsPerformanceInfo ? `SHS: ${shsPerformanceInfo.performance.title}` : 'Информация из SHS'"
        centered
        size="lg"
        hide-footer
    >
      <div v-if="shsPerformanceLoading" class="text-center my-2">
        <b-spinner label="Загрузка..."/>
      </div>
      <div v-else-if="shsPerformanceError" class="text-danger">{{ shsPerformanceError }}</div>
      <div v-else-if="shsPerformanceInfo">
        <h5>Кавер</h5>
        <p class="mb-1">
          {{ shsPerformanceInfo.performance.performer }} — {{ shsPerformanceInfo.performance.title }}
          <span v-if="shsPerformanceInfo.performance.date" class="text-muted">({{ formatShsDate(shsPerformanceInfo.performance.date) }})</span>
          <b-link :href="shsPerformanceInfo.performance.uri" target="_blank" class="ml-25">
            <feather-icon icon="ExternalLinkIcon"/>
          </b-link>
        </p>

        <template v-if="shsPerformanceInfo.original">
          <h5>Оригинал</h5>
          <p class="mb-1">
            {{ shsPerformanceInfo.original.performer }} — {{ shsPerformanceInfo.original.title }}
            <span v-if="shsPerformanceInfo.original.date" class="text-muted">({{ formatShsDate(shsPerformanceInfo.original.date) }})</span>
            <b-link :href="shsPerformanceInfo.original.uri" target="_blank" class="ml-25">
              <feather-icon icon="ExternalLinkIcon"/>
            </b-link>
            <router-link
                v-if="shsPerformanceInfo.original.track_id"
                :to="{name: 'track-detail', params: {id: shsPerformanceInfo.original.track_id}}"
                class="ml-25"
            >
              <b-badge variant="light-success">в БД</b-badge>
            </router-link>
          </p>
        </template>

        <template v-if="shsPerformanceInfo.works && shsPerformanceInfo.works.length">
          <h5>Произведение</h5>
          <p class="mb-1">
            <span v-for="(work, index) in shsPerformanceInfo.works" :key="work.uri">
              <span v-if="index > 0">, </span>
              <b-link :href="work.uri" target="_blank">{{ work.title }}</b-link>
            </span>
          </p>
        </template>

        <template v-if="shsPerformanceInfo.releases && shsPerformanceInfo.releases.length">
          <h5>Релизы</h5>
          <ul class="mb-1 pl-2">
            <li v-for="release in shsPerformanceInfo.releases" :key="release.uri">
              <template v-if="release.performer">{{ release.performer }} — </template>{{ release.title }}
              <b-link :href="release.uri" target="_blank" class="ml-25">
                <feather-icon icon="ExternalLinkIcon"/>
              </b-link>
            </li>
          </ul>
        </template>

      </div>
    </b-modal>

    <!-- LLM Covers Modal -->
    <b-modal
        v-model="showLlmCoversModal"
        :title="llmCoversDirection === 'received' ? 'Каверы на артиста' : 'Каверы артиста'"
        centered
        size="lg"
        hide-footer
    >
      <div class="d-flex align-items-center mb-1">
        <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="llmCoversLoading"
            @click="fetchLlmCovers(true)"
        >
          <feather-icon icon="RefreshCwIcon" class="mr-50"/>
          <span class="align-middle">Обновить</span>
        </b-button>
        <span v-if="llmCoversCached" class="text-muted font-small-2 ml-1">из кеша</span>
      </div>

      <div v-if="llmCoversLoading" class="text-center my-2">
        <b-spinner label="Загрузка..."/>
      </div>
      <div v-else-if="llmCoversError" class="text-danger">{{ llmCoversError }}</div>
      <template v-else>
        <b-table
            :items="llmCoversItems"
            :fields="llmCoversTableColumns"
            responsive
            show-empty
            empty-text="Каверы не найдены"
            class="position-relative"
        >
          <template #cell(artist)="data">
            <b-link
                v-if="data.item.artist_id"
                :to="{ name: 'artist-view', params: { id: data.item.artist_id } }"
                class="font-weight-bold"
            >
              {{ data.item.artist }}
            </b-link>
            <span v-else>
              {{ data.item.artist }}
              <b-button
                  :to="{ name: 'artists-search', query: { q: data.item.artist } }"
                  variant="info"
                  class="btn-icon ml-25"
                  size="sm"
                  v-b-tooltip.hover
                  title="Искать артиста"
              >
                <feather-icon icon="SearchIcon"/>
              </b-button>
            </span>
          </template>

          <template #cell(title)="data">
            {{ data.item.title }}
            <b-button
                v-if="data.item.source_url"
                :href="data.item.source_url"
                target="_blank"
                variant="flat-primary"
                class="btn-icon"
                size="sm"
                v-b-tooltip.hover
                title="Источник кавера"
            >
              <feather-icon :icon="data.item.source_url.includes('youtu') ? 'YoutubeIcon' : 'ExternalLinkIcon'"/>
            </b-button>
          </template>

          <template #cell(artist_track)="data">
            <router-link
                v-if="data.item.our_track_id"
                :to="{name: 'track-detail', params: { id: data.item.our_track_id }}"
            >
              {{ data.item.artist_track }}
            </router-link>
            <span v-else class="text-muted">{{ data.item.artist_track }}</span>
          </template>

          <template #cell(actions)="data">
            <div class="d-flex align-items-center">
              <template v-if="!data.item.our_track_id">
                <b-button
                    variant="success"
                    class="btn-icon mr-25"
                    size="sm"
                    v-b-tooltip.hover
                    title="Добавить песню артисту"
                    :disabled="llmCoversBusyRow === data.index"
                    @click="addLlmArtistTrack(data.item, data.index)"
                >
                  <b-spinner v-if="llmCoversBusyRow === data.index" small/>
                  <feather-icon v-else icon="PlusIcon"/>
                </b-button>
                <b-button
                    v-if="llmCoversDirection === 'received'"
                    variant="warning"
                    class="btn-icon"
                    size="sm"
                    v-b-tooltip.hover
                    title="Выбрать песню артиста вручную"
                    @click="openLlmPickOurTrackModal(data.item)"
                >
                  <feather-icon icon="SearchIcon"/>
                </b-button>
              </template>
              <!-- Пара уже связана в БД -->
              <feather-icon
                  v-else-if="data.item.linked"
                  icon="CheckCircleIcon"
                  size="18"
                  class="text-success"
              />
              <!-- Каверы на артиста: кавера нет в БД совсем — создать кавер-песню и сразу привязать -->
              <b-button
                  v-else-if="llmCoversDirection === 'received' && !data.item.track_id && !data.item.artist_id"
                  variant="warning"
                  class="btn-icon"
                  size="sm"
                  v-b-tooltip.hover
                  title="Добавить кавер-песню и связать"
                  :disabled="llmCoversBusyRow === data.index"
                  @click="addLlmForeignCoverTrack(data.item, data.index)"
              >
                <b-spinner v-if="llmCoversBusyRow === data.index" small/>
                <feather-icon v-else icon="PlusIcon"/>
              </b-button>
              <template v-else>
                <!-- Чужой трек найден — активная связка; не найден — серая заглушка -->
                <b-button
                    :variant="data.item.track_id ? 'primary' : 'secondary'"
                    class="btn-icon mr-25"
                    size="sm"
                    v-b-tooltip.hover
                    title="Связать кавер с оригиналом"
                    :disabled="!data.item.track_id || llmCoversBusyRow === data.index"
                    @click="linkLlmCoverTrack(data.item, data.index)"
                >
                  <b-spinner v-if="llmCoversBusyRow === data.index" small/>
                  <feather-icon v-else icon="LinkIcon"/>
                </b-button>
                <b-button
                    v-if="!data.item.track_id && data.item.artist_id"
                    variant="warning"
                    class="btn-icon"
                    size="sm"
                    v-b-tooltip.hover
                    title="Выбрать трек для привязки вручную"
                    @click="openLlmPickTrackModal(data.item)"
                >
                  <feather-icon icon="SearchIcon"/>
                </b-button>
              </template>
            </div>
          </template>
        </b-table>

        <div v-if="llmCoversSources.length" class="mt-1">
          <h6 class="text-muted mb-50">Источники</h6>
          <div class="font-small-2 text-muted">
            <div v-for="source in llmCoversSources" :key="source.uri">
              <b-link :href="source.uri" target="_blank">{{ source.title || source.uri }}</b-link>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- LLM Covers: ручной выбор трека чужого артиста для привязки -->
    <b-modal
        v-model="showLlmPickTrackModal"
        title="Выбрать трек для привязки"
        centered
        ok-title="Привязать"
        cancel-title="Отмена"
        :ok-disabled="!llmPickTrackSelectedId"
        @ok="confirmLlmPickTrack"
    >
      <div v-if="llmPickTrackItem">
        <p class="mb-50">
          <strong>{{ llmPickTrackItem.artist }}</strong> — {{ llmPickTrackItem.title }}
        </p>
        <div v-if="llmPickTrackLoading" class="text-center my-1">
          <b-spinner small label="Загрузка..."/>
        </div>
        <template v-else>
          <label class="mb-50">Выберите трек артиста {{ llmPickTrackArtistName }}:</label>
          <v-select
              v-model="llmPickTrackSelectedId"
              :options="llmPickTrackOptions"
              label="label"
              :reduce="val => val.value"
              placeholder="Поиск трека..."
              :filterable="true"
              :append-to-body="true"
          />
        </template>
      </div>
    </b-modal>

    <!-- Cover Preview Modal -->
    <b-modal
        v-model="showCoverPreview"
        :title="previewAlbum ? previewAlbum.name : ''"
        hide-footer
        centered
        size="lg"
    >
      <cover-inpainting
          v-if="previewAlbum"
          :album="previewAlbum"
          @save="saveCoverWithUrl"
      />
    </b-modal>

    <!-- Full-size Saved Cover Modal -->
    <b-modal
        v-model="showFullCover"
        :title="currentFullCover && currentFullCover.name ? currentFullCover.name : 'Обложка'"
        hide-footer
        centered
        size="xl"
        @shown="onFullCoverShown"
        @hidden="onFullCoverHidden"
    >
      <div v-if="currentFullCover" class="d-flex align-items-center" style="gap: 1rem;">
        <b-button
            variant="flat-secondary"
            class="btn-icon"
            :disabled="fullCoverIndex === 0"
            v-b-tooltip.hover
            title="Предыдущая (←)"
            @click="goPrevCover"
        >
          <feather-icon icon="ChevronLeftIcon" size="32" />
        </b-button>

        <div class="flex-grow-1 text-center">
          <b-img
              :src="currentFullCover.url"
              fluid
              style="max-width: 100%; max-height: 80vh; object-fit: contain;"
          />
        </div>

        <b-button
            variant="flat-secondary"
            class="btn-icon"
            :disabled="fullCoverIndex >= savedCovers.length - 1"
            v-b-tooltip.hover
            title="Следующая (→)"
            @click="goNextCover"
        >
          <feather-icon icon="ChevronRightIcon" size="32" />
        </b-button>
      </div>

      <div v-if="currentFullCover" class="d-flex align-items-center justify-content-between mt-1">
        <b-form-rating
            :value="currentFullCover.difficulty || 0"
            :stars="5"
            inline
            no-border
            size="lg"
            variant="warning"
            class="p-0"
            :disabled="ownPendingForTarget('album_cover', currentFullCover.id)"
            @change="updateCoverDifficulty(currentFullCover, $event)"
        />
        <div class="text-muted">
          {{ fullCoverIndex + 1 }} / {{ savedCovers.length }}
        </div>
        <b-button
            variant="danger"
            :disabled="ownPendingForTarget('album_cover', currentFullCover.id)"
            @click="deleteCurrentCover"
        >
          <feather-icon icon="TrashIcon" class="mr-50" />
          Удалить
        </b-button>
      </div>
    </b-modal>

    <!-- Photo Modal -->
    <b-modal
        v-model="showPhotoModal"
        :title="currentPhoto && (currentPhoto.title || currentPhoto.name) ? (currentPhoto.title || currentPhoto.name) : 'Фото'"
        hide-footer
        centered
        size="xl"
    >
      <div v-if="currentPhoto" class="text-center">
        <b-img
            :src="currentPhoto.isResult ? ((currentPhoto.useThumbnail ? currentPhoto.thumbnail : currentPhoto.image_url) || currentPhoto.thumbnail) : currentPhoto.url"
            fluid
            style="max-width: 100%; max-height: 80vh; object-fit: contain;"
            @error="onModalPhotoError"
        />
      </div>
      <div v-if="currentPhoto" class="d-flex align-items-center justify-content-between mt-1" style="gap: 1rem;">
        <span v-if="currentPhoto.width && currentPhoto.height" :class="isHighResPhoto(currentPhoto) ? 'text-success font-weight-bold' : 'text-muted'">
          {{ currentPhoto.width }}×{{ currentPhoto.height }}
        </span>
        <a
            v-if="currentPhoto.isResult && currentPhoto.source_url"
            :href="currentPhoto.source_url"
            target="_blank"
            rel="noopener noreferrer"
        >
          <feather-icon icon="ExternalLinkIcon" class="mr-50" />Источник
        </a>
        <span v-if="savePhotoError" class="text-danger font-small-3 ml-auto mr-1">{{ savePhotoError }}</span>
        <b-button
            v-if="currentPhoto.isResult"
            variant="success"
            :disabled="isSavingPhoto || isPhotoSaved(currentPhoto)"
            @click="savePhoto(currentPhoto)"
        >
          <b-spinner v-if="isSavingPhoto" small class="mr-50" />
          <feather-icon v-else :icon="isPhotoSaved(currentPhoto) ? 'CheckIcon' : 'DownloadIcon'" class="mr-50" />
          {{ isPhotoSaved(currentPhoto) ? 'Уже сохранено' : 'Сохранить' }}
        </b-button>
        <b-button
            v-if="currentPhoto.isResult && savePhotoError && !isPhotoSaved(currentPhoto)"
            variant="outline-primary"
            :disabled="isSavingPhoto"
            @click="savePhotoViaProxy(currentPhoto)"
            v-b-tooltip.hover
            title="Сервер сам скачает картинку с браузерными заголовками (обходит hotlink-защиту)"
        >
          <feather-icon icon="ServerIcon" class="mr-50" />
          Через сервер
        </b-button>
        <b-button
            v-if="currentPhoto.isResult && savePhotoError && !isPhotoSaved(currentPhoto)"
            variant="outline-warning"
            :disabled="isSavingPhoto"
            @click="savePhotoViaBrowser(currentPhoto)"
            v-b-tooltip.hover
            title="Браузер скачает уже отображаемую картинку и загрузит файлом (нужен CORS у источника)"
        >
          <feather-icon icon="GlobeIcon" class="mr-50" />
          Через браузер
        </b-button>
        <b-button
            v-if="!currentPhoto.isResult"
            variant="danger"
            :disabled="ownPendingForTarget('photo', currentPhoto.id)"
            @click="deletePhoto(currentPhoto)"
        >
          <feather-icon icon="TrashIcon" class="mr-50" />
          Удалить
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {
  BFormInput,
  BFormFile,
  BFormSelect,
  BMedia,
  BAvatar,
  BMediaAside,
  BMediaBody,
  BImg,
  BLink,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BCard,
  BRow,
  BCol,
  BBadge,
  BCardText,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BTab,
  BTabs,
  BSpinner,
  BOverlay,
  BTable,
  BFormRating,
  BModal,
  BAlert,
  BPopover,
  VBTooltip,
} from 'bootstrap-vue'
import AlbumCard from './AlbumCard.vue'
import AlbumTracksPanel from './AlbumTracksPanel.vue'
import SourceIcon from './SourceIcon.vue'
import Ripple from 'vue-ripple-directive'
import {badWord, formatNumber, formatDate, shortNumber} from '@core/utils/filter'
import { toggleTrack, isTrackPlaying } from '@/layouts/components/globalPlayer'
import router from "@/router";
import store from "@/store";
import ability from '@/libs/acl/ability'
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onMounted, onUnmounted, ref, computed, watch, nextTick} from "@vue/composition-api";
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TrackRow from "@/views/db-music/TrackRow";
import CoverInpainting from "@/views/db-music/CoverInpainting";
import RatingStatsBlock from "@/views/db-music/RatingStatsBlock.vue";
import MergeArtistsModal from '@/views/db-music/MergeArtistsModal.vue'
import ArtistChartsTab from './charts/ArtistChartsTab.vue'
import vSelect from "vue-select";
import {count} from "echarts/src/component/dataZoom/history";
import draggable from 'vuedraggable'
import SubmissionPreview from '@/views/moderation/SubmissionPreview.vue'
import { createCoverLinkAfterSubmission, isSubmissionResponse, notifySubmitted } from '@/libs/moderation'
import { getUserData } from '@/auth/utils'

export default {
  components: {
    TrackRow,
    CoverInpainting,
    RatingStatsBlock,
    MergeArtistsModal,
    ArtistChartsTab,
    AlbumCard,
    AlbumTracksPanel,
    SourceIcon,
    BFormInput,
    BFormFile,
    BFormSelect,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    BLink,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BImg,
    BBadge,
    BCardText,
    BDropdown,
    BForm,
    BDropdownItem,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BTab,
    BTabs,
    BSpinner,
    BOverlay,
    BTable,
    BFormRating,
    BModal,
    BAlert,
    BPopover,
    vSelect,
    draggable,
    SubmissionPreview,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {

    }
  },
  methods: {
    count,
    rowClass(item, type) {
      const colorClass = 'table-danger'
      if (!item || type !== 'row') { return }

      // eslint-disable-next-line consistent-return
      if (badWord(item.title)) { return colorClass }
    },
  },
  setup() {
    const STORE_MODULE_NAME = 'artists'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // --- Заявки авторов на одобрении (автор видит свои, админ — все) ---
    const pendingSubmissions = ref([])
    const loadPendingSubmissions = () => store.dispatch('moderation/fetchArtistPending', router.currentRoute.params.id)
      .then(response => { pendingSubmissions.value = response.data.data || [] })
      .catch(() => { pendingSubmissions.value = [] })

    const pendingBy = (type, action) => pendingSubmissions.value
      .filter(item => item.type === type && (!action || item.action === action))
    const pendingForTarget = (type, targetId) => pendingSubmissions.value
      .find(item => item.type === type && targetId !== null && targetId !== undefined
        && String(item.target_id) === String(targetId))
    // Кнопки блокирует только СВОЯ заявка (бэкенд не даёт подать вторую правку той же записи);
    // чужие заявки админ видит бэджем, но править запись они ему не мешают
    const currentUserId = (getUserData() || {}).id
    const ownPendingForTarget = (type, targetId) => pendingSubmissions.value
      .some(item => item.type === type && targetId !== null && targetId !== undefined
        && String(item.target_id) === String(targetId)
        && item.author && String(item.author.id) === String(currentUserId))
    const pendingTargetLabel = submission => (submission.action === 'delete'
      ? 'Удаление на одобрении'
      : 'Правка на одобрении')

    // Вызывается после любого ответа 202: тост + обновление выделенных элементов
    const handleSubmitted = response => {
      notifySubmitted(toast)
      loadPendingSubmissions()
      return response
    }

    const artistDetail = ref(null)
    const similar = ref([])
    const additional = ref([])
    const trackSearchQuery = ref('')
    const artistAliases = ref([])
    const aliasSearchResults = ref([])
    const manualAliasName = ref('')
    const shsTracks = ref([])
    const shsTracksError = ref('')
    const isEditingTitle = ref(false)
    const editTitle = ref('')
    const isLoadingArtist = ref(false)
    const isLoadingStaff = ref(false)
    const disabledArtistId = ref(null)
    const showShsCandidates = ref(false)
    const shsCandidates = ref([])
    const showMusicBrainzCandidates = ref(false)
    const musicBrainzCandidates = ref([])
    const showMusicBrainzAttributes = ref(false)
    const musicBrainzAttributes = ref(null)
    const musicBrainzSelectedFields = ref([])
    const musicBrainzSelectedAliases = ref([])
    const isApplyingMusicBrainzAttributes = ref(false)

    const musicBrainzHasSelection = computed(() => (
      musicBrainzSelectedFields.value.length > 0 || musicBrainzSelectedAliases.value.length > 0
    ))
    const showLinkShsTrackModal = ref(false)
    const linkShsTrackItem = ref(null)
    const selectedTrackIdForLink = ref(null)
    const showShsPerformanceModal = ref(false)
    const shsPerformanceLoading = ref(false)
    const shsPerformanceError = ref('')
    const shsPerformanceInfo = ref(null)

    const showLlmCoversModal = ref(false)
    const llmCoversDirection = ref('performed')
    const llmCoversItems = ref([])
    const llmCoversSources = ref([])
    const llmCoversCached = ref(false)
    const llmCoversLoading = ref(false)
    const llmCoversError = ref('')
    // Индекс строки, в которой сейчас идёт добавление/связывание (лоадер на кнопке)
    const llmCoversBusyRow = ref(null)
    const showLlmPickTrackModal = ref(false)
    const llmPickTrackItem = ref(null)
    const llmPickTrackOptions = ref([])
    const llmPickTrackSelectedId = ref(null)
    const llmPickTrackLoading = ref(false)
    // Режим селекта: 'foreign' — песни чужого артиста строки, 'our' — песни текущего артиста
    const llmPickTrackMode = ref('foreign')
    const llmPickTrackArtistName = ref('')

    const savedCovers = ref([])
    const isLoadingSavedCovers = ref(false)
    const savedCoversLoaded = ref(false)

    const savedPhotos = ref([])
    const isLoadingSavedPhotos = ref(false)
    const savedPhotosLoaded = ref(false)
    const photoResults = ref([])
    const isLoadingPhotos = ref(false)
    const photosSearched = ref(false)
    const photosError = ref('')
    const photoNextStart = ref(null)
    // Модерируемые источники фото: binding — ключ external_ids, без которого источник недоступен
    // (Deezer, Apple Music и Discogs ищутся по имени артиста, привязка не нужна).
    const photoSources = [
      {key: 'spotify', label: 'Spotify', variant: 'outline-success', binding: 'spotify', bindingLabel: 'Spotify'},
      {key: 'yandex', label: 'Яндекс Музыка', variant: 'outline-warning', binding: 'yandex', bindingLabel: 'Яндекс Музыке'},
      {key: 'deezer', label: 'Deezer', variant: 'outline-secondary', binding: null, note: 'Поиск по имени — проверьте, тот ли артист'},
      {key: 'apple', label: 'Apple Music', variant: 'outline-danger', binding: null, note: 'Поиск по имени — проверьте, тот ли артист'},
      {key: 'wikipedia', label: 'Википедия', variant: 'outline-dark', binding: 'wikipedia', bindingLabel: 'Википедии'},
      {key: 'discogs', label: 'Discogs', variant: 'outline-primary', binding: null, note: 'Точно — по ссылке из MusicBrainz, без неё поиск по имени. Фото до 600 px'},
      {key: 'fanart', label: 'Fanart.tv', variant: 'outline-info', binding: 'musicbrainz', bindingLabel: 'MusicBrainz'},
    ]
    const sourcePhotos = ref(photoSources.reduce((state, source) => ({
      ...state,
      [source.key]: {items: [], loading: false, searched: false},
    }), {}))
    const showPhotoModal = ref(false)
    const currentPhoto = ref(null)
    const isSavingPhoto = ref(false)
    const savePhotoError = ref('')
    const activePhotoQuery = ref('')
    const activePhotoType = ref(null)
    const activePhotoFull = ref(false)
    const customPhotoQuery = ref('')
    // Пресеты: query — суффикс к имени артиста для Google, type — сохраняемый тип фото (enum). Обычный поиск: query='' / type=null.
    const photoQueryPresets = [
      {label: 'Фотосессия', query: 'фотосессия', type: 'photoshoot'},
      {label: 'Концерт', query: 'концерт', type: 'live'},
      {label: 'Портрет', query: 'портрет', type: 'portrait'},
      {label: 'В молодости', query: 'в молодости', type: 'young'},
      {label: 'Постер', query: 'постер', type: 'poster'},
    ]
    // Типы поиска Google: выбор подставляет в поле запрос целиком («<артист> <суффикс>»), тип сохраняется вместе с фото.
    const googleSearchTypes = [{label: 'Обычный', query: '', type: null}, ...photoQueryPresets]
    const selectedGoogleSearchType = ref(null)
    const googleSearchTypeLabel = computed(() => {
      const searchType = googleSearchTypes.find(t => t.type === selectedGoogleSearchType.value)
      return searchType ? searchType.label : 'Обычный'
    })
    const photoTypeLabel = (type) => {
      const preset = photoQueryPresets.find(p => p.type === type)
      return preset ? preset.label : type
    }

    const spotifyCovers = ref([])
    const isLoadingCovers = ref(false)
    const coversError = ref('')
    const coversSearched = ref(false)
    const yandexCovers = ref([])
    const isLoadingYandexCovers = ref(false)
    const yandexCoversError = ref('')
    const yandexCoversSearched = ref(false)
    const showCoverPreview = ref(false)
    const previewAlbum = ref(null)
    const showFullCover = ref(false)
    const fullCoverIndex = ref(0)

    // --- Альбомы (вкладка с раскрытием треков) ---
    // Один активный альбом одновременно: { source, id, name } либо null.
    const activeAlbum = ref(null)
    const albumTracks = ref({})
    const albumTracksLoading = ref({})
    // Ширина сетки обложек и число колонок — чтобы вставлять панель треков
    // под строку с активным альбомом (карточки во flex-wrap).
    const albumsContainer = ref(null)
    const albumColumns = ref(1)

    // --- Состав ---
    const currentMembers = ref([])
    const formerMembers = ref([])
    const isLoadingMembers = ref(false)
    const isSavingMembers = ref(false)
    const newCurrentMember = ref('')
    const newFormerMember = ref('')

    const hasSpotifyId = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      return ext && !!ext.spotify
    })

    const hasMusicBrainzId = computed(() => !!(artistDetail.value
      && artistDetail.value.external_ids
      && artistDetail.value.external_ids.musicbrainz))

    // Дата в формате 28.03.1986: formatDate по умолчанию отдаёт «28 мар. 1986 г.»
    const formatFactDate = (value) => formatDate(value, {day: '2-digit', month: '2-digit', year: 'numeric'})

    const formatArtistYears = (from, to) => {
      if (from && to) return `${from} — ${to}`
      if (from) return `с ${from}`
      if (to) return `до ${to}`
      return null
    }

    // Строка фактов: «Тип» первым; по типу переключаются только даты против годов,
    // «Пол» показывается всегда. Неизвестный тип не прячет ничего.
    const artistFacts = computed(() => {
      const detail = artistDetail.value || {}
      const isPerson = detail.artist_type === 'Человек'
      const isGroup = detail.artist_type === 'Группа'

      const facts = [
        {label: 'Тип', value: detail.artist_type || '—'},
        {label: 'Страна', value: detail.country || '—'},
        {label: 'Пол', value: detail.gender || '—'},
      ]

      if (!isGroup) {
        facts.push({label: 'Рождение', value: detail.birth_date ? formatFactDate(detail.birth_date) : '—'})
        facts.push({label: 'Смерть', value: detail.death_date ? formatFactDate(detail.death_date) : '—'})
      }

      if (!isPerson) {
        facts.push({label: 'Годы', value: formatArtistYears(detail.foundation_year, detail.dissolution_year) || '—'})
      }

      return facts
    })

    const groupedSpotifyCovers = computed(() => {
      const albums = []
      const singles = []
      const collabs = []
      spotifyCovers.value.forEach(album => {
        if (album.artists) {
          collabs.push(album)
        } else if (album.album_type === 'album') {
          albums.push(album)
        } else {
          singles.push(album)
        }
      })
      return [albums, singles, collabs].filter(g => g.length)
    })

    const hasYandexId = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      return ext && !!ext.yandex
    })

    const groupedYandexCovers = computed(() => {
      const albums = []
      const singles = []
      const collabs = []
      yandexCovers.value.forEach(album => {
        if (album.artists) {
          collabs.push(album)
        } else if (album.album_type === 'album') {
          albums.push(album)
        } else {
          singles.push(album)
        }
      })
      return [albums, singles, collabs].filter(g => g.length)
    })

    const showShsSyncButton = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      if (!ext || !('second_hand_songs' in ext)) return true
      if (ext.second_hand_songs === null) return true
      return false
    })

    const shsSyncDisabled = computed(() => {
      if (!artistDetail.value) return true
      const ext = artistDetail.value.external_ids
      if (!ext || !('second_hand_songs' in ext)) return false
      return ext.second_hand_songs === null
    })

    const showShsTracksButton = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      return ext && 'second_hand_songs' in ext && ext.second_hand_songs != null
    })

    // Колонки таблицы списков артиста на вкладке «Списки»
    const artistListsColumns = [
      { key: 'title', label: 'Название', sortable: true, thStyle: 'min-width: 260px' },
      { key: 'items_count', label: 'Элементов', sortable: true, thStyle: 'width: 120px' },
      { key: 'auto', label: 'Авто', sortable: true, thStyle: 'width: 90px' },
      { key: 'autotopics', label: 'Автотопики', sortable: false, thStyle: 'min-width: 220px' },
    ]

    const shsTableColumns = [
      { key: 'performer', label: 'Исполнитель', sortable: true },
      { key: 'db_title', label: 'Треки в БД', sortable: true },
      { key: 'shs_title', label: 'Треки в SHS', sortable: true },
      { key: 'is_original', label: 'Тип', sortable: true },
      { key: 'actions', label: '', sortable: false },
      { key: 'uri', label: 'SHS', sortable: false },
    ]

    const llmCoversTableColumns = [
      { key: 'artist', label: 'Артист', sortable: false },
      { key: 'title', label: 'Трек', sortable: false },
      { key: 'artist_track', label: 'Песня артиста', sortable: false },
      { key: 'actions', label: '', sortable: false },
    ]

    const mergedShsTracks = computed(() => {
      if (!artistDetail.value) return []
      const dbTracks = artistDetail.value.tracks || []
      const shsList = shsTracks.value || []

      // Set of DB track IDs that are linked to SHS tracks
      const linkedDbIds = new Set(shsList.filter(s => s.track_id).map(s => s.track_id))
      // Треки БД, которым на странице соответствует SHS-оригинал: «кавер» с тем же треком —
      // это перезапись/концертник самого артиста, т.е. кавер на самого себя
      const originalDbIds = new Set(shsList.filter(s => s.is_original && s.track_id).map(s => s.track_id))

      const rows = []

      // SHS tracks (linked and unlinked)
      shsList.forEach(s => {
        const linked = !!s.linked
        const dbTrack = s.track_id ? dbTracks.find(t => t.id === s.track_id) : null

        const isSelfCover = s.is_original === false && !!s.track_id && originalDbIds.has(s.track_id)

        // Зелёный — есть привязка (для кавера — ещё и указан оригинал в БД);
        // жёлтый — трек в БД есть, но привязки нет, либо привязанный кавер без оригинала;
        // кавер на самого себя: привязан — красный (трек должен быть привязан к оригиналу),
        // не привязан — без подсветки
        let rowVariant = ''
        if (isSelfCover) {
          rowVariant = linked ? 'danger' : ''
        } else if (linked) {
          rowVariant = s.is_original === false && !s.db_original_track_id ? 'warning' : 'success'
        } else if (s.track_id) {
          rowVariant = 'warning'
        }

        rows.push({
          _sortKey: (s.title || '').toLowerCase(),
          performer: s.performer || '',
          db_title: dbTrack ? (dbTrack.track || dbTrack.title) : '',
          shs_title: s.title || '',
          is_original: s.is_original,
          date: s.date,
          track_id: s.track_id,
          shs_id: s.shs_id,
          uri: s.uri,
          linked,
          db_original_track_id: s.db_original_track_id,
          db_covers_count: s.db_covers_count,
          is_self_cover: isSelfCover,
          source: linked ? 'both' : 'shs',
          _rowVariant: rowVariant,
        })
      })

      // DB tracks not linked to any SHS track
      dbTracks.forEach(t => {
        if (!linkedDbIds.has(t.id)) {
          rows.push({
            _sortKey: (t.track || t.title || '').toLowerCase(),
            performer: '',
            db_title: t.track || t.title || '',
            shs_title: '',
            is_original: null,
            date: null,
            track_id: t.id,
            shs_id: null,
            uri: null,
            linked: false,
            db_original_track_id: null,
            db_covers_count: null,
            is_self_cover: false,
            source: 'db',
            _rowVariant: '',
          })
        }
      })

      rows.sort((a, b) => a._sortKey.localeCompare(b._sortKey))
      return rows
    })

    // Table Handlers
    const tableColumns = [
      // Чекбоксы массовых действий — только тем, кто может менять рейтинг
      ...(ability.can('manage', 'db-music')
        ? [{ key: 'select', label: '', sortable: false, thStyle: 'width: 30px' }]
        : []),
      { key: 'artist', sortable: true },
      { key: 'track', sortable: true, thStyle: 'min-width: 320px' },
      { key: 'difficulty_rating', label: 'Сложность', sortable: true, thClass: 'text-nowrap' },
      { key: 'info', label: 'Инфо', sortable: false },
      { key: 'covers', label: 'Каверы', sortable: false, thStyle: 'width: 70px', thClass: 'text-center', tdClass: 'text-center' },
      { key: 'actions', label: 'Действия' },
    ]

    // Сортировка по сложности: установленный рейтинг (или расчётный, если не задан),
    // при равенстве — по прослушиваниям Last.fm в обратном направлении
    const tracksSortCompare = (a, b, key) => {
      if (key !== 'difficulty_rating') return null
      const difficultyOf = row => {
        const value = row.difficulty_rating != null ? row.difficulty_rating : row.calculated_difficulty
        return value != null ? Number(value) : -1
      }
      return (difficultyOf(a) - difficultyOf(b))
          || ((Number(b.play_count) || 0) - (Number(a.play_count) || 0))
    }

    const fetchArtistData = () => {
      let id = router.currentRoute.params.id
      store.dispatch('artists/fetchArtistDetails', {
        id,
        queryParams:
            {
              with_tracks: 1,
              with_lists: 1,
            }
      }).then(res => {
        artistDetail.value = res.data.data
        // Алиасы для шапки приходят в объекте артиста; вкладка «Алиасы» перезапрашивает их отдельно
        artistAliases.value = res.data.data.aliases || []
        selectedClusterIds.value = (res.data.data.clusters || []).map(c => c.id)
        mainClusterId.value = res.data.data.main_cluster_id || null
        selectedDecades.value = res.data.data.decades || []
        mainDecade.value = res.data.data.main_decade || null
      })
    }

    const deleteTrack = async (id) => {
      blockData()
      try {
        await store.dispatch('artists/deleteTrack', {id : id})
            .then(response => {
              // Автор: удаление ушло на одобрение, трек остаётся в списке
              if (isSubmissionResponse(response)) {
                handleSubmitted(response)
                return
              }
              refreshData()
            }).finally(() => { unblockData() })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error deleting track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const loadLastfmInfo = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/loadLastfmInfo', {id : id})
            .then(() => {
              refreshData()
            }).finally(() => { unblockData() })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading LastFM info',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const flushCache = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/flushCache', {id: id})
        toast({
          component: ToastificationContent,
          props: {
            title: `Кеш очищен (${response.data.deleted || 0} ключей)`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error flushing cache',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // Подбор тегов «Tags» в Notion через ЛЛМ (вкладка «Списки»).
    const notionTagsVisibleLimit = 10
    const notionTagGrades = [
      {key: 'good', title: 'Хорошо', variant: 'success'},
      {key: 'medium', title: 'Средне', variant: 'warning'},
      {key: 'weak', title: 'Слабо', variant: 'secondary'},
    ]
    const notionTagGroups = ref({good: [], medium: [], weak: []})
    const notionTagDictionary = ref([])
    const notionTagsLoaded = ref(false)
    const notionTagsCached = ref(false)
    const isSuggestingNotionTags = ref(false)
    const addingNotionTag = ref(null)
    const expandedNotionTagGrades = ref({good: false, medium: false, weak: false})
    const manualNotionTag = ref(null)

    const visibleNotionTags = (grade) => {
      const tags = notionTagGroups.value[grade] || []
      return expandedNotionTagGrades.value[grade] ? tags : tags.slice(0, notionTagsVisibleLimit)
    }

    const notionTagsToggleLabel = (grade) => {
      if (expandedNotionTagGrades.value[grade]) return 'Свернуть'
      const hidden = (notionTagGroups.value[grade] || []).length - notionTagsVisibleLimit
      return 'Показать ещё ' + hidden
    }

    const toggleNotionTagsExpanded = (grade) => {
      expandedNotionTagGrades.value = {
        ...expandedNotionTagGrades.value,
        [grade]: !expandedNotionTagGrades.value[grade],
      }
    }

    const suggestNotionTags = async (refresh) => {
      const id = router.currentRoute.params.id
      isSuggestingNotionTags.value = true
      try {
        const response = await store.dispatch('artists/suggestArtistNotionTags', {id, refresh})
        notionTagGroups.value = response.data.groups || {good: [], medium: [], weak: []}
        notionTagDictionary.value = response.data.dictionary || []
        notionTagsCached.value = !!response.data.cached
        notionTagsLoaded.value = true
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || 'Не удалось подобрать теги',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isSuggestingNotionTags.value = false
      }
    }

    const addNotionTag = async (tag) => {
      if (!tag) return
      const id = router.currentRoute.params.id
      addingNotionTag.value = tag
      try {
        const response = await store.dispatch('artists/addArtistNotionTag', {id, tag})
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || 'Тег добавлен в Notion',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        // Добавленный тег из подборки и словаря убираем: он уже стоит в Notion.
        const groups = {}
        notionTagGrades.forEach(grade => {
          groups[grade.key] = (notionTagGroups.value[grade.key] || []).filter(item => item.tag !== tag)
        })
        notionTagGroups.value = groups
        notionTagDictionary.value = notionTagDictionary.value.filter(item => item !== tag)
        if (manualNotionTag.value === tag) manualNotionTag.value = null
        // Синк страницы на бэкенде уже создал список «Tag: …» — перечитываем деталку.
        await refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || 'Не удалось добавить тег',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        addingNotionTag.value = null
      }
    }

    const syncNotion = async () => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistNotion', {id})
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || 'Синхронизировано с Notion',
            icon: response.data.success ? 'CheckIcon' : 'AlertTriangleIcon',
            variant: response.data.success ? 'success' : 'danger',
          },
        })
        if (response.data.success) {
          await refreshData()
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Notion',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // Кластеры артиста: мультиселект в шапке, правка зеркалируется в мультиселект «Кластер» страницы Notion
    const clusterOptions = ref([])
    const selectedClusterIds = ref([])
    const isSavingClusters = ref(false)
    const mainClusterId = ref(null)
    const NONE_CLUSTER_ID = 6 // кластер «Ничего» — не жанр
    const selectedClusters = computed(() => clusterOptions.value.filter(option => option.id !== NONE_CLUSTER_ID && selectedClusterIds.value.includes(option.id)))

    const currentDecade = Math.floor(new Date().getFullYear() / 10) * 10
    const decadeOptions = []
    for (let decade = 1950; decade <= currentDecade; decade += 10) {
      decadeOptions.push(decade)
    }
    const selectedDecades = ref([])
    const mainDecade = ref(null)
    const isSavingDecades = ref(false)

    store.dispatch('artists/fetchClusters')
      .then(res => {
        clusterOptions.value = res.data.data || []
      })
      .catch(() => {})

    const saveClusters = async () => {
      const id = router.currentRoute.params.id
      isSavingClusters.value = true
      try {
        const main = selectedClusterIds.value.includes(mainClusterId.value) ? mainClusterId.value : null
        const response = await store.dispatch('artists/updateArtistClusters', {id, clusters: selectedClusterIds.value, mainClusterId: main})
        mainClusterId.value = response.data.main_cluster_id || null
        artistDetail.value = {
          ...artistDetail.value,
          clusters: response.data.clusters || [],
          main_cluster_id: mainClusterId.value,
        }
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || 'Кластеры сохранены',
            icon: response.data.notion_synced === false ? 'AlertTriangleIcon' : 'CheckIcon',
            variant: response.data.notion_synced === false ? 'warning' : 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения кластеров',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        // Откат выбора к последнему сохранённому состоянию
        selectedClusterIds.value = ((artistDetail.value && artistDetail.value.clusters) || []).map(c => c.id)
        mainClusterId.value = (artistDetail.value && artistDetail.value.main_cluster_id) || null
      } finally {
        isSavingClusters.value = false
      }
    }

    const setMainCluster = clusterId => {
      mainClusterId.value = clusterId
      saveClusters()
    }

    const saveDecades = async () => {
      const id = router.currentRoute.params.id
      isSavingDecades.value = true
      try {
        const main = selectedDecades.value.includes(mainDecade.value) ? mainDecade.value : null
        const response = await store.dispatch('artists/updateArtistDecades', {id, decades: selectedDecades.value, mainDecade: main})
        selectedDecades.value = response.data.decades || []
        mainDecade.value = response.data.main_decade || null
        artistDetail.value = {
          ...artistDetail.value,
          decades: selectedDecades.value,
          main_decade: mainDecade.value,
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения десятилетий',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        selectedDecades.value = (artistDetail.value && artistDetail.value.decades) || []
        mainDecade.value = (artistDetail.value && artistDetail.value.main_decade) || null
      } finally {
        isSavingDecades.value = false
      }
    }

    const calculateDecades = async () => {
      const id = router.currentRoute.params.id
      isSavingDecades.value = true
      try {
        const response = await store.dispatch('artists/calculateArtistDecades', {id})
        selectedDecades.value = response.data.decades || []
        mainDecade.value = response.data.main_decade || null
        artistDetail.value = {
          ...artistDetail.value,
          decades: selectedDecades.value,
          main_decade: mainDecade.value,
        }
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || 'Десятилетия рассчитаны',
            icon: response.data.success ? 'CheckIcon' : 'AlertTriangleIcon',
            variant: response.data.success ? 'success' : 'warning',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка расчёта десятилетий',
            text: (error.response && error.response.data && error.response.data.message) || error.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isSavingDecades.value = false
      }
    }

    const toggleDecade = decade => {
      selectedDecades.value = selectedDecades.value.includes(decade)
        ? selectedDecades.value.filter(item => item !== decade)
        : [...selectedDecades.value, decade].sort((a, b) => a - b)
      saveDecades()
    }

    const setMainDecade = decade => {
      mainDecade.value = decade
      saveDecades()
    }

    const loadAdditionalTracks = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        // Яндекс грузится отдельным запросом через ru-сервер; его ошибка
        // (например, 404 без Yandex ID) не должна валить остальные источники
        const [response, yandexResponse] = await Promise.all([
          store.dispatch('artists/additionalTracks', {id : id}),
          store.dispatch('artists/additionalTracksYandex', {id : id}).catch(() => null),
        ])
        additional.value = [...response.data, ...(yandexResponse ? yandexResponse.data : [])]
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading additional tracks',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const searchTracksByName = async () => {
      if (!trackSearchQuery.value.trim()) return
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/searchTracksByName', {
          id: id,
          queryParams: { query: trackSearchQuery.value.trim() }
        })
        // Поиск отдаёт плоский список — оборачиваем в одну группу для общего рендера
        additional.value = response.data.length ? [{ source: 'search', tracks: response.data }] : []
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error searching tracks',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addTrack = async (title) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/addTrack', {id : id, queryParams: {title: title}})
        // Автор: трек ушёл на одобрение — список треков не перезагружаем
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const toggleIgnore = async (track) => {
      blockData()
      try {
        const response = await store.dispatch('artists/updateTrack', {id : track.id, queryParams: {ignore: !track.ignore}})
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const downloadTrack = async (id) => {
      blockData()
      try {
        await store.dispatch('artist/downloadTrack', {id : id})
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error downloading track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const fetchSimilar = () => {
      let id = router.currentRoute.params.id
      store.dispatch('artists/fetchSimilarArtists', {id})
          .then(response => {
            similar.value = response.data
          })
    }

    // ==================== Ручной список похожих ====================
    const manualSimilar = ref([])
    const manualSearchQuery = ref('')
    const manualSearchResults = ref([])
    const manualSearchLoading = ref(false)
    const tastediveLoading = ref(false)
    // Имя элемента, который сейчас добавляется в похожие (спиннер на его кнопке)
    const manualAddingKey = ref(null)

    // Цвет рамки карточки кандидата по источнику
    const similarSourceClass = (artist) => ({
      lastfm: 'border-danger',
      yandex: 'border-warning',
      tastedive: 'border-info',
    }[artist.source] || '')

    const manualErrorToast = (error, fallback) => {
      const data = error && error.response && error.response.data
      const message = (data && (data.error || data.message)) || fallback
      toast({
        component: ToastificationContent,
        props: {title: message, icon: 'AlertTriangleIcon', variant: 'danger'},
      })
    }

    const fetchManualSimilar = () => {
      const id = router.currentRoute.params.id
      store.dispatch('artists/fetchManualSimilar', {id})
          .then(response => {
            manualSimilar.value = response.data
          })
    }

    // Похожие грузятся лениво при первом открытии вкладки: /similar ходит во внешние API
    const similarTabOpened = ref(false)
    const openSimilarTab = () => {
      if (similarTabOpened.value) return
      similarTabOpened.value = true
      fetchSimilar()
      fetchManualSimilar()
    }

    // Уже в ручном списке: по id артиста или по имени без регистра
    const isManualAdded = (artistId, name) => manualSimilar.value.some(row => {
      if (artistId && row.artist && row.artist.id === artistId) return true
      const rowName = row.artist ? row.artist.title : row.name
      return name && rowName.toLowerCase() === String(name).toLowerCase()
    })

    const addManualSimilar = async (payload, addingName) => {
      const id = router.currentRoute.params.id
      manualAddingKey.value = addingName
      try {
        const response = await store.dispatch('artists/addManualSimilar', {id, payload})
        // Автор: связь ушла на одобрение — в список не вставляем, она выводится заявкой
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        fetchManualSimilar()
        toast({
          component: ToastificationContent,
          props: {title: `Добавлено в похожие: ${addingName}`, icon: 'CheckIcon', variant: 'success'},
        })
      } catch (error) {
        manualErrorToast(error, 'Не удалось добавить в похожие')
      } finally {
        manualAddingKey.value = null
      }
    }

    const addManualFromSearch = (found) => addManualSimilar({artist_id: found.id}, found.title)

    // Кандидат из Last.fm/Яндекс/TasteDive: есть в БД — связь, нет — запись-строка
    const addManualFromCandidate = (candidate) => addManualSimilar(
        candidate.db ? {artist_id: candidate.db.id} : {name: candidate.name},
        candidate.name,
    )

    const removeManualSimilar = async (row) => {
      const id = router.currentRoute.params.id
      try {
        const response = await store.dispatch('artists/deleteManualSimilar', {id, similarId: row.id})
        // Автор: удаление ушло на одобрение — строка остаётся, приглушённая
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        manualSimilar.value = manualSimilar.value.filter(item => item.id !== row.id)
      } catch (error) {
        manualErrorToast(error, 'Не удалось удалить')
      }
    }

    const saveManualOrder = async () => {
      const id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/reorderManualSimilar', {
          id,
          ids: manualSimilar.value.map(row => row.id),
        })
      } catch (error) {
        manualErrorToast(error, 'Не удалось сохранить порядок')
        fetchManualSimilar()
      }
    }

    const searchManualCandidates = async () => {
      if (!manualSearchQuery.value.trim()) return
      manualSearchLoading.value = true
      try {
        const response = await store.dispatch('artists/fetchArtists', {
          q: manualSearchQuery.value,
          per_page: 10,
        })
        manualSearchResults.value = response.data.data
      } catch (error) {
        manualErrorToast(error, 'Ошибка поиска')
      } finally {
        manualSearchLoading.value = false
      }
    }

    const loadTastedive = async () => {
      const id = router.currentRoute.params.id
      tastediveLoading.value = true
      try {
        const response = await store.dispatch('artists/fetchTastediveSimilar', {id})
        // Дозаливаем в общую сетку кандидатов без дублей по имени
        const existingNames = new Set(similar.value.map(item => item.name.toLowerCase()))
        response.data.forEach(item => {
          if (!existingNames.has(item.name.toLowerCase())) similar.value.push(item)
        })
      } catch (error) {
        manualErrorToast(error, 'TasteDive недоступен')
      } finally {
        tastediveLoading.value = false
      }
    }

    // Last.fm отдаёт для артистов без обложки картинку-заглушку с этим хешем — её не показываем
    const LASTFM_PLACEHOLDER = '2a96cbd8b46e442fc41c2b86b821562f'

    const similarArtistImage = (artist) => {
      // Yandex: готовый URL строкой
      if (typeof artist.image === 'string') {
        return artist.image.includes(LASTFM_PLACEHOLDER) ? null : artist.image
      }
      // Last.fm: объект размеров { small, medium, large, ... }
      if (artist.image && typeof artist.image === 'object') {
        const url = artist.image.large || artist.image.extralarge || artist.image.medium || artist.image.small
        if (url && !url.includes(LASTFM_PLACEHOLDER)) return url
      }
      // Spotify: массив { url, width, height }
      if (Array.isArray(artist.images) && artist.images.length) {
        return artist.images[0].url || null
      }
      return null
    }

    const refreshData = async () => {
      blockData()
      try {
        await fetchArtistData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error refreshing artist data',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const blockData = () => {
      isLoadingArtist.value = true
    }

    const unblockData = () => {
      isLoadingArtist.value = false
    }

    const toStarRating = (popularity) => Math.round(popularity / 10) / 2

    const additionalGroupTitles = {
      spotify_top: 'Spotify Топ-10',
      spotify_search: 'Spotify (поиск)',
      lastfm: 'Last.fm',
      yandex: 'Яндекс.Музыка',
      search: 'Результаты поиска',
    }
    const additionalGroupTitle = (source) => additionalGroupTitles[source] || source

    const aliasesText = computed(() => {
      if (!artistAliases.value || !artistAliases.value.length) return ''
      return artistAliases.value.map(a => a.alias).join(', ')
    })

    // Заголовок статьи Википедии как кандидат в алиасы: без языкового префикса, подчёркивания → пробелы.
    const wikipediaAliasCandidate = (value) => {
      if (!value) return ''
      const decode = (s) => {
        try { return decodeURIComponent(s) } catch (e) { return s }
      }
      let title = value
      if (/^https?:\/\//i.test(value)) {
        try {
          const url = new URL(value)
          const pathMatch = url.pathname.match(/^\/wiki\/(.+)$/)
          title = pathMatch ? pathMatch[1] : url.pathname.replace(/^\//, '')
        } catch (e) {
          title = value
        }
      } else {
        const prefixMatch = value.match(/^([a-z]{2,3})[:/](.+)$/i)
        if (prefixMatch) title = prefixMatch[2]
      }
      return decode(title).replace(/_/g, ' ').trim()
    }

    // Кандидаты в алиасы из названий внешних ссылок: не совпадают с названием артиста
    // и существующими алиасами, одинаковые названия с разных площадок объединяются.
    const externalAliasCandidates = computed(() => {
      const detail = artistDetail.value
      if (!detail) return []

      const sourceLabels = {
        spotify: 'Spotify',
        yandex: 'Yandex',
        second_hand_songs: 'SHS',
        musicbrainz: 'MusicBrainz',
      }

      const rawCandidates = []
      const titles = detail.external_titles || {}
      Object.keys(sourceLabels).forEach(key => {
        const name = (titles[key] || '').trim()
        if (name) rawCandidates.push({name, source: sourceLabels[key]})
      })

      const wikipediaTitle = wikipediaAliasCandidate((detail.external_ids || {}).wikipedia)
      if (wikipediaTitle) rawCandidates.push({name: wikipediaTitle, source: 'Wikipedia'})

      const takenNames = new Set([
        (detail.title || '').trim().toLowerCase(),
        ...artistAliases.value.map(a => a.alias.trim().toLowerCase()),
      ])

      const candidates = []
      rawCandidates.forEach(({name, source}) => {
        if (takenNames.has(name.toLowerCase())) return
        const existing = candidates.find(candidate => candidate.name.toLowerCase() === name.toLowerCase())
        if (existing) {
          existing.sources.push(source)
        } else {
          candidates.push({name, sources: [source]})
        }
      })
      return candidates
    })

    fetchArtistData()
    loadPendingSubmissions()

    const ignoreTracks = async () => {
      blockData()
      try {
        await store.dispatch('artists/ignoreTracks', {
          id: router.currentRoute.params.id
        })
        fetchArtistData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error ignoring tracks',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const wikipediaUrl = (value) => {
      if (!value) return '#'
      if (/^https?:\/\//i.test(value)) return value
      const match = value.match(/^([a-z]{2,3})[:/](.+)$/i)
      if (match) return `https://${match[1]}.wikipedia.org/wiki/${match[2]}`
      return `https://ru.wikipedia.org/wiki/${value}`
    }

    const wikipediaLabel = (value) => {
      if (!value) return ''
      const decode = (s) => {
        try { return decodeURIComponent(s) } catch (e) { return s }
      }
      if (/^https?:\/\//i.test(value)) {
        try {
          const url = new URL(value)
          const langMatch = url.hostname.match(/^([a-z]{2,3})\.wikipedia\.org$/i)
          const pathMatch = url.pathname.match(/^\/wiki\/(.+)$/)
          const lang = langMatch ? langMatch[1].toUpperCase() : ''
          const title = pathMatch ? decode(pathMatch[1]) : decode(url.pathname.replace(/^\//, ''))
          return lang ? `[${lang}]: ${title}` : title
        } catch (e) {
          return value
        }
      }
      const m = value.match(/^([a-z]{2,3})[:/](.+)$/i)
      if (m) return `[${m[1].toUpperCase()}]: ${decode(m[2])}`
      return decode(value)
    }

    // Подпись бэджика внешней ссылки: название на площадке, если оно есть, иначе внешний ID.
    const externalLabel = (key) => {
      const titles = artistDetail.value.external_titles
      const ids = artistDetail.value.external_ids || {}
      return (titles && titles[key]) || ids[key]
    }

    const trackExternalLabel = (track, key) => (
      (track.external_titles && track.external_titles[key]) || track.external_ids[key]
    )

    const setExternalId = (key, value, title = null) => {
      artistDetail.value = {
        ...artistDetail.value,
        external_ids: {
          ...(artistDetail.value.external_ids || {}),
          [key]: value,
        },
        external_titles: {
          ...(artistDetail.value.external_titles || {}),
          [key]: title,
        },
      }
    }

    const syncSpotify = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistSpotify', {id})
        if (response.data.success) {
          if (response.data.spotify_id) {
            setExternalId('spotify', response.data.spotify_id, response.data.spotify_title)
          }
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Spotify синхронизирован',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в Spotify',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Spotify',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const syncYandex = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistYandex', {id})
        if (response.data.success) {
          if (response.data.yandex_id) {
            setExternalId('yandex', response.data.yandex_id, response.data.yandex_title)
          }
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Yandex синхронизирован',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в Yandex Music',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Yandex Music',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const syncWikipedia = async () => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistWikipedia', {id})
        if (response.data.success) {
          if (response.data.url) {
            setExternalId('wikipedia', response.data.url)
          } else if (response.data.external_ids) {
            artistDetail.value = {
              ...artistDetail.value,
              external_ids: response.data.external_ids,
            }
          }
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Wikipedia синхронизирован',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в Wikipedia',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Wikipedia',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const syncShs = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistShs', {id})
        if (response.data.success) {
          if (response.data.shs_id) {
            setExternalId('second_hand_songs', response.data.shs_id, response.data.shs_name)
          }
          toast({
            component: ToastificationContent,
            props: {
              title: `Привязано: ${response.data.shs_name}`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else if (response.data.candidates && response.data.candidates.length) {
          shsCandidates.value = response.data.candidates
          showShsCandidates.value = true
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в SHS',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с SHS',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const selectShsCandidate = async (shsId) => {
      let id = router.currentRoute.params.id
      blockData()
      showShsCandidates.value = false
      try {
        const response = await store.dispatch('artists/syncArtistShs', {id, queryParams: {shs_id: shsId}})
        if (response.data.success) {
          setExternalId('second_hand_songs', shsId, response.data.shs_name)
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'SHS ID сохранён',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Ошибка сохранения',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения SHS ID',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const openMusicBrainzAttributes = async () => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/fetchArtistMusicBrainzAttributes', {id})
        musicBrainzAttributes.value = response.data
        musicBrainzSelectedFields.value = (response.data.fields || [])
          .filter(field => field.checked)
          .map(field => field.key)
        musicBrainzSelectedAliases.value = (response.data.aliases || []).map(alias => alias.alias)
        showMusicBrainzAttributes.value = true
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка получения данных из MusicBrainz',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const findMusicBrainzFromAttributes = async () => {
      showMusicBrainzAttributes.value = false
      await syncMusicBrainz()
    }

    const applyMusicBrainzAttributes = async () => {
      const id = router.currentRoute.params.id
      isApplyingMusicBrainzAttributes.value = true
      try {
        const response = await store.dispatch('artists/applyArtistMusicBrainzAttributes', {
          id,
          fields: musicBrainzSelectedFields.value,
          aliases: musicBrainzSelectedAliases.value,
        })
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message,
            icon: response.data.success ? 'CheckIcon' : 'AlertTriangleIcon',
            variant: response.data.success ? 'success' : 'warning',
          },
        })
        // Закрываем модалку только при полном успехе, но деталку перечитываем,
        // если применилось хоть что-то — иначе созданные алиасы останутся невидимыми.
        if (response.data.success) {
          showMusicBrainzAttributes.value = false
        }
        if ((response.data.applied && response.data.applied.length) || response.data.aliases_created > 0) {
          fetchArtistData()
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка применения данных из MusicBrainz',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isApplyingMusicBrainzAttributes.value = false
      }
    }

    const musicBrainzYears = (candidate) => (
      [candidate.begin, candidate.end].filter(Boolean).join(' — ')
    )

    const showMusicBrainzResult = (data) => {
      const parts = []
      const backfilled = Object.keys(data.backfilled || {})
      if (backfilled.length) parts.push(`дозаполнено: ${backfilled.join(', ')}`)
      const conflicts = data.conflicts || []
      if (conflicts.length) {
        parts.push(`расхождения: ${conflicts.map(c => `${c.source} (у нас ${c.ours}, в MB ${c.mb})`).join('; ')}`)
      }

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          text: parts.join('. ') || undefined,
          icon: 'CheckIcon',
          variant: conflicts.length ? 'warning' : 'success',
        },
      })
    }

    const applyMusicBrainzResult = (data) => {
      setExternalId('musicbrainz', data.mbid, data.mb_name)
      showMusicBrainzResult(data)

      // Добивка могла создать другие внешние связи — перечитываем деталку целиком.
      if (Object.keys(data.backfilled || {}).length) {
        fetchArtistData()
      }
    }

    const syncMusicBrainz = async () => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistMusicBrainz', {id})
        if (response.data.success) {
          applyMusicBrainzResult(response.data)
        } else if (response.data.candidates && response.data.candidates.length) {
          musicBrainzCandidates.value = response.data.candidates
          showMusicBrainzCandidates.value = true
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в MusicBrainz',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с MusicBrainz',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const selectMusicBrainzCandidate = async (mbid) => {
      const id = router.currentRoute.params.id
      blockData()
      showMusicBrainzCandidates.value = false
      try {
        const response = await store.dispatch('artists/syncArtistMusicBrainz', {id, queryParams: {mbid}})
        if (response.data.success) {
          applyMusicBrainzResult(response.data)
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Ошибка сохранения',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения MusicBrainz ID',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // --- Добавление / удаление внешних ID артиста ---
    const showAddExternalIdModal = ref(false)
    const externalIdInput = ref('')
    const isAddingExternalId = ref(false)

    const addExternalId = async () => {
      const url = externalIdInput.value.trim()
      if (!url) return
      isAddingExternalId.value = true
      try {
        const id = router.currentRoute.params.id
        const res = await store.dispatch('artists/addArtistExternalId', {id, url})
        showAddExternalIdModal.value = false
        externalIdInput.value = ''
        artistDetail.value = {
          ...artistDetail.value,
          external_ids: res.data.external_ids,
          external_titles: res.data.external_titles,
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка добавления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isAddingExternalId.value = false
      }
    }

    const deleteExternalId = async (source) => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const res = await store.dispatch('artists/deleteArtistExternalId', {id, source})
        artistDetail.value = {
          ...artistDetail.value,
          external_ids: res.data.external_ids,
          external_titles: res.data.external_titles,
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка удаления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // --- Объединение и удаление артиста ---
    const showMergeArtistsModal = ref(false)

    const onArtistsMerged = (baseId) => {
      showMergeArtistsModal.value = false
      if (String(baseId) === String(router.currentRoute.params.id)) {
        refreshData()
      } else {
        router.push({ name: 'artist-view', params: { id: baseId } }).then(() => {
          refreshData()
        })
      }
    }

    const deleteArtist = async () => {
      const artist = artistDetail.value
      const message = `Удалить артиста «${artist.title}»?\n\n`
        + `Будут удалены: треки (${artist.tracks_count || 0} шт., включая mp3 на S3), `
        + `обложки (${artist.covers_count || 0}), фото (${artist.photos_count || 0}), `
        + `алиасы и привязки к спискам. Страница в Notion будет заархивирована.`
      if (!confirm(message)) return

      blockData()
      try {
        await store.dispatch('artists/deleteArtist', { id: artist.id })
        toast({
          component: ToastificationContent,
          props: { title: 'Артист удалён', icon: 'CheckIcon', variant: 'success' },
        })
        router.push({ name: 'artists' })
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Ошибка удаления артиста'
        toast({
          component: ToastificationContent,
          props: { title: errorMessage, icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        unblockData()
      }
    }

    // --- Состав: computed и методы ---
    const hasMembers = computed(() => {
      if (!artistDetail.value) return false
      return !!artistDetail.value.members
    })

    const initMembers = () => {
      if (artistDetail.value?.members) {
        const m = artistDetail.value.members
        currentMembers.value = m.current_members
          ? m.current_members.split('\n').filter(s => s.trim())
          : []
        formerMembers.value = m.former_members
          ? m.former_members.split('\n').filter(s => s.trim())
          : []
      } else {
        currentMembers.value = []
        formerMembers.value = []
      }
    }

    const loadMembers = async () => {
      const id = router.currentRoute.params.id
      isLoadingMembers.value = true
      try {
        const res = await store.dispatch('artists/loadArtistMembers', { id })
        // Автор: состав только подобран (не сохранён) — подставляем в поля и сразу отправляем на одобрение
        if (res.data.success && res.data.saved === false) {
          const splitMembers = value => (value ? String(value).split('\n').filter(s => s.trim()) : [])
          currentMembers.value = splitMembers(res.data.data.current_members)
          formerMembers.value = splitMembers(res.data.data.former_members)
          await saveMembers()
          return
        }
        if (res.data.success) {
          artistDetail.value = {
            ...artistDetail.value,
            members: res.data.data,
          }
          initMembers()
          try {
            const artistRes = await store.dispatch('artists/fetchArtistDetails', { id, queryParams: {} })
            if (artistRes.data.data && artistRes.data.data.external_ids) {
              artistDetail.value = {
                ...artistDetail.value,
                external_ids: artistRes.data.data.external_ids,
              }
            }
          } catch (e) {}
          toast({
            component: ToastificationContent,
            props: {
              title: 'Состав загружен',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'Ошибка загрузки состава',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки состава',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingMembers.value = false
      }
    }

    const saveMembers = async () => {
      const id = router.currentRoute.params.id
      isSavingMembers.value = true
      try {
        const res = await store.dispatch('artists/updateArtistMembers', {
          id,
          currentMembers: currentMembers.value.filter(s => s.trim()).join('\n'),
          formerMembers: formerMembers.value.filter(s => s.trim()).join('\n'),
        })
        // Автор: правка состава ушла на одобрение, поля не перетираем
        if (isSubmissionResponse(res)) {
          handleSubmitted(res)
          return
        }
        if (res.data.success) {
          artistDetail.value = {
            ...artistDetail.value,
            members: res.data.data,
            ...(res.data.data && res.data.data.external_ids ? { external_ids: res.data.data.external_ids } : {}),
          }
          initMembers()
          toast({
            component: ToastificationContent,
            props: {
              title: 'Состав сохранён',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения состава',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isSavingMembers.value = false
      }
    }

    const removeMember = (list, index) => {
      list.splice(index, 1)
    }

    const moveToFormer = (index) => {
      const member = currentMembers.value.splice(index, 1)[0]
      formerMembers.value.push(member)
    }

    const moveToCurrent = (index) => {
      const member = formerMembers.value.splice(index, 1)[0]
      currentMembers.value.push(member)
    }

    const addCurrentMember = () => {
      const name = newCurrentMember.value.trim()
      if (!name) return
      currentMembers.value.push(name)
      newCurrentMember.value = ''
    }

    const addFormerMember = () => {
      const name = newFormerMember.value.trim()
      if (!name) return
      formerMembers.value.push(name)
      newFormerMember.value = ''
    }

    const moveMemberUp = (list, index) => {
      if (index <= 0) return
      const tmp = list[index]
      list.splice(index, 1, list[index - 1])
      list.splice(index - 1, 1, tmp)
    }

    const moveMemberDown = (list, index) => {
      if (index >= list.length - 1) return
      const tmp = list[index]
      list.splice(index, 1, list[index + 1])
      list.splice(index + 1, 1, tmp)
    }

    const syncShsTracks = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistShsTracks', {id})
        if (response.data.success) {
          toast({
            component: ToastificationContent,
            props: {
              title: `Привязано: ${response.data.linked}, не найдено: ${response.data.not_found}`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          await fetchShsTracks()
          // SH-бэджи во вкладке треков берутся из artistDetail — без перезапроса не появятся
          fetchArtistData()
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Ошибка синхронизации треков',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации треков SHS',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const fetchShsTracks = async () => {
      let id = router.currentRoute.params.id
      blockData()
      shsTracksError.value = ''
      try {
        const response = await store.dispatch('artists/fetchArtistShsTracks', {id})
        if (response.data.success) {
          shsTracks.value = response.data.tracks
        } else {
          shsTracksError.value = response.data.message
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки SHS треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addShsTrack = async (shsTrack) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/addTrack', {id, queryParams: {title: shsTrack.shs_title || shsTrack.title}})
        // Автор: трек ушёл на одобрение — не помечаем добавленным и не перезагружаем списки
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchShsTracks()
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const artistTracksForLink = computed(() => {
      if (!artistDetail.value || !artistDetail.value.tracks) return []
      const sorted = artistDetail.value.tracks
          .slice()
          .sort((a, b) => (a.track || a.title).localeCompare(b.track || b.title))

      if (!linkShsTrackItem.value) {
        return sorted.map(t => ({ label: t.track || t.title, value: t.id }))
      }

      const shsTitle = (linkShsTrackItem.value.shs_title || linkShsTrackItem.value.title || '').toLowerCase()

      // Find insertion point in sorted array
      let insertIdx = sorted.findIndex(t => (t.track || t.title).toLowerCase().localeCompare(shsTitle) >= 0)
      if (insertIdx === -1) insertIdx = sorted.length

      // Collect nearby tracks (up to 3 before and 3 after the insertion point)
      const nearbyIds = new Set()
      const nearby = []
      for (let i = Math.max(0, insertIdx - 3); i <= Math.min(sorted.length - 1, insertIdx + 2); i++) {
        nearby.push(sorted[i])
        nearbyIds.add(sorted[i].id)
      }

      const rest = sorted.filter(t => !nearbyIds.has(t.id))

      return [
        ...nearby.map(t => ({ label: `⭐ ${t.track || t.title}`, value: t.id })),
        ...rest.map(t => ({ label: t.track || t.title, value: t.id })),
      ]
    })

    const openLinkShsTrackModal = (shsTrack) => {
      linkShsTrackItem.value = shsTrack
      selectedTrackIdForLink.value = null
      showLinkShsTrackModal.value = true
    }

    const openShsPerformanceModal = async (item) => {
      showShsPerformanceModal.value = true
      shsPerformanceLoading.value = true
      shsPerformanceError.value = ''
      shsPerformanceInfo.value = null
      try {
        const response = await store.dispatch('artists/fetchShsPerformance', {shsId: item.shs_id})
        if (response.data.success) {
          shsPerformanceInfo.value = response.data
        } else {
          shsPerformanceError.value = response.data.message || 'Не удалось загрузить данные из SHS'
        }
      } catch (error) {
        shsPerformanceError.value = 'Ошибка загрузки данных из SHS'
      } finally {
        shsPerformanceLoading.value = false
      }
    }

    const linkShsTrack = async () => {
      if (!selectedTrackIdForLink.value || !linkShsTrackItem.value) return
      blockData()
      try {
        const response = await store.dispatch('artists/linkShsTrack', {
          id: selectedTrackIdForLink.value,
          shs_id: linkShsTrackItem.value.shs_id,
        })
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.relinked ? 'Трек перепривязан' : 'Трек привязан',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        showLinkShsTrackModal.value = false
        await fetchShsTracks()
        fetchArtistData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || 'Ошибка привязки трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // Дата SHS «гггг-мм-дд» (бывает неполной: «гггг» или «гггг-мм») → «дд.мм.гггг»
    const formatShsDate = (date) => (date ? date.split('-').reverse().join('.') : '')

    // Мгновенная привязка: трек БД уже найден по названию, модалка выбора не нужна
    const quickLinkShsTrack = async (shsTrack) => {
      if (!shsTrack.track_id || !shsTrack.shs_id) return
      blockData()
      try {
        const response = await store.dispatch('artists/linkShsTrack', {
          id: shsTrack.track_id,
          shs_id: shsTrack.shs_id,
        })
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.relinked ? 'Трек перепривязан' : 'Трек привязан',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchShsTracks()
        fetchArtistData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || 'Ошибка привязки трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // Открыть модалку каверов (найденных LLM) в заданном направлении и сразу загрузить данные
    const openLlmCoversModal = (direction) => {
      llmCoversDirection.value = direction
      llmCoversItems.value = []
      llmCoversSources.value = []
      llmCoversCached.value = false
      llmCoversError.value = ''
      showLlmCoversModal.value = true
      fetchLlmCovers()
    }

    const fetchLlmCovers = async (refresh = false) => {
      let id = router.currentRoute.params.id
      llmCoversLoading.value = true
      llmCoversError.value = ''
      try {
        const response = await store.dispatch('artists/fetchArtistLlmCovers', {
          id,
          direction: llmCoversDirection.value,
          refresh,
        })
        if (response.data.success) {
          llmCoversItems.value = response.data.items.map(item => ({
            ...item,
            _rowVariant: item.linked ? 'success' : null,
          }))
          llmCoversSources.value = response.data.sources || []
          llmCoversCached.value = response.data.cached
        } else {
          llmCoversError.value = response.data.message || 'Не удалось получить данные'
        }
      } catch (error) {
        llmCoversError.value = error.response?.data?.message || 'Не удалось получить данные'
      } finally {
        llmCoversLoading.value = false
      }
    }

    // Добавить артисту его песню из строки LLM-выдачи. В «Каверах на артиста» добавленная
    // песня — оригинал: если чужой кавер уже в БД, сразу привязываем его к ней.
    const addLlmArtistTrack = async (item, rowIndex) => {
      let id = router.currentRoute.params.id
      llmCoversBusyRow.value = rowIndex
      try {
        const response = await store.dispatch('artists/addTrack', {id, queryParams: {title: item.artist_track}})
        // Автор: трек ушёл на одобрение. Связь с кавером тоже заявкой — оригиналом выступает заявка на трек
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          if (llmCoversDirection.value === 'received' && item.track_id) {
            const linked = await createCoverLinkAfterSubmission(store, toast, {
              original_submission_id: response.data.submission.id,
              cover_track_id: item.track_id,
            })
            if (linked) loadPendingSubmissions()
          }
          return
        }
        if (!response.data.success) {
          throw new Error(response.data.message || 'Ошибка добавления трека')
        }
        let linkedNow = false
        if (llmCoversDirection.value === 'received' && item.track_id && response.data.track_id) {
          const linkResponse = await store.dispatch('artists/addTrackCover', {id: response.data.track_id, cover_id: item.track_id})
          if (isSubmissionResponse(linkResponse)) {
            handleSubmitted(linkResponse)
            await fetchLlmCovers()
            refreshData()
            return
          }
          linkedNow = true
        }
        toast({
          component: ToastificationContent,
          props: {
            title: linkedNow ? 'Трек добавлен и связан с кавером' : 'Трек добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchLlmCovers()
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: (error.response && error.response.data && error.response.data.message) || error.message || 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        llmCoversBusyRow.value = null
      }
    }

    // Связать чужой трек с треком артиста: performed → чужой трек оригинал, received → чужой трек кавер
    const linkLlmCoverTrack = async (item, rowIndex, foreignTrackId = null) => {
      const coverForeignTrackId = foreignTrackId || item.track_id
      llmCoversBusyRow.value = rowIndex
      try {
        const response = llmCoversDirection.value === 'received'
          ? await store.dispatch('artists/addTrackCover', {id: item.our_track_id, cover_id: coverForeignTrackId})
          : await store.dispatch('artists/setTrackOriginal', {id: item.our_track_id, original_id: coverForeignTrackId})
        // Автор: связь ушла на одобрение
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Треки связаны',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchLlmCovers()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || 'Ошибка связывания треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        llmCoversBusyRow.value = null
      }
    }

    // Ручной выбор трека чужого артиста для привязки (LLM мог назвать трек не так, как в БД)
    const openLlmPickTrackModal = async (item) => {
      llmPickTrackMode.value = 'foreign'
      llmPickTrackArtistName.value = item.artist
      llmPickTrackItem.value = item
      llmPickTrackOptions.value = []
      llmPickTrackSelectedId.value = null
      showLlmPickTrackModal.value = true
      llmPickTrackLoading.value = true
      try {
        const response = await store.dispatch('artists/fetchArtistDetails', {
          id: item.artist_id,
          queryParams: {with_tracks: 1},
        })
        // Ресурс артиста обёрнут в data (как в fetchArtistData)
        const tracks = (response.data.data && response.data.data.tracks) || []
        llmPickTrackOptions.value = tracks.map(track => ({
          label: track.track || track.title,
          value: track.id,
        }))
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось загрузить треки артиста',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        showLlmPickTrackModal.value = false
      } finally {
        llmPickTrackLoading.value = false
      }
    }

    // Ручной выбор песни ТЕКУЩЕГО артиста («Каверы на артиста»: LLM мог назвать её не так, как в БД).
    // Треки уже загружены в artistDetail — без запроса.
    const openLlmPickOurTrackModal = (item) => {
      llmPickTrackMode.value = 'our'
      llmPickTrackArtistName.value = artistDetail.value.title
      llmPickTrackItem.value = item
      llmPickTrackSelectedId.value = null
      llmPickTrackLoading.value = false
      llmPickTrackOptions.value = (artistDetail.value.tracks || []).map(track => ({
        label: track.track || track.title,
        value: track.id,
      }))
      showLlmPickTrackModal.value = true
    }

    const confirmLlmPickTrack = async () => {
      if (!llmPickTrackSelectedId.value || !llmPickTrackItem.value) return
      const item = llmPickTrackItem.value
      const selectedTrackId = llmPickTrackSelectedId.value

      if (llmPickTrackMode.value === 'foreign') {
        await linkLlmCoverTrack(item, null, selectedTrackId)
        return
      }

      // Режим 'our' (только received): выбранная песня — оригинал; существующий кавер
      // привязываем, отсутствующий — создаём треком без артиста и привязываем.
      try {
        let coverTrackId = item.track_id
        if (!coverTrackId) {
          const response = await store.dispatch('artists/createTrackWithoutArtist', {
            artist: item.artist,
            title: item.title,
          })
          // Автор: кавер ушёл на одобрение — связь тоже заявкой, кавером выступает заявка на трек
          if (isSubmissionResponse(response)) {
            handleSubmitted(response)
            const linked = await createCoverLinkAfterSubmission(store, toast, {
              original_track_id: selectedTrackId,
              cover_submission_id: response.data.submission.id,
            })
            if (linked) loadPendingSubmissions()
            return
          }
          coverTrackId = response.data.track_id
          if (!coverTrackId) {
            throw new Error(response.data.message || 'Ошибка добавления кавера')
          }
        }
        const linkResponse = await store.dispatch('artists/addTrackCover', {id: selectedTrackId, cover_id: coverTrackId})
        if (isSubmissionResponse(linkResponse)) {
          handleSubmitted(linkResponse)
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: item.track_id ? 'Треки связаны' : 'Кавер добавлен и связан',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchLlmCovers()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: (error.response && error.response.data && error.response.data.message) || error.message || 'Ошибка связывания треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    // «Каверы на артиста»: кавера нет в БД совсем — создаём трек без артиста и сразу привязываем
    const addLlmForeignCoverTrack = async (item, rowIndex) => {
      llmCoversBusyRow.value = rowIndex
      try {
        const response = await store.dispatch('artists/createTrackWithoutArtist', {
          artist: item.artist,
          title: item.title,
        })
        // Автор: кавер ушёл на одобрение — связь тоже заявкой, кавером выступает заявка на трек
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          const linked = await createCoverLinkAfterSubmission(store, toast, {
            original_track_id: item.our_track_id,
            cover_submission_id: response.data.submission.id,
          })
          if (linked) loadPendingSubmissions()
          return
        }
        // При дубле бэкенд тоже возвращает track_id — привязываем существующий трек
        const coverTrackId = response.data.track_id
        if (!coverTrackId) {
          throw new Error(response.data.message || 'Ошибка добавления кавера')
        }
        const linkResponse = await store.dispatch('artists/addTrackCover', {id: item.our_track_id, cover_id: coverTrackId})
        if (isSubmissionResponse(linkResponse)) {
          handleSubmitted(linkResponse)
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер добавлен и связан',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchLlmCovers()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: (error.response && error.response.data && error.response.data.message) || error.message || 'Ошибка добавления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        llmCoversBusyRow.value = null
      }
    }

    const fetchAliases = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/fetchArtistAliases', {id})
        artistAliases.value = response.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading aliases',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const searchForAliases = async () => {
      blockData()
      try {
        const response = await store.dispatch('artists/searchArtists', {q: artistDetail.value.title})
        const existingNames = artistAliases.value.map(a => a.alias)
        aliasSearchResults.value = response.data.filter(a => !a.db && !existingNames.includes(a.name) && a.name !== artistDetail.value.title)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error searching aliases',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addManualAlias = async () => {
      const name = manualAliasName.value.trim()
      if (!name) return
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addAlias', {id, queryParams: {alias: name}})
        await fetchAliases()
        manualAliasName.value = ''
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding alias',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const toggleCoverArtist = async (value) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {is_cover_artist: value}})
        artistDetail.value.is_cover_artist = value
        toast({
          component: ToastificationContent,
          props: {
            title: value ? 'Отмечен как кавер-артист' : 'Снята отметка кавер-артиста',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const toggleSkipOriginals = async (value) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {skip_originals: value}})
        artistDetail.value.skip_originals = value
        toast({
          component: ToastificationContent,
          props: {
            title: value ? 'Оригиналы будут пропускаться' : 'Оригиналы не будут пропускаться',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const difficultyTooltip = '<strong>1</strong> — Все знают, легко берется вокал<br>'
      + '<strong>2</strong> — Широко известна, узнаваемый вокал, заезженные хиты<br>'
      + '<strong>3</strong> — Широко известна в своем жанре, узнаваемый вокал, топ-хиты<br>'
      + '<strong>4</strong> — Вспоминаемая группа с топ-хитами<br>'
      + '<strong>5</strong> — Вспоминаемая группа с топ-хитом или несколькими, жанровая<br>'
      + '<strong>6</strong> — Вспоминаемая группа с хитом или несколькими<br>'
      + '<strong>7</strong> — Забытая группа с топ-хитом прошлого<br>'
      + '<strong>8</strong> — Малоизвестная группа, есть хит, но не топ<br>'
      + '<strong>9</strong> — Нишевая группа, где-то светилась<br>'
      + '<strong>10</strong> — Слабо известная группа, нишевая'

    const updateDifficultyRating = async (rating) => {
      let id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {difficulty_rating: rating}})
        artistDetail.value.difficulty_rating = rating
        toast({
          component: ToastificationContent,
          props: {
            title: 'Сложность обновлена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const recalcDifficultyLoading = ref(false)

    const recalculateDifficulty = async () => {
      recalcDifficultyLoading.value = true
      try {
        const response = await store.dispatch('artists/recalculateArtistDifficulty', {id: router.currentRoute.params.id})
        const artist = response.data.artist.data || response.data.artist
        artistDetail.value.calculated_difficulty = artist.calculated_difficulty
        toast({
          component: ToastificationContent,
          props: {
            title: 'Расчётная сложность пересчитана',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка пересчёта расчётной сложности',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        recalcDifficultyLoading.value = false
      }
    }

    const editingTrackRatingId = ref(null)

    // Трек артиста уходит в общий плеер шапки; контекст — год, сложность, прослушивания
    const playArtistTrack = track => {
      const hasDifficulty = track.difficulty_rating !== null && track.difficulty_rating !== undefined

      toggleTrack({
        key: `track-${track.id}`,
        url: track.file_url,
        title: track.track,
        artist: track.artist,
        sourceLabel: 'Файл трека',
        link: artistDetail.value ? { name: 'artist-view', params: { id: artistDetail.value.id } } : null,
        details: [
          track.release_year ? String(track.release_year) : null,
          hasDifficulty ? `сложность ${track.difficulty_rating}` : null,
          track.spotify_playcount ? `Spotify ${shortNumber(track.spotify_playcount)}` : null,
        ],
        hint: null,
      })
    }

    const syncingTrackStatsId = ref(null)
    const recalcTracksDifficultyLoading = ref(false)
    const syncTracksStatsLoading = ref(false)
    const bindingTracksSource = ref(null)

    const bindTracksExternalIds = async (source) => {
      const id = router.currentRoute.params.id
      bindingTracksSource.value = source
      try {
        const response = await store.dispatch('artists/bindArtistTracksExternalIds', {id, source})
        const freshTracks = response.data.tracks && response.data.tracks.data
          ? response.data.tracks.data
          : response.data.tracks
        if (Array.isArray(freshTracks)) {
          const byId = {}
          freshTracks.forEach(t => { byId[t.id] = t })
          ;(artistDetail.value.tracks || []).forEach(track => {
            const fresh = byId[track.id]
            if (fresh) {
              track.external_ids = fresh.external_ids
              if (fresh.external_titles) track.external_titles = fresh.external_titles
              track.release_year = fresh.release_year
            }
          })
        }
        const result = response.data.result || {}
        toast({
          component: ToastificationContent,
          props: {
            title: `${source === 'spotify' ? 'Spotify' : 'Yandex'}: привязка завершена`,
            text: `Привязано: ${result.bound}, годов проставлено: ${result.years_filled}, не найдено: ${result.not_found}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка привязки внешних ID',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        bindingTracksSource.value = null
      }
    }

    const recalculateTracksDifficulty = async () => {
      const id = router.currentRoute.params.id
      recalcTracksDifficultyLoading.value = true
      try {
        const response = await store.dispatch('artists/recalculateArtistTracksDifficulty', {id})
        const ratings = response.data.ratings || {}
        ;(artistDetail.value.tracks || []).forEach(track => {
          if (ratings[track.id] !== undefined) {
            track.calculated_difficulty = ratings[track.id]
          }
        })
        toast({
          component: ToastificationContent,
          props: {
            title: 'Сложность треков пересчитана',
            text: `Обновлено: ${response.data.updated} из ${response.data.total}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка пересчёта сложности треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        recalcTracksDifficultyLoading.value = false
      }
    }

    // Массовая перезагрузка прослушиваний (Last.fm + Spotify) у всех треков артиста
    const syncTracksPlatformStats = async () => {
      if (syncTracksStatsLoading.value) return
      const id = router.currentRoute.params.id
      syncTracksStatsLoading.value = true
      try {
        const response = await store.dispatch('artists/syncArtistTracksPlatformStats', {id})
        const tracksMap = response.data.tracks || {}
        ;(artistDetail.value.tracks || []).forEach(track => {
          const stats = tracksMap[track.id]
          if (stats) {
            track.play_count = stats.play_count
            track.spotify_playcount = stats.spotify_playcount
            track.calculated_difficulty = stats.calculated_difficulty
          }
        })
        const artistData = response.data.artist && response.data.artist.data
          ? response.data.artist.data
          : response.data.artist
        if (artistData) {
          artistDetail.value.popularity = artistData.popularity
          artistDetail.value.spotify_playcount = artistData.spotify_playcount
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Прослушивания треков обновлены',
            text: `Обработано треков: ${response.data.total}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления прослушиваний треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        syncTracksStatsLoading.value = false
      }
    }

    const syncTrackPlatformStats = async (track) => {
      syncingTrackStatsId.value = track.id
      try {
        const response = await store.dispatch('artists/syncTrackPlatformStats', {id: track.id})
        const trackData = response.data.track && response.data.track.data
          ? response.data.track.data
          : response.data.track
        if (trackData) {
          track.play_count = trackData.play_count
          track.spotify_playcount = trackData.spotify_playcount
          track.calculated_difficulty = trackData.calculated_difficulty
        }
        if (!response.data.success) {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Данные площадок не обновлены',
              text: (response.data.messages || []).join('; '),
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления данных площадок',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        syncingTrackStatsId.value = null
      }
    }

    // Оптимистичное обновление: рейтинг показываем сразу, при ошибке откатываем
    const updateTrackDifficultyRating = (track, rating) => {
      const previousRating = track.difficulty_rating
      track.difficulty_rating = rating
      editingTrackRatingId.value = null

      store.dispatch('artists/updateTrack', {id: track.id, queryParams: {difficulty_rating: rating}})
        .then(response => {
          // Автор: правка ушла на одобрение — в таблице возвращаем прежнее значение
          if (isSubmissionResponse(response)) {
            track.difficulty_rating = previousRating
            handleSubmitted(response)
          }
        })
        .catch(() => {
          track.difficulty_rating = previousRating
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка обновления сложности трека',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    // Массовое изменение рейтинга: чекбоксы в таблице + кнопки «+»/«−» над ней
    const selectedTrackIds = ref([])

    const allTracksSelected = computed(() => {
      const tracks = (artistDetail.value && artistDetail.value.tracks) || []
      return tracks.length > 0 && selectedTrackIds.value.length === tracks.length
    })

    const toggleAllTracksSelection = () => {
      const tracks = (artistDetail.value && artistDetail.value.tracks) || []
      selectedTrackIds.value = allTracksSelected.value ? [] : tracks.map(track => track.id)
    }

    // Оптимистично: применяем на фронте сразу и шлём один запрос, при ошибке откатываем.
    // База — установленный рейтинг, иначе округлённый расчётный;
    // треки, у которых новый рейтинг выходит за 1–10, просто пропускаются
    const shiftSelectedTracksRating = delta => {
      const changes = []
      const rollbacks = []
      const tracks = (artistDetail.value && artistDetail.value.tracks) || []

      tracks.forEach(track => {
        if (!selectedTrackIds.value.includes(track.id)) return

        let currentRating = track.difficulty_rating != null ? Number(track.difficulty_rating) : null
        if (currentRating === null && track.calculated_difficulty != null) {
          currentRating = Math.round(Number(track.calculated_difficulty))
        }
        if (currentRating === null) return

        const newRating = currentRating + delta
        if (newRating < 1 || newRating > 10) return

        rollbacks.push({track, previousRating: track.difficulty_rating})
        track.difficulty_rating = newRating
        changes.push({id: track.id, difficulty_rating: newRating})
      })

      if (!changes.length) return

      store.dispatch('artists/bulkUpdateTracksDifficultyRating', {tracks: changes})
        .catch(() => {
          rollbacks.forEach(({track, previousRating}) => {
            track.difficulty_rating = previousRating
          })
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка массового обновления сложности треков',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const platformStatsSyncing = ref(false)

    const platformStatsSyncedTitle = computed(() => {
      const detail = artistDetail.value || {}
      const parts = []
      if (detail.spotify_synced_at) parts.push(`Spotify: ${formatDate(detail.spotify_synced_at)}`)
      if (detail.yandex_synced_at) parts.push(`Yandex: ${formatDate(detail.yandex_synced_at)}`)
      return parts.length
        ? `Обновить статистику площадок (синк — ${parts.join(', ')})`
        : 'Загрузить статистику площадок (Spotify, Yandex)'
    })

    // Spotify синкается через основной сервер, Yandex — через ru-сервер,
    // поэтому два отдельных запроса; каждый применяет только свои поля
    const syncPlatformStats = async () => {
      if (platformStatsSyncing.value) return
      const id = router.currentRoute.params.id
      platformStatsSyncing.value = true

      const extractArtist = response => (response.data.artist && response.data.artist.data
        ? response.data.artist.data
        : response.data.artist)

      const syncSource = async source => {
        const response = await store.dispatch('artists/syncArtistPlatformStats', {id, source})
        const artistData = extractArtist(response)
        if (artistData) {
          if (source === 'spotify') {
            artistDetail.value.spotify_playcount = artistData.spotify_playcount
            artistDetail.value.spotify_synced_at = artistData.spotify_synced_at
            // Last.fm-популярность синкается на не-RU сервере вместе со Spotify
            artistDetail.value.popularity = artistData.popularity
          } else {
            artistDetail.value.yandex_monthly_listeners = artistData.yandex_monthly_listeners
            artistDetail.value.yandex_synced_at = artistData.yandex_synced_at
          }
        }
        return response.data
      }

      const results = await Promise.allSettled([syncSource('spotify'), syncSource('yandex')])
      platformStatsSyncing.value = false

      const messages = []
      let anySuccess = false
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          messages.push(...(result.value.messages || []))
          if (result.value.success) anySuccess = true
        } else {
          messages.push(`${index === 0 ? 'Spotify' : 'Yandex'}: запрос не выполнен`)
        }
      })

      toast({
        component: ToastificationContent,
        props: {
          title: 'Статистика площадок',
          text: messages.join('; '),
          icon: anySuccess ? 'CheckIcon' : 'AlertTriangleIcon',
          variant: anySuccess ? 'success' : 'warning',
        },
      })
    }

    // Правка атрибутов: в запрос уходят только изменённые поля, пустое значение очищает
    const showAttributesModal = ref(false)
    const isSavingAttributes = ref(false)
    const isLoadingCountries = ref(false)
    const countryOptions = ref([])
    const attributesForm = ref({})
    const languageOptions = [
      {value: true, text: 'Русский'},
      {value: false, text: 'Зарубежный'},
    ]
    const artistTypeOptions = [
      {value: null, text: '— не указан'},
      {value: 'Человек', text: 'Человек'},
      {value: 'Группа', text: 'Группа'},
    ]
    const attributeColumns = ['russian', 'artist_type', 'country', 'birth_date', 'death_date', 'foundation_year', 'dissolution_year']

    const normalizeAttribute = (column, value) => {
      if (column === 'russian') return !!value
      if (value === '' || value === undefined || value === null) return null
      if (column === 'foundation_year' || column === 'dissolution_year') return parseInt(value, 10)
      // Дата из API может прийти с временем — инпуту type=date нужен Y-m-d
      if (column === 'birth_date' || column === 'death_date') return String(value).slice(0, 10)
      return value
    }

    const loadCountryOptions = async () => {
      if (countryOptions.value.length || isLoadingCountries.value) return
      isLoadingCountries.value = true
      try {
        const response = await store.dispatch('artists/fetchArtistCountries')
        countryOptions.value = response.data.data || []
      } catch (error) {
        countryOptions.value = []
      } finally {
        isLoadingCountries.value = false
      }
    }

    const openAttributesModal = () => {
      const detail = artistDetail.value || {}
      const form = {}
      attributeColumns.forEach(column => {
        form[column] = normalizeAttribute(column, detail[column])
      })
      attributesForm.value = form
      showAttributesModal.value = true
      loadCountryOptions()
    }

    const saveAttributes = async () => {
      const detail = artistDetail.value || {}
      const changes = {}
      attributeColumns.forEach(column => {
        const value = normalizeAttribute(column, attributesForm.value[column])
        if (value !== normalizeAttribute(column, detail[column])) {
          changes[column] = value
        }
      })

      if (!Object.keys(changes).length) {
        showAttributesModal.value = false
        return
      }

      isSavingAttributes.value = true
      try {
        const response = await store.dispatch('artists/updateArtistAttributes', {
          id: router.currentRoute.params.id,
          attributes: changes,
        })
        Object.assign(artistDetail.value, response.data.attributes || {})
        showAttributesModal.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || 'Сохранено',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const data = (error.response && error.response.data) || {}
        const validationErrors = data.errors ? Object.values(data.errors).flat().join('; ') : null
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения атрибутов',
            text: validationErrors || data.message || error.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isSavingAttributes.value = false
      }
    }

    const startEditingTitle = () => {
      editTitle.value = artistDetail.value.title
      isEditingTitle.value = true
    }

    const cancelEditingTitle = () => {
      isEditingTitle.value = false
    }

    const saveTitle = async () => {
      const newTitle = editTitle.value.trim()
      if (!newTitle || newTitle === artistDetail.value.title) {
        isEditingTitle.value = false
        return
      }
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {title: newTitle}})
        artistDetail.value.title = newTitle
        isEditingTitle.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Название обновлено',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления названия',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addAliasFromSearch = async (name) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addAlias', {id, queryParams: {alias: name}})
        await fetchAliases()
        aliasSearchResults.value = aliasSearchResults.value.filter(r => r.name !== name)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding alias',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addAliasFromExternal = async (name) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addAlias', {id, queryParams: {alias: name}})
        await fetchAliases()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding alias',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const loadSavedCovers = async () => {
      if (savedCoversLoaded.value) return
      const id = router.currentRoute.params.id
      isLoadingSavedCovers.value = true
      try {
        const response = await store.dispatch('artists/fetchArtistCovers', {id})
        savedCovers.value = response.data
        savedCoversLoaded.value = true
      } catch (error) {
        // silent
      } finally {
        isLoadingSavedCovers.value = false
      }
    }

    const savedCoverNames = computed(() => {
      const set = new Set()
      savedCovers.value.forEach(c => {
        if (c.name) set.add(c.name.toLowerCase().trim())
      })
      return set
    })

    const isCoverSaved = (album) => {
      if (!album.name) return false
      return savedCoverNames.value.has(album.name.toLowerCase().trim())
    }

    const searchSpotifyCovers = async () => {
      let id = router.currentRoute.params.id
      isLoadingCovers.value = true
      coversError.value = ''
      coversSearched.value = false
      try {
        const response = await store.dispatch('artists/searchArtistAlbums', {id})
        spotifyCovers.value = response.data
        coversSearched.value = true
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error
        coversError.value = msg || 'Ошибка поиска обложек'
        toast({
          component: ToastificationContent,
          props: {
            title: msg || 'Ошибка поиска обложек',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingCovers.value = false
      }
    }

    const searchYandexCovers = async () => {
      let id = router.currentRoute.params.id
      isLoadingYandexCovers.value = true
      yandexCoversError.value = ''
      yandexCoversSearched.value = false
      try {
        const response = await store.dispatch('artists/searchArtistYandexAlbums', {id})
        yandexCovers.value = response.data
        yandexCoversSearched.value = true
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error
        yandexCoversError.value = msg || 'Ошибка поиска обложек'
        toast({
          component: ToastificationContent,
          props: {
            title: msg || 'Ошибка поиска обложек',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingYandexCovers.value = false
      }
    }

    const formatReleaseDate = (date, precision) => {
      if (!date) return ''
      const parts = date.split('-')
      if (precision === 'year' || parts.length === 1) return parts[0]
      if (precision === 'month' || parts.length === 2) {
        const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
        const monthIndex = parseInt(parts[1], 10) - 1
        return `${months[monthIndex]} ${parts[0]}`
      }
      const d = new Date(date)
      return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
    }

    const albumTypeLabels = { album: 'Альбом', single: 'Сингл', compilation: 'Сборник' }
    const albumTypeIcons = { album: 'DiscIcon', single: 'FileIcon', compilation: 'LayersIcon' }
    const albumTypeVariants = { album: 'primary', single: 'info', compilation: 'warning' }

    const albumTypeLabel = type => albumTypeLabels[type] || type
    const albumTypeIcon = type => albumTypeIcons[type] || 'DiscIcon'
    const albumTypeVariant = type => albumTypeVariants[type] || 'secondary'

    const currentFullCover = computed(() => {
      const i = fullCoverIndex.value
      return i >= 0 && i < savedCovers.value.length ? savedCovers.value[i] : null
    })

    const openFullCover = (index) => {
      fullCoverIndex.value = index
      showFullCover.value = true
    }

    const goPrevCover = () => {
      if (fullCoverIndex.value > 0) {
        fullCoverIndex.value -= 1
      }
    }

    const goNextCover = () => {
      if (fullCoverIndex.value < savedCovers.value.length - 1) {
        fullCoverIndex.value += 1
      }
    }

    const openCoverPreview = (album) => {
      previewAlbum.value = album
      showCoverPreview.value = true
    }

    const loadAlbumTracks = async (album, source) => {
      if (albumTracks.value[album.id]) return
      if (albumTracksLoading.value[album.id]) return
      const id = router.currentRoute.params.id
      albumTracksLoading.value = { ...albumTracksLoading.value, [album.id]: true }
      try {
        const response = await store.dispatch('artists/fetchAlbumTracks', {
          id,
          source,
          albumId: album.id,
        })
        // adding — реактивный флаг блокировки кнопки добавления (Vue 2: свойство
        // должно существовать заранее, иначе мутация не отслеживается).
        const tracks = (response.data || []).map(track => ({ ...track, adding: false }))
        albumTracks.value = { ...albumTracks.value, [album.id]: tracks }
      } catch (error) {
        const next = { ...albumTracks.value }
        delete next[album.id]
        albumTracks.value = next
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки треков альбома',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        albumTracksLoading.value = { ...albumTracksLoading.value, [album.id]: false }
      }
    }

    // Пересчитать число колонок по фактической ширине контейнера сетки.
    // Карточка 220px + gap 1rem (16px). Игнорируем нулевую ширину (скрытая вкладка).
    const measureAlbumColumns = () => {
      const el = albumsContainer.value
      if (!el || !el.clientWidth) return
      const cardWidth = 220
      const gap = 16
      albumColumns.value = Math.max(1, Math.floor((el.clientWidth + gap) / (cardWidth + gap)))
    }

    // Разбить группу альбомов на строки по числу колонок.
    const albumRows = (group) => {
      const size = albumColumns.value
      const rows = []
      for (let i = 0; i < group.length; i += size) {
        rows.push(group.slice(i, i + size))
      }
      return rows
    }

    // Содержит ли строка активный альбом данного источника.
    const rowHasActiveAlbum = (row, source) => {
      const active = activeAlbum.value
      return !!active && active.source === source && row.some(album => album.id === active.id)
    }

    const toggleAlbumTracks = (album, source) => {
      const isActive = activeAlbum.value
          && activeAlbum.value.source === source
          && activeAlbum.value.id === album.id
      if (isActive) {
        activeAlbum.value = null
        return
      }
      measureAlbumColumns()
      activeAlbum.value = { source, id: album.id, name: album.name }
      loadAlbumTracks(album, source)
    }

    const closeAlbumTracks = () => {
      activeAlbum.value = null
    }

    // ResizeObserver: пересчитывает колонки при появлении вкладки (0 → ширина)
    // и при любом изменении ширины контейнера сетки.
    let albumsResizeObserver = null
    onMounted(() => {
      if (typeof ResizeObserver !== 'undefined' && albumsContainer.value) {
        albumsResizeObserver = new ResizeObserver(() => measureAlbumColumns())
        albumsResizeObserver.observe(albumsContainer.value)
      } else {
        window.addEventListener('resize', measureAlbumColumns)
      }
    })
    onUnmounted(() => {
      if (albumsResizeObserver) {
        albumsResizeObserver.disconnect()
        albumsResizeObserver = null
      } else {
        window.removeEventListener('resize', measureAlbumColumns)
      }
    })

    // При появлении/смене списков обложек пересчитать колонки после отрисовки,
    // иначе первая отрисовка идёт со стартовым albumColumns = 1 (один столбец).
    watch([spotifyCovers, yandexCovers], () => {
      nextTick(() => measureAlbumColumns())
    })

    const addAlbumTrack = async (track) => {
      if (track.adding) return
      const id = router.currentRoute.params.id
      track.adding = true
      try {
        const response = await store.dispatch('artists/addTrack', { id, queryParams: { title: track.title } })
        // Автор: трек ушёл на одобрение — не помечаем его добавленным
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        track.in_db = true
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        track.adding = false
      }
    }

    const saveCover = async (album) => {
      let id = router.currentRoute.params.id
      try {
        const response = await store.dispatch('artists/saveArtistCover', {
          id,
          queryParams: {
            cover: album.cover,
            name: album.name,
          },
        })
        // Автор: обложка ушла на одобрение — в savedCovers не вставляем, она выводится заявкой
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        if (response.data) {
          savedCovers.value.push(response.data)
        } else {
          savedCovers.value.push({name: album.name, url: album.cover})
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Обложка сохранена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения обложки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const deleteCover = async (cover) => {
      if (!confirm('Удалить обложку?')) return
      const id = router.currentRoute.params.id
      try {
        const response = await store.dispatch('artists/deleteArtistCover', {id, coverId: cover.id})
        // Автор: удаление ушло на одобрение — обложка остаётся, приглушённая
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        savedCovers.value = savedCovers.value.filter(c => c.id !== cover.id)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Обложка удалена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления обложки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const loadSavedPhotos = async () => {
      if (savedPhotosLoaded.value) return
      if (!customPhotoQuery.value && artistDetail.value) customPhotoQuery.value = artistDetail.value.title
      const id = router.currentRoute.params.id
      isLoadingSavedPhotos.value = true
      try {
        const response = await store.dispatch('artists/fetchArtistPhotos', {id})
        savedPhotos.value = response.data
        savedPhotosLoaded.value = true
      } catch (error) {
        // silent
      } finally {
        isLoadingSavedPhotos.value = false
      }
    }

    const searchPhotos = async (start = 1, query = '', type = null, full = false) => {
      const id = router.currentRoute.params.id
      isLoadingPhotos.value = true
      photosError.value = ''
      if (start === 1) {
        photoResults.value = []
        photosSearched.value = false
        activePhotoQuery.value = query
        activePhotoType.value = type
        activePhotoFull.value = full
      }
      try {
        const response = await store.dispatch('artists/searchArtistPhotos', {id, start, query: activePhotoQuery.value, full: activePhotoFull.value})
        // Проставляем тип из активного пресета, чтобы он сохранился вместе с фото.
        const items = (response.data.items || []).map(item => ({...item, type: activePhotoType.value}))
        photoResults.value = photoResults.value.concat(items)
        photoNextStart.value = response.data.next_start || null
        photosSearched.value = true
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error
        photosError.value = msg || 'Ошибка поиска фото'
        toast({
          component: ToastificationContent,
          props: {title: msg || 'Ошибка поиска фото', icon: 'AlertTriangleIcon', variant: 'danger'},
        })
      } finally {
        isLoadingPhotos.value = false
      }
    }

    const loadMorePhotos = () => {
      if (photoNextStart.value) searchPhotos(photoNextStart.value)
    }

    const selectGoogleSearchType = (searchType) => {
      selectedGoogleSearchType.value = searchType.type
      const title = artistDetail.value ? artistDetail.value.title : ''
      customPhotoQuery.value = [title, searchType.query].filter(Boolean).join(' ')
    }

    const runGoogleSearch = () => {
      const query = customPhotoQuery.value.trim()
      if (query) searchPhotos(1, query, selectedGoogleSearchType.value, true)
    }

    const isPhotoSourceAvailable = (source) => !source.binding || !!(artistDetail.value
      && artistDetail.value.external_ids
      && artistDetail.value.external_ids[source.binding])

    const loadSourcePhotos = async (provider) => {
      const id = router.currentRoute.params.id
      const source = photoSources.find(s => s.key === provider)
      sourcePhotos.value[provider] = {...sourcePhotos.value[provider], loading: true}
      try {
        const response = await store.dispatch('artists/fetchArtistSourcePhotos', {id, provider})
        sourcePhotos.value[provider] = {items: response.data.items || [], loading: false, searched: true}
      } catch (error) {
        sourcePhotos.value[provider] = {...sourcePhotos.value[provider], loading: false}
        const msg = error.response && error.response.data && error.response.data.error
        toast({
          component: ToastificationContent,
          props: {title: msg || `Ошибка загрузки: ${source.label}`, icon: 'AlertTriangleIcon', variant: 'danger'},
        })
      }
    }

    const openPhotoModal = (photo, isResult) => {
      currentPhoto.value = {...photo, isResult}
      savePhotoError.value = ''
      showPhotoModal.value = true
    }

    // Instagram/Facebook отдают оригинал только гуглоботу — при ошибке загрузки
    // показываем гугловский thumbnail (единственную доступную версию).
    const onModalPhotoError = () => {
      const photo = currentPhoto.value
      if (photo && photo.isResult && photo.thumbnail && !photo.useThumbnail) {
        currentPhoto.value = {...photo, useThumbnail: true}
      }
    }

    // Достаёт человекочитаемое сообщение об ошибке из ответа бэкенда/таймаута.
    const extractPhotoError = (error) => {
      const data = error.response && error.response.data
      let msg = (data && data.message) || ''
      if (!msg && data && data.errors) {
        const first = Object.values(data.errors)[0]
        msg = Array.isArray(first) ? first[0] : first
      }
      if (!msg && error.code === 'ECONNABORTED') {
        msg = 'Превышено время ожидания (15 с)'
      }
      return msg || 'Ошибка сохранения фото'
    }

    // Высокое разрешение: больше 1000 px по вертикали или горизонтали.
    const isHighResPhoto = (photo) => !!photo && (photo.width > 1000 || photo.height > 1000)

    // Множество исходных ссылок уже сохранённых фото — для пометки дублей в выдаче.
    const savedPhotoUrls = computed(() => new Set(savedPhotos.value.map(p => p.image_url).filter(Boolean)))
    const isPhotoSaved = (photo) => !!photo && savedPhotoUrls.value.has(photo.image_url)

    const savePhoto = async (photo) => {
      const id = router.currentRoute.params.id
      isSavingPhoto.value = true
      savePhotoError.value = ''
      try {
        const response = await store.dispatch('artists/saveArtistPhoto', {
          id,
          queryParams: {
            image_url: photo.image_url,
            thumbnail_url: photo.thumbnail || null,
            name: photo.title ? photo.title.slice(0, 255) : photo.title,
            type: photo.type || null,
            width: photo.width,
            height: photo.height,
          },
        })
        // Автор: фото ушло на одобрение — в savedPhotos не вставляем, оно выводится заявкой
        if (isSubmissionResponse(response)) {
          showPhotoModal.value = false
          handleSubmitted(response)
          return
        }
        if (response.data && response.data.photo) {
          savedPhotos.value.unshift(response.data.photo)
        }
        showPhotoModal.value = false
        toast({
          component: ToastificationContent,
          props: {title: 'Фото сохранено', icon: 'CheckIcon', variant: 'success'},
        })
      } catch (error) {
        let msg = extractPhotoError(error)
        if (error.code === 'ECONNABORTED') {
          msg = 'Превышено время ожидания (15 с) — попробуйте «Через сервер»'
        }
        savePhotoError.value = msg
      } finally {
        isSavingPhoto.value = false
      }
    }

    const onPhotoSaved = (response) => {
      // Автор: фото ушло на одобрение — в savedPhotos не вставляем
      if (isSubmissionResponse(response)) {
        showPhotoModal.value = false
        handleSubmitted(response)
        return
      }
      if (response.data && response.data.photo) {
        savedPhotos.value.unshift(response.data.photo)
      }
      showPhotoModal.value = false
      toast({
        component: ToastificationContent,
        props: {title: 'Фото сохранено', icon: 'CheckIcon', variant: 'success'},
      })
    }

    // Фолбэк 1: сервер сам качает картинку с браузероподобными заголовками —
    // обходит hotlink-защиту, не зависит от CORS.
    const savePhotoViaProxy = async (photo) => {
      const id = router.currentRoute.params.id
      isSavingPhoto.value = true
      savePhotoError.value = ''
      try {
        const response = await store.dispatch('artists/proxyArtistPhoto', {
          id,
          queryParams: {
            image_url: photo.image_url,
            thumbnail_url: photo.thumbnail || null,
            name: photo.title ? photo.title.slice(0, 255) : photo.title,
            type: photo.type || null,
            width: photo.width,
            height: photo.height,
          },
        })
        onPhotoSaved(response)
      } catch (error) {
        savePhotoError.value = extractPhotoError(error)
      } finally {
        isSavingPhoto.value = false
      }
    }

    // Фолбэк 2: браузер сам скачивает уже отображаемую картинку (fetch → blob)
    // и грузит её файлом. Сохраняет исходный формат. Работает, если хост отдаёт CORS.
    const savePhotoViaBrowser = async (photo) => {
      const id = router.currentRoute.params.id
      isSavingPhoto.value = true
      savePhotoError.value = ''
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 15000)
      try {
        const res = await fetch(photo.image_url, {signal: controller.signal})
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const blob = await res.blob()
        const extension = (blob.type && blob.type.split('/')[1]) || 'jpg'
        const formData = new FormData()
        formData.append('file', blob, `photo.${extension}`)
        if (photo.image_url) formData.append('image_url', photo.image_url)
        if (photo.title) formData.append('name', photo.title.slice(0, 255))
        if (photo.type) formData.append('type', photo.type)
        if (photo.width) formData.append('width', photo.width)
        if (photo.height) formData.append('height', photo.height)
        const response = await store.dispatch('artists/uploadArtistPhoto', {id, formData})
        onPhotoSaved(response)
      } catch (error) {
        if (error.name === 'AbortError') {
          savePhotoError.value = 'Превышено время загрузки (15 с)'
        } else if (error.response) {
          savePhotoError.value = extractPhotoError(error)
        } else {
          savePhotoError.value = 'Браузер не смог скачать картинку (CORS/недоступно)'
        }
      } finally {
        clearTimeout(timer)
        isSavingPhoto.value = false
      }
    }

    const deletePhoto = async (photo) => {
      if (!confirm('Удалить фото?')) return
      const id = router.currentRoute.params.id
      try {
        const response = await store.dispatch('artists/deleteArtistPhoto', {id, photoId: photo.id})
        // Автор: удаление ушло на одобрение — фото остаётся, приглушённое
        if (isSubmissionResponse(response)) {
          showPhotoModal.value = false
          handleSubmitted(response)
          return
        }
        savedPhotos.value = savedPhotos.value.filter(p => p.id !== photo.id)
        showPhotoModal.value = false
        toast({
          component: ToastificationContent,
          props: {title: 'Фото удалено', icon: 'CheckIcon', variant: 'success'},
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {title: 'Ошибка удаления фото', icon: 'AlertTriangleIcon', variant: 'danger'},
        })
      }
    }

    const uploadFile = ref(null)
    const uploadType = ref(null)
    const isUploadingPhoto = ref(false)
    const photoTypeOptions = [
      {value: null, text: 'Без типа'},
      ...photoQueryPresets.map(preset => ({value: preset.type, text: preset.label})),
    ]

    // Читаем размеры картинки на клиенте, чтобы сохранить их вместе с фото.
    const readImageSize = (file) => new Promise(resolve => {
      const objectUrl = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        resolve({width: image.naturalWidth, height: image.naturalHeight})
        URL.revokeObjectURL(objectUrl)
      }
      image.onerror = () => {
        resolve({width: null, height: null})
        URL.revokeObjectURL(objectUrl)
      }
      image.src = objectUrl
    })

    const uploadOwnPhoto = async () => {
      if (!uploadFile.value) return
      const id = router.currentRoute.params.id
      isUploadingPhoto.value = true
      try {
        const {width, height} = await readImageSize(uploadFile.value)
        const formData = new FormData()
        formData.append('file', uploadFile.value)
        const baseName = uploadFile.value.name.replace(/\.[^.]+$/, '')
        if (baseName) formData.append('name', baseName.slice(0, 255))
        if (uploadType.value) formData.append('type', uploadType.value)
        if (width) formData.append('width', width)
        if (height) formData.append('height', height)
        const response = await store.dispatch('artists/uploadArtistPhoto', {id, formData})
        uploadFile.value = null
        uploadType.value = null
        // Автор: фото ушло на одобрение — в savedPhotos не вставляем
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        if (response.data && response.data.photo) {
          savedPhotos.value.unshift(response.data.photo)
        }
        toast({
          component: ToastificationContent,
          props: {title: 'Фото сохранено', icon: 'CheckIcon', variant: 'success'},
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {title: extractPhotoError(error), icon: 'AlertTriangleIcon', variant: 'danger'},
        })
      } finally {
        isUploadingPhoto.value = false
      }
    }

    const deleteCurrentCover = async () => {
      const cover = currentFullCover.value
      // У обложки уже есть заявка на одобрении — повторно не отправляем
      if (!cover || ownPendingForTarget('album_cover', cover.id)) return
      await deleteCover(cover)
      showFullCover.value = false
    }

    const onFullCoverKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        goPrevCover()
      } else if (event.key === 'ArrowRight') {
        goNextCover()
      }
    }

    const onFullCoverShown = () => {
      window.addEventListener('keydown', onFullCoverKeydown)
    }

    const onFullCoverHidden = () => {
      window.removeEventListener('keydown', onFullCoverKeydown)
    }

    const updateCoverDifficulty = async (cover, value) => {
      const id = router.currentRoute.params.id
      try {
        const response = await store.dispatch('artists/updateArtistCover', {
          id,
          coverId: cover.id,
          queryParams: { difficulty: value || null },
        })
        // Автор: правка ушла на одобрение — значение в карточке не меняем
        if (isSubmissionResponse(response)) {
          handleSubmitted(response)
          return
        }
        cover.difficulty = value
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления сложности',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const saveCoverWithUrl = async (imageUrl, done, difficulty) => {
      let id = router.currentRoute.params.id
      const albumName = previewAlbum.value ? previewAlbum.value.name : ''
      try {
        const response = await store.dispatch('artists/saveArtistCover', {
          id,
          queryParams: {
            cover: imageUrl,
            name: albumName,
            difficulty: difficulty || null,
          },
        })
        // Автор: обложка ушла на одобрение — список сохранённых не перезагружаем
        if (isSubmissionResponse(response)) {
          showCoverPreview.value = false
          handleSubmitted(response)
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Обложка сохранена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        showCoverPreview.value = false
        savedCoversLoaded.value = false
        await loadSavedCovers()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения обложки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        if (done) done()
      }
    }

    const openExternal = (url) => {
      window.open(url, '_blank')
    }

    return {
      pendingSubmissions,
      pendingBy,
      pendingForTarget,
      ownPendingForTarget,
      pendingTargetLabel,
      openExternal,
      ignoreTracks,
      formatDate,
      artistDetail,
      similar,
      manualSimilar,
      manualSearchQuery,
      manualSearchResults,
      manualSearchLoading,
      tastediveLoading,
      manualAddingKey,
      similarSourceClass,
      fetchManualSimilar,
      openSimilarTab,
      isManualAdded,
      addManualFromSearch,
      addManualFromCandidate,
      removeManualSimilar,
      saveManualOrder,
      searchManualCandidates,
      loadTastedive,
      additional,
      isLoadingArtist,
      isLoadingStaff,
      disabledArtistId,
      refreshData,
      blockData,
      unblockData,
      tableColumns,
      tracksSortCompare,
      badWord,
      formatNumber,
      deleteTrack,
      flushCache,
      syncNotion,

      clusterOptions,
      selectedClusterIds,
      isSavingClusters,
      saveClusters,
      mainClusterId,
      selectedClusters,
      setMainCluster,
      decadeOptions,
      selectedDecades,
      mainDecade,
      isSavingDecades,
      calculateDecades,
      toggleDecade,
      setMainDecade,

      notionTagsVisibleLimit,
      notionTagGrades,
      notionTagGroups,
      notionTagDictionary,
      notionTagsLoaded,
      notionTagsCached,
      isSuggestingNotionTags,
      addingNotionTag,
      manualNotionTag,
      visibleNotionTags,
      notionTagsToggleLabel,
      toggleNotionTagsExpanded,
      suggestNotionTags,
      addNotionTag,

      loadAdditionalTracks,
      trackSearchQuery,
      searchTracksByName,
      loadLastfmInfo,
      addTrack,
      toggleIgnore,
      artistAliases,
      aliasSearchResults,
      fetchAliases,
      searchForAliases,
      addAliasFromSearch,
      externalAliasCandidates,
      addAliasFromExternal,
      manualAliasName,
      addManualAlias,
      shsTracks,
      shsTracksError,
      artistListsColumns,
      shsTableColumns,
      mergedShsTracks,
      fetchShsTracks,
      addShsTrack,
      showLinkShsTrackModal,
      linkShsTrackItem,
      selectedTrackIdForLink,
      artistTracksForLink,
      openLinkShsTrackModal,
      linkShsTrack,
      quickLinkShsTrack,
      showShsPerformanceModal,
      shsPerformanceLoading,
      shsPerformanceError,
      shsPerformanceInfo,
      openShsPerformanceModal,
      formatShsDate,
      showLlmCoversModal,
      llmCoversDirection,
      llmCoversItems,
      llmCoversSources,
      llmCoversCached,
      llmCoversLoading,
      llmCoversError,
      llmCoversTableColumns,
      openLlmCoversModal,
      fetchLlmCovers,
      addLlmArtistTrack,
      linkLlmCoverTrack,
      llmCoversBusyRow,
      showLlmPickTrackModal,
      llmPickTrackItem,
      llmPickTrackOptions,
      llmPickTrackSelectedId,
      llmPickTrackLoading,
      llmPickTrackMode,
      llmPickTrackArtistName,
      openLlmPickTrackModal,
      openLlmPickOurTrackModal,
      confirmLlmPickTrack,
      addLlmForeignCoverTrack,
      showShsSyncButton,
      shsSyncDisabled,
      showShsTracksButton,
      syncShs,
      syncSpotify,
      syncYandex,
      syncWikipedia,
      syncShsTracks,
      showShsCandidates,
      shsCandidates,
      selectShsCandidate,
      syncMusicBrainz,
      selectMusicBrainzCandidate,
      showMusicBrainzCandidates,
      musicBrainzCandidates,
      musicBrainzYears,
      openMusicBrainzAttributes,
      applyMusicBrainzAttributes,
      findMusicBrainzFromAttributes,
      showMusicBrainzAttributes,
      musicBrainzAttributes,
      musicBrainzSelectedFields,
      musicBrainzSelectedAliases,
      isApplyingMusicBrainzAttributes,
      musicBrainzHasSelection,
      toggleCoverArtist,
      toggleSkipOriginals,
      updateDifficultyRating,
      recalcDifficultyLoading,
      recalculateDifficulty,
      updateTrackDifficultyRating,
      selectedTrackIds,
      allTracksSelected,
      toggleAllTracksSelection,
      shiftSelectedTracksRating,
      editingTrackRatingId,
      playArtistTrack,
      isTrackPlaying,
      syncingTrackStatsId,
      syncTrackPlatformStats,
      recalcTracksDifficultyLoading,
      recalculateTracksDifficulty,
      syncTracksStatsLoading,
      syncTracksPlatformStats,
      bindingTracksSource,
      bindTracksExternalIds,
      trackExternalLabel,
      similarArtistImage,
      difficultyTooltip,
      platformStatsSyncing,
      platformStatsSyncedTitle,
      syncPlatformStats,
      aliasesText,
      showAddExternalIdModal,
      externalIdInput,
      isAddingExternalId,
      addExternalId,
      deleteExternalId,
      showMergeArtistsModal,
      onArtistsMerged,
      deleteArtist,
      externalLabel,
      wikipediaUrl,
      wikipediaLabel,
      isEditingTitle,
      editTitle,
      showAttributesModal,
      isSavingAttributes,
      isLoadingCountries,
      countryOptions,
      attributesForm,
      languageOptions,
      artistTypeOptions,
      openAttributesModal,
      saveAttributes,
      startEditingTitle,
      cancelEditingTitle,
      saveTitle,
      toStarRating,
      additionalGroupTitle,
      savedCovers,
      savedCoversLoaded,
      isLoadingSavedCovers,
      spotifyCovers,
      groupedSpotifyCovers,
      isLoadingCovers,
      coversError,
      coversSearched,
      showCoverPreview,
      previewAlbum,
      showFullCover,
      fullCoverIndex,
      currentFullCover,
      openFullCover,
      goPrevCover,
      goNextCover,
      deleteCurrentCover,
      onFullCoverShown,
      onFullCoverHidden,
      hasSpotifyId,
      hasMusicBrainzId,
      artistFacts,
      loadSavedCovers,
      searchSpotifyCovers,
      yandexCovers,
      groupedYandexCovers,
      isLoadingYandexCovers,
      yandexCoversError,
      yandexCoversSearched,
      hasYandexId,
      searchYandexCovers,
      isCoverSaved,
      openCoverPreview,
      activeAlbum,
      albumTracks,
      albumTracksLoading,
      albumsContainer,
      albumRows,
      rowHasActiveAlbum,
      toggleAlbumTracks,
      closeAlbumTracks,
      addAlbumTrack,
      saveCover,
      deleteCover,
      updateCoverDifficulty,
      saveCoverWithUrl,
      savedPhotos,
      savedPhotosLoaded,
      isLoadingSavedPhotos,
      photoResults,
      isLoadingPhotos,
      photosSearched,
      photosError,
      photoNextStart,
      photoSources,
      sourcePhotos,
      isPhotoSourceAvailable,
      loadSourcePhotos,
      googleSearchTypes,
      selectedGoogleSearchType,
      googleSearchTypeLabel,
      selectGoogleSearchType,
      runGoogleSearch,
      showPhotoModal,
      currentPhoto,
      isSavingPhoto,
      savePhotoError,
      activePhotoQuery,
      activePhotoType,
      activePhotoFull,
      customPhotoQuery,
      photoQueryPresets,
      photoTypeLabel,
      isHighResPhoto,
      isPhotoSaved,
      loadSavedPhotos,
      searchPhotos,
      loadMorePhotos,
      openPhotoModal,
      onModalPhotoError,
      savePhoto,
      savePhotoViaProxy,
      savePhotoViaBrowser,
      deletePhoto,
      uploadFile,
      uploadType,
      isUploadingPhoto,
      photoTypeOptions,
      uploadOwnPhoto,
      formatReleaseDate,
      albumTypeLabel,
      albumTypeIcon,
      albumTypeVariant,
      currentMembers,
      formerMembers,
      isLoadingMembers,
      isSavingMembers,
      newCurrentMember,
      newFormerMember,
      hasMembers,
      initMembers,
      loadMembers,
      saveMembers,
      removeMember,
      moveToFormer,
      moveToCurrent,
      addCurrentMember,
      addFormerMember,
      moveMemberUp,
      moveMemberDown,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';

.vs__dropdown-menu[style] {
  z-index: 1060 !important;
}

.notion-tag-manual {
  max-width: 420px;
}

// Заявки авторов на одобрении
.pending-submission {
  border: 2px dashed #ff9f43 !important;
  border-radius: 6px;
  position: relative;
}
.pending-submission-delete {
  opacity: 0.45;
}
.pending-submission-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 2;
}
.pending-submission-count {
  color: #ff9f43;
  font-weight: 600;
}
.pending-submission-popover {
  max-width: 360px;
}

</style>
